<template>
  <v-app>
    <v-main class="app-page">
      <section v-if="booting" class="center-state">
        <v-progress-circular indeterminate color="primary" />
      </section>

      <section v-else-if="!user" class="login-shell">
        <div class="login-brand">
          <span class="brand-mark">
            <img :src="demandeskMarkUrl" alt="" aria-hidden="true">
          </span>
          <div>
            <strong class="brand-wordmark">Demandesk</strong>
            <span>by Mazzitech</span>
          </div>
        </div>
        <div class="login-card">
          <span class="eyebrow">{{ authCopy.eyebrow }}</span>
          <h1>{{ authCopy.title }}</h1>
          <p>{{ authCopy.description }}</p>

          <v-form v-if="authMode === 'login'" ref="loginFormRef" @submit.prevent="login">
            <v-text-field
              v-model="loginForm.email"
              label="E-mail"
              placeholder="E-mail"
              aria-label="E-mail"
              autocomplete="email"
              type="email"
              outlined
              dense
              :rules="[rules.required]"
            />
            <v-text-field
              v-model="loginForm.password"
              label="Senha"
              placeholder="Senha"
              aria-label="Senha"
              autocomplete="current-password"
              type="password"
              outlined
              dense
              :rules="[rules.required]"
            />
            <v-alert v-if="loginError" type="error" dense outlined>{{ loginError }}</v-alert>
            <v-alert v-if="authMessage" type="success" dense outlined>{{ authMessage }}</v-alert>
            <v-btn block depressed type="submit" :loading="loggingIn" class="primary-button">
              Entrar
            </v-btn>
            <button class="auth-link" type="button" @click="setAuthMode('forgot')">Esqueci minha senha</button>
          </v-form>

          <v-form v-else-if="authMode === 'invite'" ref="inviteRegisterFormRef" @submit.prevent="registerInvite">
            <v-alert v-if="inviteInfo && inviteInfo.projectIds && !inviteInfo.projectIds.length" type="info" dense outlined>
              Seu cadastro será criado sem projetos vinculados. Um administrador poderá liberar o acesso depois.
            </v-alert>
            <v-text-field v-model="inviteForm.name" label="Nome" placeholder="Nome" aria-label="Nome" autocomplete="name" outlined dense :rules="[rules.required]" />
            <v-text-field v-model="inviteForm.username" label="Username" placeholder="Username" aria-label="Username" autocomplete="username" prefix="@" outlined dense :rules="[rules.required, rules.username]" @input="normalizeInviteUsername" />
            <v-text-field v-model="inviteForm.email" label="E-mail" placeholder="E-mail" aria-label="E-mail" autocomplete="email" type="email" outlined dense :disabled="Boolean(inviteInfo && inviteInfo.email)" :rules="[rules.required]" />
            <v-text-field v-model="inviteForm.password" label="Senha" placeholder="Senha" aria-label="Senha" autocomplete="new-password" type="password" outlined dense :rules="[rules.required, rules.minPassword]" />
            <v-text-field v-model="inviteForm.confirmation" label="Confirmar senha" placeholder="Confirmar senha" aria-label="Confirmar senha" autocomplete="new-password" type="password" outlined dense :rules="[rules.required, rules.minPassword]" />
            <v-alert v-if="loginError" type="error" dense outlined>{{ loginError }}</v-alert>
            <v-alert v-if="authMessage" type="success" dense outlined>{{ authMessage }}</v-alert>
            <v-btn block depressed type="submit" :loading="loggingIn" class="primary-button">
              Criar acesso
            </v-btn>
            <button class="auth-link" type="button" @click="clearInviteMode">Voltar para login</button>
          </v-form>

          <v-form v-else-if="authMode === 'forgot'" ref="forgotFormRef" @submit.prevent="forgotPassword">
            <v-text-field v-model="forgotForm.email" label="E-mail" placeholder="E-mail" aria-label="E-mail" autocomplete="email" type="email" outlined dense :rules="[rules.required]" />
            <v-alert v-if="loginError" type="error" dense outlined>{{ loginError }}</v-alert>
            <v-alert v-if="authMessage" type="success" dense outlined>{{ authMessage }}</v-alert>
            <v-btn block depressed type="submit" :loading="loggingIn" class="primary-button">
              Enviar link
            </v-btn>
            <button class="auth-link" type="button" @click="setAuthMode('login')">Voltar para login</button>
          </v-form>

          <v-form v-else ref="resetFormRef" @submit.prevent="resetPassword">
            <v-text-field v-model="resetForm.password" label="Nova senha" placeholder="Nova senha" aria-label="Nova senha" autocomplete="new-password" type="password" outlined dense :rules="[rules.required, rules.minPassword]" />
            <v-text-field v-model="resetForm.confirmation" label="Confirmar senha" placeholder="Confirmar senha" aria-label="Confirmar senha" autocomplete="new-password" type="password" outlined dense :rules="[rules.required, rules.minPassword]" />
            <v-alert v-if="loginError" type="error" dense outlined>{{ loginError }}</v-alert>
            <v-alert v-if="authMessage" type="success" dense outlined>{{ authMessage }}</v-alert>
            <v-btn block depressed type="submit" :loading="loggingIn" class="primary-button">
              Alterar senha
            </v-btn>
            <button class="auth-link" type="button" @click="setAuthMode('login')">Voltar para login</button>
          </v-form>
        </div>
      </section>

      <template v-else>
        <div class="product-shell" :class="{ 'sidebar-active': sidebarOpen }">
          <header class="topbar">
            <button
              class="mobile-menu-button"
              type="button"
              :aria-label="sidebarOpen ? 'Recolher menu' : 'Abrir menu'"
              :title="sidebarOpen ? 'Recolher menu' : 'Abrir menu'"
              @click="toggleSidebar"
            >
              <svg :style="{ transform: sidebarOpen ? 'rotate(0deg)' : 'rotate(180deg)', transition: 'transform 0.2s ease' }" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M10 3L5 8l5 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
            <div v-if="activeView === 'demands'" class="topbar-context topbar-demand-context">
              <div class="topbar-project-label">
                <span>{{ demandHeroKicker }}</span>
                <strong>{{ demandBoardTitle }}</strong>
              </div>
              <div class="topbar-search">
                <UiInput
                  v-model="filters.search"
                  :placeholder="isClient ? 'Código, título, empresa ou anotação' : 'Código, título, empresa, responsável ou anotação'"
                  @input="debouncedLoad"
                />
              </div>
              <div class="topbar-filters-toggle" tabindex="0" @blur="filtersMenuOpen = false">
                <button type="button" class="filters-trigger" :class="{ active: filtersMenuOpen }" @click="filtersMenuOpen = !filtersMenuOpen">
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2 4h12M4.5 8h7M7 12h2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                  </svg>
                  <span>Filtros</span>
                  <span v-if="activeDemandFilterCount" class="filters-count-badge">{{ activeDemandFilterCount }}</span>
                </button>
                <div v-if="filtersMenuOpen" class="topbar-filter-group" @mousedown.stop>
                  <UiSelect v-model="filters.status" placeholder="Status" :items="statusFilterOptions" @change="loadDemands" />
                  <UiSelect v-model="filters.priority" placeholder="Prioridade" :items="priorityFilterOptions" @change="loadDemands" />
                  <div class="topbar-date-filter">
                    <span>Periodo</span>
                    <div>
                      <UiInput v-model="filters.dateFrom" type="date" aria-label="Data inicial" @input="handleDemandDateFilterChange" />
                      <UiInput v-model="filters.dateTo" type="date" aria-label="Data final" @input="handleDemandDateFilterChange" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div v-else-if="activeView === 'requests'" class="topbar-context topbar-request-context">
              <div class="topbar-project-label">
                <span>Solicitações</span>
                <strong>Tickets e conversas</strong>
              </div>
              <div class="topbar-search">
                <UiInput
                  v-model="requestFilters.search"
                  placeholder="Código, título, projeto"
                  @input="debouncedLoadRequests"
                />
              </div>
              <div class="topbar-filters-toggle" tabindex="0" @blur="filtersMenuOpen = false">
                <button type="button" class="filters-trigger" :class="{ active: filtersMenuOpen }" @click="filtersMenuOpen = !filtersMenuOpen">
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2 4h12M4.5 8h7M7 12h2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                  </svg>
                  <span>Filtros</span>
                  <span v-if="activeRequestFilterCount" class="filters-count-badge">{{ activeRequestFilterCount }}</span>
                </button>
                <div v-if="filtersMenuOpen" class="topbar-filter-group request-filter-group" @mousedown.stop>
                  <UiSelect
                    v-if="isStaff"
                    v-model="requestFilters.projectId"
                    placeholder="Projeto"
                    :items="boardProjectOptions"
                    @change="loadRequests"
                  />
                  <UiSelect v-model="requestFilters.archive" placeholder="Exibição" :items="[{ text: 'Ativas', value: 'active' }, { text: 'Arquivadas', value: 'archived' }]" @change="loadRequests" />
                  <UiSelect v-model="requestFilters.status" placeholder="Status" :items="requestStatusFilterOptions" @change="loadRequests" />
                  <UiSelect v-model="requestFilters.priority" placeholder="Prioridade" :items="priorityFilterOptions" @change="loadRequests" />
                </div>
              </div>
            </div>
            <div class="topbar-actions">
              <div v-if="isClient && projects.length > 1" class="project-context-select">
                <span>Projeto</span>
                <button
                  type="button"
                  class="project-context-trigger"
                  :aria-expanded="clientProjectMenuOpen ? 'true' : 'false'"
                  aria-haspopup="listbox"
                  @click="clientProjectMenuOpen = !clientProjectMenuOpen"
                >
                  {{ selectedClientProjectLabel }}
                </button>
                <div v-if="clientProjectMenuOpen" class="project-context-popover" role="listbox">
                  <button
                    v-for="option in clientProjectContextOptions"
                    :key="option.value"
                    type="button"
                    role="option"
                    :aria-selected="clientProjectContextId === option.value"
                    :class="{ active: clientProjectContextId === option.value }"
                    @click="selectClientProjectContext(option.value)"
                  >
                    {{ option.text }}
                  </button>
                </div>
              </div>
              <div v-else-if="isStaff" class="top-actions">
                <div v-if="activeView === 'demands' && isAdmin" class="bulk-actions-group">
                  <button class="btn-ghost" @click="openBulkDemandModal">Criar em massa</button>
                  <button class="btn-ghost bulk-drafts-btn" @click="setWorkspaceView('drafts')" :class="{ active: activeView === 'drafts' }">
                    Rascunhos<span v-if="demandDrafts.length" class="draft-count-badge">{{ demandDrafts.length }}</span>
                  </button>
                </div>
                <button class="btn-glow" @click="openDemandModal">Nova demanda</button>
              </div>
              <div class="account-menu">
                <button
                  type="button"
                  class="account-trigger"
                  :aria-expanded="userMenuOpen ? 'true' : 'false'"
                  aria-haspopup="menu"
                  :title="user.name"
                  @click="userMenuOpen = !userMenuOpen"
                >
                  <span>{{ userInitials }}</span>
                </button>
                <div v-if="userMenuOpen" class="account-popover" role="menu">
                  <button type="button" role="menuitem" @click="openProfileDialog">Perfil</button>
                  <button type="button" role="menuitem" @click="logout">Sair</button>
                </div>
              </div>
            </div>
          </header>

          <div class="sidebar-backdrop" :class="{ active: sidebarOpen }" @click="sidebarOpen = false"></div>

          <div class="workspace-layout" :class="{ 'menu-open': sidebarOpen }">
            <aside class="workspace-rail" :class="{ open: sidebarOpen }" aria-label="Navegação do workspace" @click="!sidebarOpen && toggleSidebar()">
              <div class="sidebar-mobile-head">
                <div class="sidebar-brand-lockup">
                  <span class="brand-mark">
                    <img :src="demandeskMarkUrl" alt="" aria-hidden="true">
                  </span>
                  <div>
                    <strong class="brand-wordmark">Demandesk</strong>
                    <span>{{ isClient ? 'Portal do cliente' : 'Operação Mazzitech' }}</span>
                  </div>
                </div>
                <button type="button" @click="sidebarOpen = false">Fechar</button>
              </div>
              <div class="sidebar-productivity-card">
                <span>Visão rápida</span>
                <strong>{{ sidebarPulse.title }}</strong>
                <div>
                  <small>{{ sidebarPulse.pending }} pendentes</small>
                  <small>{{ sidebarPulse.projects }} projetos</small>
                  <small v-if="isAdmin">{{ sidebarPulse.drafts }} rascunhos</small>
                </div>
              </div>
              <div v-for="section in navigationSections" :key="section.title" class="rail-section">
                <span class="rail-section-label">{{ section.title }}</span>
                <div v-for="item in section.items" :key="item.value" class="rail-item-wrap">
                  <button
                    class="rail-item"
                    :class="{ active: isNavigationItemActive(item) }"
                    type="button"
                    :title="item.text"
                    @click="handleNavigationItem(item)"
                  >
                    <span class="rail-icon" aria-hidden="true">
                      <!-- dashboard -->
                      <svg v-if="item.icon === 'dashboard'" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="1" y="1" width="6" height="6" rx="1.5" fill="currentColor" opacity=".9"/>
                        <rect x="9" y="1" width="6" height="6" rx="1.5" fill="currentColor" opacity=".5"/>
                        <rect x="1" y="9" width="6" height="6" rx="1.5" fill="currentColor" opacity=".5"/>
                        <rect x="9" y="9" width="6" height="6" rx="1.5" fill="currentColor" opacity=".9"/>
                      </svg>
                      <!-- requests / reports -->
                      <svg v-else-if="item.icon === 'requests' || item.icon === 'reports'" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="2" y="2" width="12" height="12" rx="2" stroke="currentColor" stroke-width="1.5"/>
                        <line x1="5" y1="6" x2="11" y2="6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                        <line x1="5" y1="9" x2="9" y2="9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                      </svg>
                      <!-- demands -->
                      <svg v-else-if="item.icon === 'demands'" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="1" y="3" width="4" height="10" rx="1.5" fill="currentColor" opacity=".5"/>
                        <rect x="6" y="1" width="4" height="14" rx="1.5" fill="currentColor" opacity=".9"/>
                        <rect x="11" y="5" width="4" height="8" rx="1.5" fill="currentColor" opacity=".5"/>
                      </svg>
                      <!-- projects -->
                      <svg v-else-if="item.icon === 'projects'" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="1" y="5" width="14" height="9" rx="1.5" stroke="currentColor" stroke-width="1.5"/>
                        <path d="M5 5V4a3 3 0 0 1 6 0v1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                      </svg>
                      <!-- users -->
                      <svg v-else-if="item.icon === 'users'" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="6" cy="5" r="2.5" stroke="currentColor" stroke-width="1.5"/>
                        <path d="M1 13c0-2.761 2.239-4 5-4s5 1.239 5 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                        <path d="M11 7c1.5 0 3 .8 3 3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                        <circle cx="11" cy="4.5" r="1.5" stroke="currentColor" stroke-width="1.5"/>
                      </svg>
                      <!-- fallback -->
                      <svg v-else width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="8" cy="8" r="6" stroke="currentColor" stroke-width="1.5"/>
                      </svg>
                    </span>
                    <span class="rail-item-main">
                      <strong>{{ item.text }}</strong>
                      <small>{{ item.description }}</small>
                    </span>
                    <span v-if="navigationItemBadge(item)" class="rail-item-badge">{{ navigationItemBadge(item) }}</span>
                    <small v-if="item.value === 'demands'" class="rail-caret" :class="{ open: demandsMenuOpen }">
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M3 4.5l3 3 3-3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                    </small>
                  </button>
                  <div v-if="item.value === 'demands' && demandsMenuOpen" class="rail-projects">
                    <div class="rail-projects-head">
                      <span>Projetos</span>
                      <button type="button" :class="{ active: activeView === 'demands' && !selectedBoardProjectId }" @click="selectAllDemandProjects">
                        Todos
                      </button>
                    </div>
                    <button
                      v-for="project in demandMenuProjects"
                      :key="project.id"
                      type="button"
                      class="rail-project-item"
                      :class="{ active: activeView === 'demands' && selectedBoardProjectId === project.id }"
                      :title="project.name"
                      @click="selectDemandProject(project.id)"
                    >
                      {{ project.name }}
                    </button>
                  </div>
                </div>
              </div>
              <div class="sidebar-account-strip">
                <span>{{ userRoleLabel(user) }}</span>
                <strong>{{ user.name }}</strong>
              </div>
            </aside>

            <main class="workspace-main" :class="{ 'requests-workspace': activeView === 'requests' }">
              <section class="work-grid">
                <section v-if="activeView === 'dashboard'" class="resource-screen dashboard-screen">
                  <div class="resource-header">
                    <div>
                      <span>Dashboard</span>
                      <h2>{{ clientProjectName }}</h2>
                      <p>Resumo do contexto selecionado, com foco no que precisa de acompanhamento.</p>
                    </div>
                  </div>

                  <div class="dashboard-metrics">
                    <div class="dashboard-metric metric-info">
                      <span>Solicitações abertas</span>
                      <strong>{{ openRequestsCount }}</strong>
                    </div>
                    <div class="dashboard-metric metric-progress">
                      <span>Demandas em desenvolvimento</span>
                      <strong>{{ developmentDemands.length }}</strong>
                    </div>
                    <div class="dashboard-metric metric-warning">
                      <span>Demandas pendentes</span>
                      <strong>{{ dashboardSummary.pending }}</strong>
                    </div>
                    <div class="dashboard-metric metric-success">
                      <span>Concluídas</span>
                      <strong>{{ dashboardSummary.done }}</strong>
                    </div>
                  </div>

                  <div class="dashboard-grid">
                    <section class="dashboard-panel">
                      <div class="table-toolbar">
                        <strong>Últimas solicitações</strong>
                        <button type="button" @click="setWorkspaceView('requests')">Ver todas</button>
                      </div>
                      <div class="dashboard-list">
                        <button
                          v-for="request in recentRequests"
                          :key="request.id"
                          type="button"
                          class="dashboard-list-item"
                          @click="openRequestDetails(request)"
                        >
                          <div class="dashboard-item-main">
                            <strong>{{ request.displayId }} · {{ request.title }}</strong>
                            <small>{{ request.projectName }} · {{ formatDateTime(request.updatedAt) }}</small>
                          </div>
                          <span class="status-badge" :class="requestStatusClass(request.status)">{{ request.status }}</span>
                        </button>
                        <div v-if="!recentRequests.length" class="dashboard-empty">Nenhuma solicitação neste contexto.</div>
                      </div>
                    </section>

                    <section class="dashboard-panel">
                      <div class="table-toolbar">
                        <strong>Demandas em desenvolvimento</strong>
                        <button type="button" @click="setWorkspaceView('demands')">Ver kanban</button>
                      </div>
                      <div class="dashboard-list">
                        <button
                          v-for="demand in developmentDemands"
                          :key="demand.id"
                          type="button"
                          class="dashboard-list-item"
                          @click="openDemandDetails(demand)"
                        >
                          <div class="dashboard-item-main">
                            <strong>{{ demand.displayId }} · {{ demand.title }}</strong>
                            <small>{{ isClient ? demand.company : `${demand.company} · ${demand.owner || 'Sem responsável'}` }}</small>
                          </div>
                          <div class="dashboard-badges">
                            <span class="status-badge" :class="demandStatusClass(demand.status)">{{ demand.status }}</span>
                            <span class="priority-badge" :class="priorityClass(demand.priority)">{{ priorityLabel(demand.priority) }}</span>
                          </div>
                        </button>
                        <div v-if="!developmentDemands.length" class="dashboard-empty">Nenhuma demanda em desenvolvimento.</div>
                      </div>
                    </section>
                  </div>
                </section>

                <section v-else-if="activeView === 'requests'" class="requests-screen shadcn-screen">
                  <div class="resource-header shadcn-page-header">
                    <div>
                      <span>Solicitações</span>
                      <h2>Tickets e conversas</h2>
                      <p>Solicitações abertas pelos clientes, respostas da equipe e demandas vinculadas.</p>
                    </div>
                    <UiButton v-if="isClient" @click="openRequestModal">
                      Nova solicitação
                    </UiButton>
                  </div>

                  <v-alert v-if="error" type="error" outlined dense>{{ error }}</v-alert>
                  <UiCard class="requests-table-card">
                    <div class="table-toolbar shadcn-card-header">
                      <div>
                        <strong>{{ requests.length }} solicitações</strong>
                        <span>Use o código curto para localizar e compartilhar rapidamente.</span>
                      </div>
                    </div>
                    <div class="requests-table-scroll" tabindex="0" role="region" aria-label="Lista de solicitações">
                    <table class="resource-table">
                      <thead>
                        <tr>
                          <th>Código</th>
                          <th>Solicitação</th>
                          <th>Projeto</th>
                          <th>Status</th>
                          <th>Prioridade</th>
                          <th v-if="isStaff">Horas</th>
                          <th class="actions-col">Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="item in requests" :key="item.id">
                          <td data-label="Código">
                            <button type="button" class="resource-link request-code-link" @click="openRequestDetails(item)">
                              {{ item.displayId }}
                            </button>
                          </td>
                          <td data-label="Solicitação">
                            <button type="button" class="resource-link request-title-link" @click="openRequestDetails(item)">
                              {{ item.title }}
                            </button>
                            <small>Aberta por {{ requestAuthorLabel(item) }} · {{ formatDateTime(item.updatedAt) }}</small>
                          </td>
                          <td data-label="Projeto">{{ item.projectName }}</td>
                          <td data-label="Status">
                            <UiBadge :variant="requestStatusBadgeVariant(item.status)" class="request-status-chip">
                              {{ item.status }}
                            </UiBadge>
                          </td>
                          <td data-label="Prioridade">
                            <UiBadge variant="outline">{{ priorityLabel(item.priority) }}</UiBadge>
                          </td>
                          <td v-if="isStaff" data-label="Horas">{{ item.hours }}h</td>
                          <td class="actions-cell" data-label="Ações">
                            <UiButton variant="outline" size="sm" @click="openRequestDetails(item)">Abrir</UiButton>
                            <v-menu v-if="isStaff" offset-y>
                              <template #activator="{ on, attrs }">
                                <button type="button" v-bind="attrs" v-on="on" :disabled="requestActionBusy" :aria-label="'Mais ações para ' + item.displayId">•••</button>
                              </template>
                              <v-list dense>
                                <v-list-item v-if="!item.archivedAt" @click="createDemandForRequest(item)"><v-list-item-title>Transformar em demanda</v-list-item-title></v-list-item>
                                <v-list-item :disabled="requestActionBusy" @click="changeRequestLifecycle(item, item.archivedAt ? 'restore' : 'archive')"><v-list-item-title>{{ item.archivedAt ? 'Restaurar' : 'Arquivar' }}</v-list-item-title></v-list-item>
                                <v-list-item v-if="isAdmin" :disabled="requestActionBusy" @click="changeRequestLifecycle(item, 'delete')"><v-list-item-title class="error--text">Excluir solicitação</v-list-item-title></v-list-item>
                              </v-list>
                            </v-menu>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                    </div>
                    <div v-if="!requests.length" class="resource-empty">Nenhuma solicitação encontrada.</div>
                  </UiCard>
                </section>

                <section v-else-if="activeView === 'demands'" class="board-panel trello-board-panel">
                  <v-alert v-if="error" type="error" outlined dense class="mb-4">{{ error }}</v-alert>
                  <div v-if="loading" class="center-state board-state"><v-progress-circular indeterminate color="primary" /></div>

                  <div v-else class="kanban-workspace">
                    <div class="kanban-summary-bar">
                      <div class="kanban-summary-title">
                        <span>Quadro</span>
                        <strong>{{ demandProjectScopeLabel }}</strong>
                      </div>
                      <div class="kanban-summary-stats">
                        <div>
                          <span>Total</span>
                          <strong>{{ demandBoardInsights.total }}</strong>
                        </div>
                        <div>
                          <span>Pendentes</span>
                          <strong>{{ demandBoardInsights.pending }}</strong>
                        </div>
                        <div>
                          <span>Concluídas</span>
                          <strong>{{ demandBoardInsights.done }}</strong>
                        </div>
                        <div>
                          <span>Alta prioridade</span>
                          <strong>{{ demandBoardInsights.highPriority }}</strong>
                        </div>
                      </div>
                      <div class="kanban-summary-actions">
                        <template v-if="isStaff">
                          <button v-if="isAdmin" type="button" class="btn-ghost" @click="openBulkDemandModal">Criar em massa</button>
                          <button type="button" class="btn-glow" @click="openDemandModal({ lockProjectId: selectedBoardProjectId })">Nova demanda</button>
                        </template>
                        <button v-if="selectedBoardProjectId" type="button" class="btn-ghost" @click="openGanttView">Cronograma</button>
                        <button v-if="isStaff" type="button" class="btn-ghost" @click="openArchiveDialog">Arquivadas</button>
                        <button type="button" class="kanban-summary-toggle" :class="{ active: kanbanInsightsOpen }" @click="kanbanInsightsOpen = !kanbanInsightsOpen">
                          <svg width="13" height="13" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" :style="{ transform: kanbanInsightsOpen ? 'rotate(180deg)' : 'none', transition: 'transform 160ms ease' }">
                            <path d="M3 6l5 5 5-5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                          </svg>
                          {{ kanbanInsightsOpen ? 'Ocultar detalhes' : 'Ver detalhes' }}
                        </button>
                      </div>
                    </div>

                    <aside v-if="kanbanInsightsOpen" class="kanban-insights-panel kanban-insights-panel-expanded" aria-label="Resumo do quadro">
                      <section class="insight-card">
                        <header>
                          <span>Prioridades</span>
                          <strong>{{ demandBoardInsights.active }} ativas</strong>
                        </header>
                        <div class="priority-distribution">
                          <div v-for="item in demandPriorityBreakdown" :key="item.label" :class="priorityClass(item.label)">
                            <strong>{{ item.count }}</strong>
                            <span class="priority-name">{{ item.label }}</span>
                          </div>
                        </div>
                        <div class="priority-ratio-bar">
                          <i
                            v-for="item in demandPriorityBreakdown"
                            :key="item.label"
                            :class="priorityClass(item.label)"
                            :style="{ '--fill': item.width }"
                          ></i>
                        </div>
                        <p class="priority-hint">Distribuição das demandas do quadro por nível de prioridade.</p>
                      </section>

                      <section class="insight-card">
                        <header>
                          <span>Próximas entregas</span>
                          <strong>{{ demandNextFocus.length }}</strong>
                        </header>
                        <div class="focus-list">
                          <button
                            v-for="demand in demandNextFocus"
                            :key="demand.id"
                            type="button"
                            @click="openDemandDetails(demand)"
                          >
                            <span>{{ demand.displayId }}</span>
                            <strong>{{ demand.title }}</strong>
                            <small>{{ demand.dueDate ? formatDate(demand.dueDate) : demand.status }}</small>
                          </button>
                          <p v-if="!demandNextFocus.length">Nenhuma entrega crítica no filtro atual.</p>
                        </div>
                      </section>
                    </aside>

                    <div class="kanban-board">
              <section
                v-for="(column, columnIndex) in kanbanColumns"
                :key="column.status"
                class="kanban-column"
                :class="[column.className, { 'column-drop-before': columnDropTargetStatus === column.status && columnDropPlacement === 'before', 'column-drop-after': columnDropTargetStatus === column.status && columnDropPlacement === 'after' }]"
                @dragover.prevent="handleColumnDragOver(column, $event)"
                @dragleave="leaveColumnDragTarget(column, $event)"
                @drop.prevent="dropKanbanItem(column, null, $event)"
              >
                <header class="kanban-column-header">
                  <div>
                    <button
                      v-if="isAdmin && selectedBoardProjectId"
                      type="button"
                      class="column-drag-handle"
                      draggable="true"
                      :aria-label="`Mover coluna ${column.label}`"
                      :title="`Arrastar coluna ${column.label}`"
                      @dragstart.stop="startDraggingColumn(column, $event)"
                      @dragend="stopDraggingColumn"
                    >
                      <svg width="14" height="16" viewBox="0 0 14 16" fill="currentColor" aria-hidden="true"><circle cx="4" cy="3" r="1.2"/><circle cx="10" cy="3" r="1.2"/><circle cx="4" cy="8" r="1.2"/><circle cx="10" cy="8" r="1.2"/><circle cx="4" cy="13" r="1.2"/><circle cx="10" cy="13" r="1.2"/></svg>
                    </button>
                    <span class="list-dot" :style="{ borderColor: column.color }"></span>
                    <span>{{ column.label }}</span>
                    <strong class="column-count">{{ column.items.length }}</strong>
                    <small v-if="isStaff" class="column-hours">{{ column.hours }}h</small>
                  </div>
                  <div v-if="isStaff" class="column-header-actions">
                    <button
                      v-if="inlineDemandComposer.status !== column.status"
                      type="button"
                      class="column-header-add"
                      :aria-label="`Adicionar demanda em ${column.label}`"
                      @click.stop="openInlineDemandComposer(column)"
                    >
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M7 2v10M2 7h10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
                    </button>
                    <v-menu offset-y left>
                      <template v-slot:activator="{ on, attrs }">
                        <button class="column-menu-button" v-bind="attrs" v-on="on" @click.stop :aria-label="`Opções da coluna ${column.label}`">
                          <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><circle cx="7" cy="2.5" r="1.2" fill="currentColor"/><circle cx="7" cy="7" r="1.2" fill="currentColor"/><circle cx="7" cy="11.5" r="1.2" fill="currentColor"/></svg>
                        </button>
                      </template>
                      <v-list dense class="card-menu-list">
                        <template v-if="isAdmin && selectedBoardProjectId">
                          <v-list-item :disabled="savingColumnOrder || columnIndex === 0" @click.stop="moveColumnBy(column, -1)">
                            <v-list-item-title>Mover para esquerda</v-list-item-title>
                          </v-list-item>
                          <v-list-item :disabled="savingColumnOrder || columnIndex === kanbanColumns.length - 1" @click.stop="moveColumnBy(column, 1)">
                            <v-list-item-title>Mover para direita</v-list-item-title>
                          </v-list-item>
                          <v-divider />
                        </template>
                        <v-list-item
                          v-for="status in columnMoveTargets(column)"
                          :key="status"
                          :disabled="!column.items.length || movingColumnStatus === column.status"
                          @click.stop="moveColumnDemands(column, status)"
                        >
                          <v-list-item-title>Mover todos para {{ status }}</v-list-item-title>
                        </v-list-item>
                      </v-list>
                    </v-menu>
                  </div>
                </header>

                <p v-if="column.description" class="kanban-column-description">{{ column.description }}</p>

                <form
                  v-if="inlineDemandComposer.status === column.status"
                  class="inline-demand-composer"
                  @submit.prevent="createInlineDemand(column)"
                >
                  <textarea
                    v-model="inlineDemandComposer.title"
                    :ref="`inlineDemandTitle-${column.status}`"
                    rows="3"
                    placeholder="Digite o título da demanda..."
                    @keydown.enter.exact.prevent="createInlineDemand(column)"
                    @keydown.esc.prevent="closeInlineDemandComposer"
                  ></textarea>
                  <div v-if="column.className === 'is-done'" class="inline-demand-extra">
                    <input v-model="inlineDemandComposer.dueDate" type="date" @keydown.esc.prevent="closeInlineDemandComposer" />
                  </div>
                  <div class="inline-demand-actions">
                    <button type="submit" :disabled="savingInlineDemand">
                      {{ savingInlineDemand ? 'Adicionando...' : 'Adicionar' }}
                    </button>
                    <button type="button" @click="closeInlineDemandComposer">Cancelar</button>
                  </div>
                </form>

                <div
                  v-for="demand in column.items"
                  :key="demand.id"
                  class="demand-card"
                  :class="{ completed: isDemandCompleted(demand.status), readonly: !isStaff }"
                  :draggable="isStaff"
                  @click="openDemandDetails(demand)"
                  @dragstart="startDragging(demand)"
                  @dragend="stopDragging"
                  @dragover.prevent
                  @drop.stop.prevent="dropKanbanItem(column, demand, $event)"
                >
                  <div class="card-labels" v-if="demand.priority">
                    <span class="card-label" :class="priorityClass(demand.priority)"></span>
                  </div>
                  <div class="card-body">
                    <div class="card-title-row">
                      <span class="card-title">{{ demand.title }}</span>
                      <v-menu v-if="isStaff" offset-y left>
                        <template v-slot:activator="{ on, attrs }">
                          <button class="card-menu-button" v-bind="attrs" v-on="on" @click.stop aria-label="Opções da demanda">
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><circle cx="7" cy="2.5" r="1.2" fill="currentColor"/><circle cx="7" cy="7" r="1.2" fill="currentColor"/><circle cx="7" cy="11.5" r="1.2" fill="currentColor"/></svg>
                          </button>
                        </template>
                        <v-list dense class="card-menu-list">
                          <v-list-item v-for="status in statusMoveOptions" :key="status" @click.stop="moveDemand(demand, status)">
                            <v-list-item-title>Mover para {{ status }}</v-list-item-title>
                          </v-list-item>
                          <v-divider />
                          <v-list-item @click.stop="setDemandArchived(demand, true)">
                            <v-list-item-title>Arquivar</v-list-item-title>
                          </v-list-item>
                          <v-list-item @click.stop="deleteDemand(demand)">
                            <v-list-item-title class="danger-text">Excluir</v-list-item-title>
                          </v-list-item>
                        </v-list>
                      </v-menu>
                    </div>
                    <p v-if="demand.company" class="card-project-name">{{ demand.company }}</p>
                    <p v-if="demand.moduleName || demand.storyTitle" class="card-schedule-path">
                      <span v-if="demand.moduleName">{{ demand.moduleName }}</span>
                      <span v-if="demand.moduleName && demand.storyTitle">/</span>
                      <span v-if="demand.storyTitle">{{ demand.storyTitle }}</span>
                    </p>
                  </div>
                  <div class="card-footer">
                    <div class="card-footer-left">
                      <span class="demand-display-id">{{ demand.displayId }}</span>
                      <span
                        v-if="demand.requestId"
                        class="request-age-pill"
                        :class="requestAgeClass(demand)"
                        :title="requestAgeTitle(demand)"
                      ><span aria-hidden="true"></span>{{ requestAgeLabel(demand) }}</span>
                      <span v-if="Number(demand.totalHours || 0) > 0" class="card-hour-tag" title="Total de horas">
                        {{ formatReportHours(demand.totalHours) }}
                      </span>
                      <span v-if="demand.attachments && demand.attachments.length" class="card-meta-icon" title="Anexos">
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 4.5A2.5 2.5 0 0 1 6.5 2l3 3A2.5 2.5 0 0 1 6 8.5L4 6.5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>
                        {{ demand.attachments.length }}
                      </span>
                    </div>
                    <div class="card-footer-right">
                      <span v-if="demand.dueDate" class="card-due" :class="{ overdue: new Date(demand.dueDate) < new Date() && !demand.status.toLowerCase().includes('conclu'), done: demand.status.toLowerCase().includes('conclu') }">{{ formatDate(demand.dueDate) }}</span>
                      <span v-if="isStaff && demand.owner" class="card-avatar" :title="demand.owner">{{ demand.owner.charAt(0).toUpperCase() }}</span>
                    </div>
                  </div>
                </div>
                <div v-if="!column.items.length && inlineDemandComposer.status !== column.status" class="kanban-empty">Nenhuma demanda nesta etapa.</div>

                <button
                  v-if="isStaff && inlineDemandComposer.status !== column.status"
                  type="button"
                  class="column-add-button"
                  :aria-label="`Adicionar demanda em ${column.label}`"
                  @click.stop="openInlineDemandComposer(column)"
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M7 2v10M2 7h10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
                  Adicionar demanda
                </button>
              </section>

              <button v-if="isAdmin" class="add-list-card" type="button" @click="openListModal">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M8 3v10M3 8h10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                </svg>
                Nova coluna
              </button>
                    </div>
                  </div>
                </section>

                <section v-else-if="activeView === 'gantt'" class="board-panel gantt-panel schedule-panel" :class="{ 'schedule-fullscreen': scheduleFullscreen }">
                  <div v-if="loadingGantt" class="center-state board-state"><v-progress-circular indeterminate color="primary" /></div>
                  <ScheduleWorkspace
                    v-else-if="selectedBoardProject"
                    :project="selectedBoardProject"
                    :demands="ganttDemands"
                    :is-staff="isStaff"
                    :status-options="statusMoveOptions"
                    :owner-options="scheduleOwnerOptions"
                    @back="setWorkspaceView('demands')"
                    @export="openGanttExportModal"
                    @open-task="openDemandDetails"
                    @demand-created="handleScheduleDemandCreated"
                    @demand-updated="handleScheduleDemandUpdated"
                    @refresh-demands="refreshScheduleDemands"
                    @fullscreen-change="scheduleFullscreen = $event"
                  />
                </section>

                <section v-else-if="activeView === 'reports'" class="resource-screen reports-screen">
                  <div class="resource-header">
                    <div>
                      <span>Relatórios</span>
                      <h2>Projetos por mês</h2>
                      <p>Acompanhe demandas criadas e horas registradas por projeto no ano selecionado.</p>
                    </div>
                    <v-btn depressed color="primary" class="new-demand-button" @click="openReportExportModal">
                      Exportar horas
                    </v-btn>
                  </div>

                  <div class="toolbar report-toolbar">
                    <v-select v-model="reportYear" :items="reportYearOptions" label="Ano" outlined dense hide-details @change="loadReports" />
                    <v-select v-model="reportProjectId" :items="reportProjectOptions" label="Projeto" outlined dense hide-details @change="loadReports" />
                  </div>

                  <div class="dashboard-metrics report-metrics">
                    <div class="dashboard-metric metric-info">
                      <span>Demandas no período</span>
                      <strong>{{ reportTotals.tasks }}</strong>
                    </div>
                    <div class="dashboard-metric metric-progress">
                      <span>Horas registradas</span>
                      <strong>{{ formatReportHours(reportTotals.hours) }}</strong>
                    </div>
                    <div class="dashboard-metric metric-success">
                      <span>Projetos com demandas</span>
                      <strong>{{ reportTotals.activeProjects }}</strong>
                    </div>
                  </div>

                  <div class="table-panel">
                    <div class="table-toolbar">
                      <strong>{{ reportProjectRows.length }} {{ reportProjectRows.length === 1 ? 'projeto' : 'projetos' }}</strong>
                      <span>Demandas criadas no mês, com soma de horas da demanda.</span>
                    </div>
                    <div v-if="loadingReports" class="center-state board-state">
                      <v-progress-circular indeterminate color="primary" />
                    </div>
                    <table v-else class="resource-table report-table">
                      <thead>
                        <tr>
                          <th>Projeto</th>
                          <th v-for="month in reportMonths" :key="month.key">{{ month.short }}</th>
                          <th>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="project in reportProjectRows" :key="project.projectId">
                          <td data-label="Projeto">
                            <strong>{{ project.projectName }}</strong>
                            <small>{{ project.totalTasks }} demandas · {{ formatReportHours(project.totalHours) }}</small>
                          </td>
                          <td v-for="month in project.months" :key="month.key" :data-label="month.short">
                            <strong>{{ month.tasks }}</strong>
                            <small>{{ formatReportHours(month.hours) }}</small>
                          </td>
                          <td data-label="Total">
                            <strong>{{ project.totalTasks }}</strong>
                            <small>{{ formatReportHours(project.totalHours) }}</small>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                    <div v-if="!loadingReports && !reportProjectRows.length" class="resource-empty">Nenhum projeto encontrado para o relatório.</div>
                  </div>
                </section>

                <section v-else-if="activeView === 'projects'" class="resource-screen">
                  <div class="resource-header">
                    <div>
                      <span>Projetos</span>
                      <h2>Projetos</h2>
                      <p>Cadastre e organize os projetos usados nas demandas e solicitações.</p>
                    </div>
                    <v-btn v-if="isAdmin" depressed color="primary" class="new-demand-button" @click="openProjectModal">
                      Novo projeto
                    </v-btn>
                  </div>

                  <div class="table-panel">
                    <div class="table-toolbar">
                      <strong>{{ visibleProjects.length }} {{ visibleProjects.length === 1 ? 'projeto' : 'projetos' }}</strong>
                      <span>{{ isClient ? 'Contexto disponível para seu acesso.' : 'Base usada para vincular clientes, devs e demandas.' }}</span>
                    </div>
                    <table class="resource-table">
                      <thead>
                        <tr>
                          <th>Projeto</th>
                          <th>Tipo</th>
                          <th>Responsável</th>
                          <th>Notas</th>
                          <th v-if="isStaff" class="actions-col">Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="project in visibleProjects" :key="project.id">
                          <td data-label="Projeto"><strong>{{ project.name }}</strong></td>
                          <td data-label="Tipo"><v-chip x-small label>{{ project.contractType }}</v-chip></td>
                          <td data-label="Responsável">{{ project.owner || 'Sem responsável' }}</td>
                          <td data-label="Notas">{{ project.notes || 'Sem notas' }}</td>
                          <td v-if="isStaff" class="actions-cell" data-label="Ações">
                            <button type="button" @click="prepareDemandForProject(project)">Nova demanda</button>
                            <button type="button" @click="selectBoardProject(project.id)">Ver kanban</button>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                    <div v-if="!visibleProjects.length" class="resource-empty">Nenhum projeto encontrado para o contexto selecionado.</div>
                  </div>

                  <WhatsappGroupsPanel v-if="isAdmin" :projects="projects" />
                </section>

                <section v-else-if="activeView === 'users'" class="resource-screen">
                  <div class="resource-header">
                    <div>
                      <span>Usuários</span>
                      <h2>Acessos e vínculos</h2>
                      <p>Crie contas para clientes, administradores e devs vinculados aos projetos certos.</p>
                    </div>
                    <div class="resource-actions">
                      <v-btn v-if="isAdmin" outlined color="primary" class="new-demand-button" @click="openInviteModal">
                        Gerar convite
                      </v-btn>
                      <v-btn v-if="isAdmin" depressed color="primary" class="new-demand-button" @click="openUserModal">
                        Novo usuário
                      </v-btn>
                    </div>
                  </div>

                  <div class="table-panel invite-panel">
                    <div class="table-toolbar">
                      <strong>{{ clientInvites.length }} {{ clientInvites.length === 1 ? 'link público' : 'links públicos' }}</strong>
                      <span>Convites por projeto válidos até serem excluídos.</span>
                    </div>
                    <table class="resource-table">
                      <thead>
                        <tr>
                          <th>Projeto</th>
                          <th>Link</th>
                          <th>Criado em</th>
                          <th class="actions-col">Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="invite in clientInvites" :key="invite.id">
                          <td data-label="Projeto"><strong>{{ inviteProjectLabel(invite) }}</strong></td>
                          <td data-label="Link"><small>{{ invite.registerUrl || 'Link antigo não recuperável' }}</small></td>
                          <td data-label="Criado em">{{ formatDateTime(invite.createdAt) }}</td>
                          <td class="actions-cell" data-label="Ações">
                            <button type="button" :disabled="!invite.registerUrl" @click="copyInviteUrl(invite)">Copiar</button>
                            <button type="button" @click="deleteInvite(invite)">Excluir</button>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                    <div v-if="!clientInvites.length" class="resource-empty">Nenhum link público criado ainda.</div>
                  </div>

                  <div class="table-panel">
                    <div class="user-tabs" role="tablist" aria-label="Tipo de usuário">
                      <button
                        v-for="tab in userDirectoryTabs"
                        :key="tab.value"
                        type="button"
                        role="tab"
                        :aria-selected="userDirectoryTab === tab.value"
                        :class="{ active: userDirectoryTab === tab.value }"
                        @click="userDirectoryTab = tab.value"
                      >
                        <strong>{{ tab.text }}</strong>
                        <span>{{ tab.count }}</span>
                      </button>
                    </div>
                    <div class="table-toolbar">
                      <strong>{{ filteredUsers.length }} {{ filteredUsers.length === 1 ? 'usuário' : 'usuários' }}</strong>
                      <span>{{ userDirectoryTab === 'clients' ? 'Clientes com acesso ao portal.' : 'Admins e devs da operação.' }}</span>
                    </div>
                    <table class="resource-table">
                      <thead>
                        <tr>
                          <th>Usuário</th>
                          <th>Perfil</th>
                          <th>Projetos</th>
                          <th>Status</th>
                          <th class="actions-col">Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="item in filteredUsers" :key="item.id">
                          <td data-label="Usuário">
                            <strong>{{ item.name }}</strong>
                            <small>@{{ item.username }} · {{ item.email }}</small>
                          </td>
                          <td data-label="Perfil"><v-chip x-small label>{{ userRoleLabel(item) }}</v-chip></td>
                          <td data-label="Projetos">{{ userProjectLabel(item) }}</td>
                          <td data-label="Status">{{ item.active ? 'Ativo' : 'Inativo' }}</td>
                          <td class="actions-cell" data-label="Ações">
                            <button v-if="isAdmin" type="button" @click="openUserModal(item)">Editar</button>
                            <button v-if="isAdmin && item.role !== 'admin'" type="button" @click="openUserProjectsModal(item)">Projetos</button>
                            <button
                              v-if="isAdmin && item.id !== user.id"
                              type="button"
                              @click="toggleUserActive(item)"
                            >
                              {{ item.active ? 'Desativar' : 'Ativar' }}
                            </button>
                            <button type="button" @click="copyUserEmail(item)">Copiar e-mail</button>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                    <div v-if="!filteredUsers.length" class="resource-empty">
                      {{ userDirectoryTab === 'clients' ? 'Nenhum cliente cadastrado ainda.' : 'Nenhum admin ou dev cadastrado ainda.' }}
                    </div>
                  </div>
                </section>

                <section v-else-if="activeView === 'drafts'" class="resource-screen drafts-screen">
                  <div class="resource-header">
                    <div>
                      <span>Demandas</span>
                      <h2>Rascunhos</h2>
                      <p>Rascunhos são visíveis para todos os administradores. Ao publicar, a demanda vai para a coluna escolhida — ou <strong>Pendente</strong> por padrão.</p>
                    </div>
                    <button class="btn-glow" @click="openBulkDraftModal">Criar rascunhos</button>
                  </div>
                  <div v-if="loadingDrafts" class="resource-empty">Carregando...</div>
                  <div v-else-if="!demandDrafts.length" class="resource-empty">Nenhum rascunho ainda. Crie um usando o botão acima.</div>
                  <div v-else class="drafts-list">
                    <div v-for="draft in demandDrafts" :key="draft.id" class="draft-item">
                      <div class="draft-item-main">
                        <div class="draft-item-title">
                          <span v-if="!draft._editing">{{ draft.title }}</span>
                          <input v-else v-model="draft._editTitle" class="draft-edit-input" @keydown.enter="saveDraftEdit(draft)" @keydown.esc="cancelDraftEdit(draft)" />
                        </div>
                        <div class="draft-item-meta">
                          <span v-if="draft.company">{{ draft.company }}</span>
                          <span v-if="draft.priority" class="draft-priority" :class="priorityClass(draft.priority)">{{ priorityLabel(draft.priority) }}</span>
                          <span v-if="draft.createdByName" class="draft-author">por {{ draft.createdByName }}</span>
                          <span v-if="draft.attachments && draft.attachments.length">{{ draft.attachments.length }} anexo(s)</span>
                        </div>
                      </div>
                      <div class="draft-item-actions">
                        <template v-if="!draft._editing">
                          <button type="button" class="draft-btn draft-btn-edit" @click="startDraftEdit(draft)">Editar</button>
                          <div class="draft-publish-group">
                            <v-select
                              v-model="draft._publishStatus"
                              :items="publishStatusOptions"
                              dense
                              outlined
                              hide-details
                              class="draft-status-select"
                              placeholder="Coluna"
                            />
                            <button type="button" class="draft-btn draft-btn-publish" :disabled="draft._publishing" @click="publishDraft(draft)">
                              {{ draft._publishing ? 'Publicando...' : 'Publicar' }}
                            </button>
                          </div>
                          <button type="button" class="draft-btn draft-btn-delete" @click="deleteDraft(draft)">Excluir</button>
                        </template>
                        <template v-else>
                          <button type="button" class="draft-btn draft-btn-publish" @click="saveDraftEdit(draft)">Salvar</button>
                          <button type="button" class="draft-btn draft-btn-edit" @click="cancelDraftEdit(draft)">Cancelar</button>
                        </template>
                      </div>
                    </div>
                  </div>
                </section>

              </section>
            </main>
          </div>
        </div>

        <v-dialog v-model="requestDialog" max-width="720px">
          <v-card class="modal-card">
            <v-card-title><div><span>Nova solicitação</span><h2>Abrir ticket</h2></div></v-card-title>
            <v-card-text>
              <v-form ref="requestFormRef" @submit.prevent="createRequest">
                <v-select v-model="requestForm.projectId" :items="projectOptions" label="Projeto" outlined dense :rules="[rules.required]" />
                <v-text-field v-model="requestForm.title" label="Título" outlined dense :rules="[rules.required]" />
                <v-select v-model="requestForm.priority" :items="priorities" label="Prioridade" outlined dense />
                <v-textarea v-model="requestForm.description" label="Descrição" rows="5" outlined />
              </v-form>
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn text @click="requestDialog = false">Cancelar</v-btn>
              <v-btn depressed color="primary" :loading="savingRequest" @click="createRequest">Abrir solicitação</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <v-dialog v-model="requestDetailDialog" max-width="1040px" scrollable>
          <v-card v-if="selectedRequest" class="modal-card detail-modal">
            <v-card-title>
              <div>
                <span>{{ selectedRequest.displayId }}</span>
                <h2>{{ selectedRequest.title }}</h2>
              </div>
              <v-spacer />
              <v-btn
                v-if="isStaff"
                depressed
                color="primary"
                class="request-transform-button"
                :loading="savingRequest"
                @click="createDemandForRequest(selectedRequest)"
              >
                Transformar em demanda
              </v-btn>
              <v-btn icon text aria-label="Fechar solicitação" class="modal-close" @click="closeRequestDetails">
                <span aria-hidden="true">×</span>
              </v-btn>
            </v-card-title>
            <v-card-text>
              <div class="request-detail-grid">
                <section class="request-main-column">
                  <v-text-field v-model="requestDetailForm.title" label="Título" outlined dense :disabled="!isStaff" />
                  <v-textarea v-model="requestDetailForm.description" label="Descrição" rows="4" outlined :disabled="!isStaff" />

                  <div class="request-chat">
                    <h3>Conversa</h3>
                    <div class="chat-list">
                      <div
                        v-for="message in requestMessages"
                        :key="message.id"
                        class="chat-message"
                        :class="{ mine: message.userId === user.id, 'email-log': message.kind === 'email' }"
                      >
                        <strong>{{ message.kind === 'email' ? 'E-mail enviado ao cliente' : (message.authorName || 'Usuário') }}</strong>
                        <template v-if="message.kind === 'email'">
                          <p><b>Assunto:</b> {{ message.metadata && message.metadata.subject }}</p>
                          <p v-if="message.metadata && message.metadata.to"><b>Para:</b> {{ message.metadata.to }}</p>
                        </template>
                        <p v-else-if="message.text">{{ message.text }}</p>
                        <div v-if="message.attachments && message.attachments.length" class="message-attachments">
                          <a
                            v-for="attachment in message.attachments"
                            :key="attachment.id"
                            :href="attachment.dataUrl"
                            :download="attachment.name"
                            class="message-attachment"
                          >
                            <span>{{ attachmentIcon(attachment) }}</span>
                            <strong>{{ attachment.name }}</strong>
                            <small>{{ formatFileSize(attachment.size) }}</small>
                          </a>
                        </div>
                        <small>{{ formatDateTime(message.createdAt) }}</small>
                      </div>
                      <span v-if="!requestMessages.length" class="muted">Sem mensagens ainda.</span>
                    </div>
                    <v-textarea v-model="requestMessageDraft" rows="3" outlined dense label="Responder" @keydown.ctrl.enter.prevent="sendRequestMessage" />
                    <div class="request-attachment-box">
                      <input ref="requestAttachmentInput" type="file" multiple class="attachment-native-input" :accept="attachmentAccept" @change="addRequestAttachmentFiles">
                      <button type="button" class="request-attachment-button" aria-label="Anexar arquivos" title="Anexar arquivos" @click="openRequestAttachmentPicker">📎</button>
                      <span v-if="!requestMessageAttachments.length">Sem anexos</span>
                      <div v-else class="request-attachment-chips">
                        <span v-for="attachment in requestMessageAttachments" :key="attachment.id">
                          {{ attachment.name }}
                          <button type="button" @click="removeRequestAttachment(attachment.id)">×</button>
                        </span>
                      </div>
                    </div>
                    <div class="request-send-actions">
                      <v-btn depressed color="primary" :loading="sendingRequestMessage" @click="sendRequestMessage()">Enviar</v-btn>
                      <button
                        v-if="isStaff && selectedRequest && selectedRequest.status !== 'Aguardando cliente'"
                        type="button"
                        class="request-send-waiting"
                        :disabled="sendingRequestMessage"
                        @click="sendRequestMessage('Aguardando cliente')"
                      >
                        Enviar e aguardar cliente
                      </button>
                    </div>
                  </div>
                </section>

                <aside class="request-side-column">
                  <section class="request-side-panel">
                    <h3>Dados da solicitação</h3>
                    <v-select v-model="requestDetailForm.status" :items="requestStatusOptions" label="Status" outlined dense :disabled="!isStaff" />
                    <v-select v-model="requestDetailForm.priority" :items="priorities" label="Prioridade" outlined dense :disabled="!isStaff" />
                    <v-text-field v-if="isStaff" v-model="requestDetailForm.hours" label="Horas" type="number" min="0" step="0.25" outlined dense />
                    <v-btn v-if="isStaff" depressed color="primary" block :loading="savingRequest" @click="saveRequestDetails">Salvar solicitação</v-btn>
                  </section>

                  <section class="linked-demands">
                    <div class="table-toolbar">
                      <strong>Demandas vinculadas</strong>
                      <span>{{ selectedRequest.demands && selectedRequest.demands.length ? selectedRequest.demands.length + ' demandas' : 'Nenhuma demanda criada ainda' }}</span>
                    </div>
                    <div v-if="isStaff && !(selectedRequest.demands && selectedRequest.demands.length)" class="request-convert-empty">
                      <strong>Transforme este ticket em execução</strong>
                      <span>A demanda será criada no mesmo projeto e vinculada automaticamente à conversa.</span>
                      <v-btn depressed color="primary" block :loading="savingRequest" @click="createDemandForRequest(selectedRequest)">
                        Transformar em demanda
                      </v-btn>
                    </div>
                    <div v-if="isStaff" class="link-existing-demand">
                      <v-select
                        v-model="requestDetailForm.linkDemandId"
                        :items="requestDemandLinkOptions"
                        label="Vincular demanda existente"
                        outlined
                        dense
                        hide-details
                        clearable
                        no-data-text="Nenhuma demanda disponível neste projeto"
                      />
                      <v-btn
                        outlined
                        color="primary"
                        :disabled="!requestDetailForm.linkDemandId"
                        :loading="savingRequest"
                        @click="linkExistingDemandToRequest"
                      >
                        Vincular
                      </v-btn>
                    </div>
                    <button
                      v-for="demand in selectedRequest.demands"
                      :key="demand.id"
                      type="button"
                      class="linked-demand-item"
                      @click="openLinkedDemandFromRequest(demand)"
                    >
                      <strong>{{ demand.displayId }} · {{ demand.title }}</strong>
                      <span class="linked-demand-badges">
                        <span class="status-badge" :class="demandStatusClass(demand.status)">{{ demand.status }}</span>
                        <span class="priority-badge" :class="priorityClass(demand.priority)">{{ priorityLabel(demand.priority) }}</span>
                      </span>
                    </button>
                    <v-btn v-if="isStaff" outlined color="primary" class="mt-3" @click="createDemandForRequest(selectedRequest)">Criar outra demanda vinculada</v-btn>
                  </section>
                </aside>
              </div>
            </v-card-text>
          </v-card>
        </v-dialog>

        <v-dialog v-model="profileDialog" max-width="520px">
          <v-card class="modal-card">
            <v-card-title>
              <div>
                <span>Perfil</span>
                <h2>Minha conta</h2>
              </div>
              <v-spacer />
              <v-btn icon text aria-label="Fechar perfil" class="modal-close" @click="closeProfileDialog">
                <span aria-hidden="true">×</span>
              </v-btn>
            </v-card-title>
            <v-card-text>
              <div class="profile-summary">
                <strong>{{ user.name }}</strong>
                <span>{{ user.email }}</span>
                <small>{{ userRoleLabel(user) }}</small>
              </div>
              <v-form ref="profilePasswordFormRef" class="profile-password-form" @submit.prevent="changeOwnPassword">
                <v-text-field v-model="profilePasswordForm.currentPassword" label="Senha atual" type="password" outlined dense :rules="[rules.required]" />
                <v-text-field v-model="profilePasswordForm.newPassword" label="Nova senha" type="password" outlined dense :rules="[rules.required, rules.minPassword]" />
                <v-text-field v-model="profilePasswordForm.confirmation" label="Confirmar nova senha" type="password" outlined dense :rules="[rules.required, rules.minPassword]" />
                <v-alert v-if="profileError" type="error" dense outlined>{{ profileError }}</v-alert>
                <v-alert v-if="profileMessage" type="success" dense outlined>{{ profileMessage }}</v-alert>
              </v-form>
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn text @click="closeProfileDialog">Fechar</v-btn>
              <v-btn depressed color="primary" :loading="savingProfile" @click="changeOwnPassword">Alterar senha</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <v-dialog v-model="inviteDialog" max-width="680px">
          <v-card class="modal-card">
            <v-card-title><div><span>Convite público</span><h2>Gerar link por projeto</h2></div></v-card-title>
            <v-card-text>
              <v-form ref="inviteFormRef" @submit.prevent="createInvite">
                <v-select v-model="clientInviteForm.projectId" :items="projectOptions" label="Projeto vinculado" outlined dense :rules="[rules.required]" />
              </v-form>
              <v-alert v-if="generatedInviteUrl" type="success" dense outlined>
                {{ generatedInviteUrl }}
              </v-alert>
              <p class="modal-help-text">Este link não expira por data e pode ser usado por mais de um cliente. Ele deixa de funcionar somente quando for excluído.</p>
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn text @click="inviteDialog = false">Fechar</v-btn>
              <v-btn depressed color="primary" :loading="savingInvite" @click="createInvite">Gerar convite</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <v-dialog v-model="reportExportDialog" max-width="560px">
          <v-card class="modal-card">
            <v-card-title><div><span>Relatório de horas</span><h2>Escolher exportação</h2></div></v-card-title>
            <v-card-text>
              <v-form ref="reportExportFormRef" class="stack-form" @submit.prevent="exportHourMonthlyReport">
                <v-select v-model="reportExportForm.projectId" :items="projectOptions" label="Projeto" outlined dense :rules="[rules.required]" />
                <div class="two-columns">
                  <v-select v-model="reportExportForm.month" :items="reportMonthOptions" label="Mês" outlined dense :rules="[rules.required]" />
                  <v-select v-model="reportExportForm.year" :items="reportYearOptions" label="Ano" outlined dense :rules="[rules.required]" />
                </div>
              </v-form>
              <p class="modal-help-text">O arquivo será gerado somente com as tarefas do projeto e mês selecionados.</p>
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn text @click="reportExportDialog = false">Cancelar</v-btn>
              <v-btn depressed color="primary" :loading="savingReportExport" @click="exportHourMonthlyReport">Exportar</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <v-dialog v-model="ganttExportDialog" max-width="560px">
          <v-card class="modal-card">
            <v-card-title><div><span>Gantt</span><h2>Exportar demandas</h2></div></v-card-title>
            <v-card-text>
              <v-form ref="ganttExportFormRef" class="stack-form">
                <div class="two-columns">
                  <v-text-field v-model="ganttExportForm.dateFrom" label="Data inicial" type="date" outlined dense clearable />
                  <v-text-field v-model="ganttExportForm.dateTo" label="Data final" type="date" outlined dense clearable />
                </div>
                <v-select v-model="ganttExportForm.statuses" :items="statusMoveOptions" label="Status" outlined dense multiple chips clearable />
                <v-select v-model="ganttExportForm.priorities" :items="priorities" label="Prioridade" outlined dense multiple chips clearable />
              </v-form>
              <button
                type="button"
                class="gantt-export-option"
                :class="{ active: ganttExportForm.withGanttBars }"
                @click="ganttExportForm.withGanttBars = !ganttExportForm.withGanttBars"
              >
                <span class="gantt-export-option-checkbox">
                  <svg v-if="ganttExportForm.withGanttBars" width="12" height="12" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 8l3.5 3.5L13 5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </span>
                <span class="gantt-export-option-text">
                  <strong>Incluir barras visuais de Gantt</strong>
                  <small>A planilha ganha uma grade de dias com as barras coloridas por prioridade, além da tabela</small>
                </span>
              </button>
              <p class="modal-help-text">Deixe os filtros em branco para exportar todas as demandas do projeto.</p>
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn text @click="ganttExportDialog = false">Cancelar</v-btn>
              <v-btn depressed color="primary" :loading="savingGanttExport" @click="exportGanttToExcel">Exportar</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <v-dialog v-model="userProjectsDialog" max-width="640px">
          <v-card v-if="selectedUserForProjects" class="modal-card">
            <v-card-title><div><span>Projetos</span><h2>{{ selectedUserForProjects.name }}</h2></div></v-card-title>
            <v-card-text>
              <v-select
                v-model="userProjectsForm.projectIds"
                :items="projectOptionsWithAll"
                label="Projetos vinculados"
                outlined
                dense
                multiple
                small-chips
                deletable-chips
                attach
                :menu-props="{ bottom: true, offsetY: true }"
                class="project-multi-select"
                @change="handleProjectSelectAll('userProjectsForm')"
              >
                <template #item="{ item, attrs, on }">
                  <v-list-item v-bind="attrs" v-on="on" :class="item.value === '__all__' ? 'project-select-all-item' : ''" style="min-height:40px">
                    <v-list-item-action style="margin-right:12px;min-width:24px">
                      <v-simple-checkbox
                        v-if="item.value === '__all__'"
                        :value="userProjectsForm.projectIds.length === projects.length"
                        :indeterminate="userProjectsForm.projectIds.length > 0 && userProjectsForm.projectIds.length < projects.length"
                        color="cyan"
                        ripple
                      />
                      <v-simple-checkbox
                        v-else
                        :value="userProjectsForm.projectIds.includes(item.value)"
                        color="cyan"
                        ripple
                      />
                    </v-list-item-action>
                    <v-list-item-content>
                      <v-list-item-title :class="item.value === '__all__' ? 'font-weight-bold' : ''">{{ item.text }}</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </template>
                <template #selection="{ item, index }">
                  <v-chip v-if="userProjectsForm.projectIds.length === projects.length && index === 0" small label class="project-chip-all">Todos os projetos</v-chip>
                  <template v-else-if="userProjectsForm.projectIds.length < projects.length">
                    <v-chip small label close class="project-chip" @click:close="userProjectsForm.projectIds = userProjectsForm.projectIds.filter(id => id !== item.value)">{{ item.text }}</v-chip>
                  </template>
                </template>
              </v-select>
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn text @click="userProjectsDialog = false">Cancelar</v-btn>
              <v-btn depressed color="primary" :loading="savingUserProjects" @click="saveUserProjects">Salvar vinculos</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <v-dialog v-model="listDialog" max-width="560px">
          <v-card class="modal-card">
            <v-card-title><div><span>Nova lista</span><h2>Criar lista do projeto</h2></div></v-card-title>
            <v-card-text>
              <v-form ref="listFormRef" @submit.prevent="createProjectList">
                <v-select v-model="listForm.projectId" :items="projectOptions" label="Projeto / empresa" outlined dense :rules="[rules.required]" />
                <v-text-field v-model="listForm.name" label="Nome da lista" placeholder="Ex.: Em review, Suporte, Homologação" outlined dense :rules="[rules.required]" />
                <v-text-field v-model="listForm.description" label="Descrição curta" outlined dense />
                <v-text-field v-model="listForm.color" label="Cor" type="color" outlined dense />
              </v-form>
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn text @click="listDialog = false">Cancelar</v-btn>
              <v-btn depressed color="primary" :loading="savingList" @click="createProjectList">Criar lista</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <v-dialog v-model="projectDialog" max-width="620px">
          <v-card class="modal-card">
            <v-card-title><div><span>Novo projeto</span><h2>Adicionar projeto</h2></div></v-card-title>
            <v-card-text>
              <v-form ref="projectFormRef" @submit.prevent="createProject">
                <v-text-field ref="projectNameInput" v-model="projectForm.name" label="Projeto / empresa" outlined dense :rules="[rules.required]" />
                <div class="two-columns">
                  <v-select v-model="projectForm.contractType" :items="contractTypes" label="Tipo" outlined dense />
                  <v-text-field v-model="projectForm.owner" label="Responsável" outlined dense />
                </div>
                <v-textarea v-model="projectForm.notes" label="Observações" rows="4" outlined />
              </v-form>
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn text @click="projectDialog = false">Cancelar</v-btn>
              <v-btn depressed color="primary" :loading="savingProject" @click="createProject">Cadastrar projeto</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <v-dialog v-model="userDialog" max-width="760px">
          <v-card class="modal-card user-modal-card">
            <v-card-title>
              <div>
                <span>{{ editingUserId ? 'Editar usuário' : 'Novo usuário' }}</span>
                <h2>{{ editingUserId ? 'Gerenciar acesso' : 'Criar acesso' }}</h2>
              </div>
            </v-card-title>
            <v-card-text class="user-modal-body">
              <v-form ref="userFormRef" @submit.prevent="saveUser">
                <div class="user-modal-columns">
                  <div class="user-modal-left">
                    <p class="user-modal-section-label">Dados pessoais</p>
                    <v-text-field v-model="userForm.name" label="Nome" outlined dense :rules="[rules.required]" />
                    <v-text-field v-model="userForm.username" label="Username" prefix="@" outlined dense :rules="[rules.required, rules.username]" @input="normalizeUsername" />
                    <v-text-field v-model="userForm.email" label="E-mail" type="email" outlined dense :rules="[rules.required]" />
                    <v-text-field
                      v-model="userForm.password"
                      :label="editingUserId ? 'Nova senha (opcional)' : 'Senha temporaria (opcional)'"
                      type="password"
                      outlined
                      dense
                    />
                    <v-select v-model="userForm.role" :items="roleOptions" label="Perfil" outlined dense @change="normalizeUserProjectSelection" />
                    <v-switch
                      v-if="editingUserId && editingUserId !== user.id"
                      v-model="userForm.active"
                      inset
                      dense
                      hide-details
                      label="Usuário ativo"
                      class="mt-1"
                    />
                  </div>
                  <div class="user-modal-right">
                    <p class="user-modal-section-label">{{ userForm.role === 'admin' ? 'Projetos' : 'Projetos vinculados' }}</p>
                    <v-select
                      v-model="userForm.projectIds"
                      :items="projectOptionsWithAll"
                      label="Selecionar projetos"
                      outlined
                      dense
                      :multiple="userForm.role !== 'admin'"
                      :disabled="userForm.role === 'admin'"
                      :rules="userForm.role === 'dev' ? [rules.required] : []"
                      small-chips
                      deletable-chips
                      attach
                      :menu-props="{ bottom: true, offsetY: true }"
                      class="project-multi-select"
                      @change="handleProjectSelectAll('userForm')"
                    >
                      <template v-if="userForm.role !== 'admin'" #item="{ item, attrs, on }">
                        <v-list-item v-bind="attrs" v-on="on" :class="item.value === '__all__' ? 'project-select-all-item' : ''" style="min-height:40px">
                          <v-list-item-action style="margin-right:12px;min-width:24px">
                            <v-simple-checkbox
                              v-if="item.value === '__all__'"
                              :value="userForm.projectIds.length === projects.length"
                              :indeterminate="userForm.projectIds.length > 0 && userForm.projectIds.length < projects.length"
                              color="cyan"
                              ripple
                            />
                            <v-simple-checkbox
                              v-else
                              :value="userForm.projectIds.includes(item.value)"
                              color="cyan"
                              ripple
                            />
                          </v-list-item-action>
                          <v-list-item-content>
                            <v-list-item-title :class="item.value === '__all__' ? 'font-weight-bold' : ''">{{ item.text }}</v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                      </template>
                      <template v-if="userForm.role !== 'admin'" #selection="{ item, index }">
                        <v-chip v-if="userForm.projectIds.length === projects.length && index === 0" small label class="project-chip-all">Todos os projetos</v-chip>
                        <template v-else-if="userForm.projectIds.length < projects.length">
                          <v-chip small label close class="project-chip" @click:close="userForm.projectIds = userForm.projectIds.filter(id => id !== item.value)">{{ item.text }}</v-chip>
                        </template>
                      </template>
                    </v-select>
                    <div v-if="userForm.role === 'admin'" class="user-modal-admin-note">
                      Administradores tem acesso a todos os projetos automaticamente.
                    </div>
                  </div>
                </div>
              </v-form>
            </v-card-text>
            <v-card-actions class="user-modal-actions">
              <v-btn text @click="closeUserModal">Cancelar</v-btn>
              <v-btn depressed color="primary" :loading="savingUser" @click="saveUser">
                {{ editingUserId ? 'Salvar usuário' : 'Criar usuário' }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <v-dialog v-model="archiveDialog" max-width="840px" scrollable>
          <v-card class="modal-card archive-modal">
            <v-card-title>
              <div><span>Demandas</span><h2>Arquivadas</h2></div>
              <v-spacer />
              <v-btn icon text aria-label="Fechar arquivadas" class="modal-close" @click="archiveDialog = false"><span aria-hidden="true">×</span></v-btn>
            </v-card-title>
            <v-card-text>
              <div class="archive-toolbar">
                <UiInput v-model="archiveSearch" placeholder="Buscar por código, título ou projeto" aria-label="Buscar demandas arquivadas" />
                <span>{{ filteredArchivedDemands.length }} arquivadas</span>
              </div>
              <div v-if="loadingArchived" class="center-state"><v-progress-circular indeterminate color="primary" /></div>
              <div v-else-if="archiveError" class="resource-empty">{{ archiveError }}</div>
              <div v-else-if="!filteredArchivedDemands.length" class="resource-empty">Nenhuma demanda arquivada encontrada.</div>
              <div v-else class="archive-list">
                <div v-for="demand in filteredArchivedDemands" :key="demand.id" class="archive-row">
                  <div class="archive-row-content">
                    <small>{{ demand.displayId }} · {{ demand.company }}</small>
                    <strong>{{ demand.title }}</strong>
                    <span>{{ demand.status }} · Arquivada em {{ formatDateTime(demand.archivedAt) }}</span>
                  </div>
                  <div class="archive-row-actions">
                    <button type="button" class="btn-ghost" @click="openArchivedDemandDetails(demand)">Abrir</button>
                    <button type="button" class="btn-glow" :disabled="archivingDemandId === demand.id" @click="setDemandArchived(demand, false)">Restaurar</button>
                  </div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-dialog>

        <v-dialog v-model="detailDialog" max-width="1040px" scrollable>
          <v-card v-if="selectedDemand" class="modal-card detail-modal">
            <v-card-title>
              <div>
                <span>Detalhes da demanda</span>
                <h2>{{ selectedDemand.displayId }} · {{ selectedDemand.title }}</h2>
              </div>
              <v-spacer />
              <v-btn icon text aria-label="Fechar detalhes" class="modal-close" @click="closeDemandDetails">
                <span aria-hidden="true">×</span>
              </v-btn>
            </v-card-title>
            <v-card-text>
              <v-form ref="detailFormRef" class="detail-grid" @submit.prevent="saveDemandDetails">
                <section class="detail-main-column">
                  <v-text-field v-model="detailForm.title" label="Título" outlined dense hide-details="auto" :disabled="!isStaff || !!selectedDemand.archivedAt" :rules="[rules.required]" />
                  <v-textarea v-model="detailForm.description" label="Descrição" rows="4" outlined hide-details="auto" :disabled="!isStaff || !!selectedDemand.archivedAt" />

                  <div v-if="isStaff && !selectedDemand.archivedAt" class="attachment-dropzone" role="button" tabindex="0" @click="openAttachmentPicker" @keydown.enter.prevent="openAttachmentPicker" @keydown.space.prevent="openAttachmentPicker">
                    <input ref="attachmentInput" type="file" multiple class="attachment-native-input" :accept="attachmentAccept" @change="addAttachmentFiles">
                    <span class="attachment-icon">+</span>
                    <div>
                      <strong>Anexar arquivos</strong>
                      <small>Imagens, PDFs e documentos ficam salvos na demanda ao clicar em Salvar detalhes.</small>
                    </div>
                  </div>
                  <div class="attachment-list">
                    <div v-for="attachment in detailForm.attachments" :key="attachment.id" class="attachment-item">
                      <a :href="attachment.dataUrl" :download="attachment.name" class="attachment-preview" :class="{ image: isImageAttachment(attachment) }">
                        <img v-if="isImageAttachment(attachment)" :src="attachment.dataUrl" :alt="attachment.name">
                        <span v-else>{{ attachmentIcon(attachment) }}</span>
                      </a>
                      <div class="attachment-copy">
                        <a :href="attachment.dataUrl" :download="attachment.name">{{ attachment.name }}</a>
                        <small>{{ formatFileSize(attachment.size) }}</small>
                      </div>
                      <button v-if="isStaff && !selectedDemand.archivedAt" type="button" @click="removeAttachment(attachment.id)">Remover</button>
                    </div>
                    <button v-if="detailForm.attachments.length && isStaff && !selectedDemand.archivedAt" type="button" class="attachment-add-more" @click="openAttachmentPicker">
                      Adicionar mais arquivos
                    </button>
                    <div v-if="!detailForm.attachments.length" class="attachment-empty">
                      Nenhum arquivo anexado.
                    </div>
                  </div>

                  <section class="detail-notes">
                    <h3>Anotações</h3>
                    <div class="notes-list">
                      <div v-for="note in selectedDemand.notes" :key="note.id" class="note-item">
                        <small>{{ formatDateTime(note.createdAt) }}</small>
                        <span class="note-text">
                          <span v-for="(part, index) in noteParts(note.text, selectedDemand)" :key="index">
                            <mark v-if="part.mention">@{{ part.text }}</mark>
                            <button
                              v-else-if="part.request"
                              type="button"
                              class="note-request-link"
                              @click="openLinkedRequestFromDemand(selectedDemand)"
                            >
                              {{ part.text }}
                            </button>
                            <template v-else>{{ part.text }}</template>
                          </span>
                        </span>
                        <div v-if="note.mentions && note.mentions.length" class="mention-list">
                          <span v-for="mention in note.mentions" :key="mention.id || mention.email || mention.username">@{{ mention.username || mention.name }}</span>
                        </div>
                      </div>
                      <span v-if="!selectedDemand.notes.length" class="muted">Sem anotações ainda.</span>
                    </div>
                    <v-textarea
                      v-if="!selectedDemand.archivedAt"
                      v-model="noteDrafts[selectedDemand.id]"
                      rows="4"
                      outlined
                      dense
                      label="Nova anotação"
                      hint="Use @username para marcar pessoas. Ex.: @gabriel"
                      persistent-hint
                      @input="handleMentionTyping(selectedDemand.id)"
                      @keydown.esc="closeMentionPopover"
                    />
                    <div v-if="!selectedDemand.archivedAt && mentionPopover.open && mentionPopover.demandId === selectedDemand.id" class="mention-popover">
                      <button
                        v-for="person in filteredMentionUsers"
                        :key="person.id"
                        type="button"
                        @click="selectMention(selectedDemand.id, person.username)"
                      >
                        <strong>@{{ person.username }}</strong>
                        <span>{{ person.name }}</span>
                      </button>
                      <span v-if="!filteredMentionUsers.length">Nenhum usuário encontrado.</span>
                    </div>
                    <v-btn v-if="!selectedDemand.archivedAt" small depressed color="primary" @click="addNote(selectedDemand)">Salvar anotação</v-btn>
                  </section>
                </section>

                <aside class="detail-side-column">
                  <section class="detail-side-panel">
                    <h3>Dados da demanda</h3>
                    <v-select
                      v-model="detailForm.projectId"
                      :items="projectOptions"
                      label="Projeto / empresa"
                      outlined
                      dense
                      :disabled="!isStaff || !!selectedDemand.archivedAt"
                      :rules="[rules.required]"
                      @change="applyDetailProjectDefaults"
                    />
                    <v-select v-model="detailForm.status" :items="statusMoveOptions" label="Status" outlined dense :disabled="!isStaff || !!selectedDemand.archivedAt" />
                    <v-select v-model="detailForm.priority" :items="priorities" label="Prioridade" outlined dense :disabled="!isStaff || !!selectedDemand.archivedAt" />
                    <v-select
                      v-if="isStaff"
                      v-model="detailForm.ownerUserId"
                      :items="devOptionsForProject(detailForm.projectId)"
                      label="Responsável"
                      outlined
                      dense
                      clearable
                      :disabled="!isStaff || !!selectedDemand.archivedAt"
                      no-data-text="Nenhum dev vinculado ao projeto"
                    />
                    <v-select
                      v-if="isStaff"
                      v-model="detailForm.requesterUserId"
                      :items="clientOptionsForProject(detailForm.projectId)"
                      label="Solicitante"
                      outlined
                      dense
                      clearable
                      :disabled="!!selectedDemand.archivedAt"
                      no-data-text="Nenhum cliente vinculado ao projeto"
                      hint="Recebe um e-mail quando a demanda for concluída"
                      persistent-hint
                    />
                    <v-text-field v-model="detailForm.dueDate" label="Prazo de entrega" type="date" outlined dense :disabled="!isStaff || !!selectedDemand.archivedAt" />
                    <v-text-field v-if="isStaff" v-model="detailForm.totalHours" label="Total de horas" type="number" min="0" step="0.25" outlined dense :disabled="!!selectedDemand.archivedAt" />
                  </section>

                  <section v-if="isStaff && !selectedDemand.archivedAt" class="detail-side-panel link-existing-demand demand-request-link">
                    <h3>Solicitação vinculada</h3>
                    <v-select
                      v-model="detailForm.linkRequestId"
                      :items="demandRequestLinkOptions"
                      label="Selecionar solicitação"
                      outlined
                      dense
                      hide-details
                      clearable
                      no-data-text="Nenhuma solicitação disponível neste projeto"
                    />
                    <v-btn
                      outlined
                      color="primary"
                      block
                      :disabled="!detailForm.linkRequestId"
                      :loading="savingDetail"
                      @click="linkExistingRequestToDemand"
                    >
                      Vincular
                    </v-btn>
                  </section>

                  <section v-if="selectedDemand.requestId" class="source-attachments-panel">
                    <div class="source-attachments-header">
                      <div>
                        <span>Solicitação origem</span>
                        <strong>{{ demandSourceRequest ? demandSourceRequest.displayId : 'Carregando' }}</strong>
                      </div>
                      <button type="button" @click="openLinkedRequestFromDemand(selectedDemand)">Abrir</button>
                    </div>
                    <span v-if="loadingDemandSourceAttachments" class="muted">Carregando anexos...</span>
                    <div v-else-if="demandSourceAttachments.length" class="source-attachment-list">
                      <a
                        v-for="attachment in demandSourceAttachments"
                        :key="attachment.sourceId"
                        :href="attachment.dataUrl"
                        :download="attachment.name"
                        class="source-attachment-item"
                      >
                        <span>{{ attachmentIcon(attachment) }}</span>
                        <div>
                          <strong>{{ attachment.name }}</strong>
                          <small>{{ attachment.authorName }} · {{ formatDateTime(attachment.createdAt) }} · {{ formatFileSize(attachment.size) }}</small>
                        </div>
                      </a>
                    </div>
                    <span v-else class="muted">Nenhum anexo na solicitação.</span>
                  </section>
                </aside>
              </v-form>
            </v-card-text>
            <v-card-actions>
              <v-btn v-if="isStaff && !selectedDemand.archivedAt" text color="error" @click="deleteDemand(selectedDemand)">Excluir</v-btn>
              <v-btn v-if="isStaff && !selectedDemand.archivedAt" text @click="setDemandArchived(selectedDemand, true)">Arquivar</v-btn>
              <v-btn v-if="isStaff && selectedDemand.archivedAt" text @click="setDemandArchived(selectedDemand, false)">Restaurar</v-btn>
              <v-spacer />
              <v-btn text @click="closeDemandDetails">Fechar</v-btn>
              <v-btn v-if="isStaff && !selectedDemand.archivedAt" depressed color="primary" :loading="savingDetail" @click="saveDemandDetails">Salvar detalhes</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <v-dialog v-model="demandDialog" max-width="760px">
          <v-card class="modal-card">
            <v-card-title>
              <div>
                <span>{{ form.requestId ? 'Solicitação vinculada' : 'Nova demanda' }}</span>
                <h2>{{ form.requestId ? 'Transformar em demanda' : 'Adicionar demanda' }}</h2>
              </div>
            </v-card-title>
            <v-card-text>
              <div v-if="form.requestId" class="request-conversion-callout">
                <strong>{{ selectedRequest ? selectedRequest.displayId : 'Solicitação' }}</strong>
                <span>A demanda será criada já vinculada ao ticket de origem.</span>
              </div>
              <v-form ref="form" @submit.prevent="createDemand">
                <v-select v-model="form.projectId" :items="projectOptions" label="Projeto / empresa" outlined dense :rules="[rules.required]" :readonly="demandFormProjectLocked" :class="{ 'v-select-locked': demandFormProjectLocked }" @change="applyProjectDefaults" />
                <v-text-field v-model="form.title" label="Título" placeholder="Ex.: Ajustar fluxo de contratos" outlined dense :rules="[rules.required]" />
                <div class="two-columns">
                  <v-select v-model="form.priority" :items="priorities" label="Prioridade" outlined dense />
                  <v-select
                    v-model="form.ownerUserId"
                    :items="devOptionsForProject(form.projectId)"
                    label="Responsável"
                    outlined
                    dense
                    clearable
                    no-data-text="Nenhum dev vinculado ao projeto"
                  />
                </div>
                <v-select
                  v-model="form.requesterUserId"
                  :items="clientOptionsForProject(form.projectId)"
                  label="Solicitante"
                  outlined
                  dense
                  clearable
                  no-data-text="Nenhum cliente vinculado ao projeto"
                  hint="Recebe um e-mail quando a demanda for concluída"
                  persistent-hint
                />
                <v-text-field v-model="form.dueDate" label="Prazo" type="date" outlined dense />
                <v-textarea v-model="form.note" label="Anotação inicial" rows="4" outlined />
              </v-form>
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn text @click="demandDialog = false">Cancelar</v-btn>
              <v-btn depressed color="primary" :loading="saving" @click="createDemand">Adicionar demanda</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <v-dialog v-model="bulkDemandDialog" max-width="980px" scrollable>
          <v-card class="modal-card">
            <v-card-title><div><span>Criação em massa</span><h2>Adicionar várias tarefas</h2></div></v-card-title>
            <v-card-text>
              <v-form ref="bulkDemandFormRef" class="stack-form" @submit.prevent="createBulkDemands">
                <div class="two-columns">
                  <v-select v-model="bulkDemandForm.projectId" :items="projectOptions" label="Projeto" outlined dense :rules="[rules.required]" @change="resetBulkDemandSuggestions" />
                  <v-select v-model="bulkDemandForm.defaultStatus" :items="statusMoveOptions" label="Status inicial" outlined dense />
                </div>
                <div class="two-columns">
                  <v-select v-model="bulkDemandForm.defaultPriority" :items="priorities" label="Prioridade inicial" outlined dense />
                  <v-select
                    v-model="bulkDemandForm.defaultOwnerUserId"
                    :items="devOptionsForProject(bulkDemandForm.projectId)"
                    label="Responsável inicial"
                    outlined
                    dense
                    clearable
                    no-data-text="Nenhum dev vinculado ao projeto"
                  />
                </div>
                <v-textarea
                  v-model="bulkDemandForm.rawText"
                  label="Texto com tarefas"
                  rows="7"
                  outlined
                  placeholder="Cole uma tarefa por linha. Ex.:&#10;- Ajustar tela de login&#10;- Corrigir validação do cadastro&#10;1. Publicar relatório mensal"
                />
              </v-form>

              <div class="bulk-demand-actions">
                <v-btn outlined color="primary" :disabled="!bulkDemandForm.rawText.trim()" @click="suggestBulkDemands">Sugerir lista</v-btn>
                <span>{{ bulkDemandForm.items.length }} {{ bulkDemandForm.items.length === 1 ? 'tarefa sugerida' : 'tarefas sugeridas' }}</span>
              </div>

              <div v-if="bulkDemandForm.items.length" class="bulk-demand-review">
                <div class="bulk-demand-review-head">
                  <strong>Revisar antes de criar</strong>
                  <button type="button" @click="clearBulkDemandSuggestions">Limpar lista</button>
                </div>
                <div
                  v-for="(item, index) in bulkDemandForm.items"
                  :key="item.key"
                  class="bulk-demand-row"
                >
                  <span class="bulk-demand-index">{{ index + 1 }}</span>
                  <v-text-field v-model="item.title" label="Título" outlined dense hide-details />
                  <v-select v-model="item.status" :items="statusMoveOptions" label="Status" outlined dense hide-details />
                  <v-select v-model="item.priority" :items="priorities" label="Prioridade" outlined dense hide-details />
                  <button type="button" class="icon-remove-button" aria-label="Remover tarefa" @click="removeBulkDemand(index)">×</button>
                </div>
              </div>
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn text @click="bulkDemandDialog = false">Cancelar</v-btn>
              <v-btn depressed color="primary" :loading="savingBulkDemands" :disabled="!bulkDemandForm.items.length" @click="createBulkDemands">Criar tarefas</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <v-dialog v-model="confirmDeleteDemandDialog" max-width="420px">
          <v-card class="modal-card">
            <v-card-title><div><span>Confirmar exclusão</span><h2>Excluir demanda</h2></div></v-card-title>
            <v-card-text>
              <p>Tem certeza que deseja excluir a demanda <strong>{{ demandToDelete && demandToDelete.title }}</strong>? Esta ação não pode ser desfeita.</p>
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn text @click="confirmDeleteDemandDialog = false">Cancelar</v-btn>
              <button type="button" class="btn-delete-confirm" :disabled="deletingDemand" @click="confirmDeleteDemand">{{ deletingDemand ? 'Excluindo...' : 'Excluir' }}</button>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <v-dialog v-model="bulkDraftDialog" max-width="760px" scrollable>
          <v-card class="modal-card">
            <v-card-title><div><span>Rascunhos</span><h2>Criar rascunhos em massa</h2></div></v-card-title>
            <v-card-text>
              <div class="stack-form">
                <v-select v-model="bulkDraftForm.projectId" :items="projectOptions" label="Projeto / empresa" outlined dense :rules="[rules.required]" />
                <v-select v-model="bulkDraftForm.defaultPriority" :items="priorities" label="Prioridade padrão" outlined dense />
                <v-textarea
                  v-model="bulkDraftForm.rawText"
                  label="Títulos das demandas (um por linha)"
                  placeholder="Implementar login&#10;Criar dashboard&#10;Revisar API"
                  rows="6"
                  outlined
                  hide-details
                />
                <v-btn outlined color="primary" :disabled="!bulkDraftForm.rawText.trim()" @click="suggestBulkDrafts">Sugerir lista</v-btn>
                <div v-if="bulkDraftForm.items.length" class="bulk-demand-review">
                  <div class="bulk-demand-review-head">
                    <span>{{ bulkDraftForm.items.length }} {{ bulkDraftForm.items.length === 1 ? 'rascunho' : 'rascunhos' }}</span>
                    <button type="button" class="link-button" @click="bulkDraftForm.items = []">Limpar</button>
                  </div>
                  <div v-for="(item, index) in bulkDraftForm.items" :key="item.key" class="bulk-demand-row">
                    <v-text-field v-model="item.title" outlined dense hide-details />
                    <v-select v-model="item.priority" :items="priorities" outlined dense hide-details />
                    <button type="button" class="bulk-demand-remove" @click="bulkDraftForm.items.splice(index, 1)">×</button>
                  </div>
                </div>
              </div>
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn text @click="bulkDraftDialog = false">Cancelar</v-btn>
              <v-btn depressed color="primary" :loading="savingBulkDrafts" :disabled="!bulkDraftForm.items.length" @click="createBulkDrafts">Salvar rascunhos</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

      </template>
    </v-main>
  </v-app>
</template>

<script>
import api from './services/api'
import demandeskMarkUrl from './assets/demandesk-mark.svg'
import reportLogoUrl from './assets/logo-mazzitech-light.svg'
import realtime from './services/socket'
import UiBadge from './components/ui/UiBadge.vue'
import UiButton from './components/ui/UiButton.vue'
import UiCard from './components/ui/UiCard.vue'
import UiInput from './components/ui/UiInput.vue'
import UiSelect from './components/ui/UiSelect.vue'
import ScheduleWorkspace from './components/ScheduleWorkspace.vue'
import WhatsappGroupsPanel from './components/WhatsappGroupsPanel.vue'

export default {
  name: 'App',
  components: {
    UiBadge,
    UiButton,
    UiCard,
    UiInput,
    UiSelect,
    ScheduleWorkspace,
    WhatsappGroupsPanel
  },
  data: () => ({
    booting: true,
    loggingIn: false,
    loading: false,
    saving: false,
    savingDetail: false,
    confirmDeleteDemandDialog: false,
    demandToDelete: null,
    deletingDemand: false,
    savingList: false,
    savingProject: false,
    savingUser: false,
    savingRequest: false,
    savingInvite: false,
    savingUserProjects: false,
    savingProfile: false,
    savingReportExport: false,
    savingBulkDemands: false,
    savingBulkDrafts: false,
    savingInlineDemand: false,
    loadingDrafts: false,
    movingColumnStatus: '',
    demandDrafts: [],
    bulkDraftDialog: false,
    bulkDraftForm: { projectId: '', defaultPriority: 2, rawText: '', items: [] },
    sendingRequestMessage: false,
    loadingReports: false,
    demandDialog: false,
    bulkDemandDialog: false,
    detailDialog: false,
    archiveDialog: false,
    archivedDemands: [],
    archiveSearch: '',
    archiveError: '',
    loadingArchived: false,
    archivingDemandId: '',
    requestDialog: false,
    requestDetailDialog: false,
    listDialog: false,
    projectDialog: false,
    userDialog: false,
    inviteDialog: false,
    reportExportDialog: false,
    userProjectsDialog: false,
    profileDialog: false,
    sidebarOpen: false,
    sidebarViewportWasDesktop: null,
    userMenuOpen: false,
    clientProjectMenuOpen: false,
    filtersMenuOpen: false,
    kanbanInsightsOpen: false,
    demandsMenuOpen: false,
    activeView: 'requests',
    error: '',
    loginError: '',
    authMessage: '',
    authMode: 'login',
    resetToken: '',
    inviteToken: '',
    searchTimer: null,
    requestSearchTimer: null,
    clientProjectContextId: 'all',
    draggingDemandId: null,
    draggingColumnStatus: '',
    columnDropTargetStatus: '',
    columnDropPlacement: '',
    savingColumnOrder: false,
    selectedDemand: null,
    selectedRequest: null,
    demandSourceRequest: null,
    demandSourceAttachments: [],
    loadingDemandSourceAttachments: false,
    selectedUserForProjects: null,
    editingUserId: '',
    user: null,
    demands: [],
    summaryDemands: [],
    requests: [],
    requestMessages: [],
    requestMessageAttachments: [],
    demandLinkRequests: [],
    clientInvites: [],
    projects: [],
    users: [],
    projectClientsCache: {},
    userDirectoryTab: 'clients',
    demandeskMarkUrl,
    reportRows: [],
    reportYear: new Date().getFullYear(),
    reportProjectId: 'all',
    reportExportForm: { projectId: '', month: new Date().getMonth() + 1, year: new Date().getFullYear() },
    ganttDemands: [],
    loadingGantt: false,
    scheduleFullscreen: false,
    ganttExportDialog: false,
    savingGanttExport: false,
    ganttExportForm: { dateFrom: '', dateTo: '', statuses: [], priorities: [], withGanttBars: false },
    demandLists: [],
    noteDrafts: {},
    mentionPopover: { open: false, demandId: '', query: '', startIndex: -1 },
    summary: { total: 0, pending: 0, done: 0, highPriority: 0 },
    statuses: ['Backlog', 'Pendente', 'Em andamento', 'Concluida'],
    requestStatuses: [
      { text: 'Aberta', value: 'Aberta' },
      { text: 'Em análise', value: 'Em analise' },
      { text: 'Em desenvolvimento', value: 'Em desenvolvimento' },
      { text: 'Aguardando cliente', value: 'Aguardando cliente' },
      { text: 'Concluída', value: 'Concluida' },
      { text: 'Cancelada', value: 'Cancelada' }
    ],
    priorities: [
      { text: 'Alta', value: 1 },
      { text: 'Media', value: 2 },
      { text: 'Baixa', value: 3 }
    ],
    contractTypes: ['Demandesk', 'PJ', 'Dev contratado', 'Cliente direto'],
    roleOptions: [
      { text: 'Administrador', value: 'admin' },
      { text: 'Cliente', value: 'client' },
      { text: 'Dev Demandesk', value: 'dev' }
    ],
    boardProjectId: 'all',
    filters: { search: '', status: 'all', priority: 'all', dateFrom: '', dateTo: '' },
    requestActionBusy: false,
    requestFilters: { archive: 'active', search: '', projectId: 'all', status: 'all', priority: 'all' },
    loginForm: { email: '', password: '' },
    forgotForm: { email: '' },
    resetForm: { password: '', confirmation: '' },
    profilePasswordForm: { currentPassword: '', newPassword: '', confirmation: '' },
    profileError: '',
    profileMessage: '',
    inviteInfo: null,
    inviteForm: { name: '', username: '', email: '', password: '', confirmation: '' },
    form: { projectId: '', requestId: '', title: '', company: '', contractType: 'Demandesk', priority: 2, status: 'Pendente', owner: '', ownerUserId: '', requesterUserId: '', dueDate: '', note: '' },
    demandFormProjectLocked: false,
    inlineDemandComposer: { status: '', title: '', totalHours: '', dueDate: '' },
    bulkDemandForm: { projectId: '', defaultStatus: 'Pendente', defaultPriority: 2, defaultOwnerUserId: '', rawText: '', items: [] },
    detailForm: { id: '', projectId: '', title: '', company: '', contractType: 'Demandesk', priority: 2, status: 'Pendente', owner: '', ownerUserId: '', requesterUserId: '', dueDate: '', description: '', totalHours: '', attachments: [], linkRequestId: '' },
    requestForm: { projectId: '', title: '', priority: 2, description: '' },
    requestDetailForm: { id: '', title: '', description: '', status: 'Aberta', priority: 2, hours: 0, linkDemandId: '' },
    requestMessageDraft: '',
    clientInviteForm: { projectId: '' },
    generatedInviteUrl: '',
    userProjectsForm: { projectIds: [] },
    listForm: { projectId: '', name: '', description: '', color: '#00f0ff' },
    projectForm: { name: '', contractType: 'Demandesk', owner: '', notes: '' },
    userForm: { name: '', username: '', email: '', password: '', role: 'client', projectIds: [], active: true },
    rules: {
      required: (value) => !!String(value || '').trim() || 'Campo obrigatório',
      minPassword: (value) => String(value || '').length >= 8 || 'Use pelo menos 8 caracteres',
      username: (value) => /^[a-z0-9._-]{3,80}$/.test(String(value || '').trim()) || 'Use letras minúsculas, números, ponto, hífen ou underline'
    },
    attachmentAccept: 'image/jpeg,image/png,image/gif,image/webp,application/pdf,text/plain,text/csv,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  }),
  computed: {
    activeDemandFilterCount () {
      let count = 0
      if (this.filters.status && this.filters.status !== 'all') count += 1
      if (this.filters.priority && this.filters.priority !== 'all') count += 1
      if (this.filters.dateFrom) count += 1
      if (this.filters.dateTo) count += 1
      return count
    },
    activeRequestFilterCount () {
      let count = 0
      if (this.requestFilters.projectId && this.requestFilters.projectId !== 'all') count += 1
      if (this.requestFilters.status && this.requestFilters.status !== 'all') count += 1
      if (this.requestFilters.priority && this.requestFilters.priority !== 'all') count += 1
      if (this.requestFilters.archive === 'archived') count += 1
      return count
    },
    userInitials () {
      const name = (this.user && this.user.name) || ''
      const parts = name.trim().split(/\s+/).filter(Boolean)
      if (!parts.length) return ''
      const initials = parts.length === 1 ? parts[0].slice(0, 2) : parts[0][0] + parts[parts.length - 1][0]
      return initials.toUpperCase()
    },
    isAdmin () {
      return this.user && this.user.role === 'admin'
    },
    isClient () {
      return this.user && this.user.role === 'client'
    },
    isStaff () {
      return this.user && ['admin', 'dev'].includes(this.user.role)
    },
    authCopy () {
      const copy = {
        reset: {
          eyebrow: 'Nova senha',
          title: 'Defina sua nova senha',
          description: 'Informe uma nova senha para voltar ao painel.'
        },
        forgot: {
          eyebrow: 'Recuperar acesso',
          title: 'Recuperar senha',
          description: 'Digite seu e-mail para receber o link de redefinição.'
        },
        invite: {
          eyebrow: 'Convite de cliente',
          title: 'Crie seu acesso',
          description: 'Complete seu cadastro para acessar projetos, solicitações e status das demandas vinculadas.'
        },
        login: {
          eyebrow: 'Acesso restrito',
          title: 'Demandesk',
          description: 'Entre para administrar demandas, projetos e usuários ou acompanhar o projeto vinculado ao seu acesso.'
        }
      }
      return copy[this.authMode] || copy.login
    },
    navigationItems () {
      return this.navigationSections.reduce((items, section) => [...items, ...section.items], [])
    },
    demandMenuProjects () {
      return this.projects
    },
    reportYearOptions () {
      const current = new Date().getFullYear()
      return Array.from({ length: 6 }, (_, index) => {
        const year = current - index
        return { text: String(year), value: year }
      })
    },
    reportProjectOptions () {
      return [
        { text: 'Todos os projetos', value: 'all' },
        ...this.projects.map((project) => ({ text: project.name, value: project.id }))
      ]
    },
    reportMonths () {
      const labels = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']
      return labels.map((label, index) => ({
        key: `${this.reportYear}-${String(index + 1).padStart(2, '0')}`,
        short: label
      }))
    },
    reportMonthOptions () {
      return Array.from({ length: 12 }, (_, index) => ({
        text: this.fullMonthName(index),
        value: index + 1
      }))
    },
    reportProjectRows () {
      const rowsByProject = new Map()
      this.reportRows.forEach((row) => {
        if (!rowsByProject.has(row.projectId)) {
          rowsByProject.set(row.projectId, {
            projectId: row.projectId,
            projectName: row.projectName,
            months: this.reportMonths.map((month) => ({ ...month, tasks: 0, hours: 0 })),
            totalTasks: 0,
            totalHours: 0
          })
        }

        const project = rowsByProject.get(row.projectId)
        const month = project.months.find((item) => item.key === row.month)
        if (month) {
          month.tasks = Number(row.tasks || 0)
          month.hours = Number(row.hours || 0)
        }
      })

      return Array.from(rowsByProject.values())
        .map((project) => ({
          ...project,
          totalTasks: project.months.reduce((total, month) => total + month.tasks, 0),
          totalHours: project.months.reduce((total, month) => total + month.hours, 0)
        }))
        .sort((a, b) => b.totalTasks - a.totalTasks || a.projectName.localeCompare(b.projectName))
    },
    reportTotals () {
      return {
        tasks: this.reportProjectRows.reduce((total, project) => total + project.totalTasks, 0),
        hours: this.reportProjectRows.reduce((total, project) => total + project.totalHours, 0),
        activeProjects: this.reportProjectRows.filter((project) => project.totalTasks > 0).length
      }
    },
    navigationSections () {
      if (!this.user) return []

      if (this.isClient) {
        return [{
          title: 'Portal do cliente',
          items: [
            { text: 'Dashboard', value: 'dashboard', short: 'DB', icon: 'dashboard', description: 'Resumo do projeto' },
            { text: 'Solicitações', value: 'requests', short: 'SO', icon: 'requests', description: 'Tickets e conversas' },
            { text: 'Demandas', value: 'demands', short: 'DM', icon: 'demands', description: 'Kanban operacional' },
            { text: 'Projetos', value: 'projects', short: 'PR', icon: 'projects', description: 'Contextos vinculados' }
          ]
        }]
      }

      const sections = [
        {
          title: 'Equipe Demandesk',
          items: [
            { text: 'Solicitações', value: 'requests', short: 'SO', icon: 'requests', description: 'Entrada e suporte' },
            { text: 'Demandas', value: 'demands', short: 'DM', icon: 'demands', description: 'Execução por projeto' }
          ]
        }
      ]

      sections.push({
        title: this.isAdmin ? 'Administração' : 'Contexto',
        items: [
          ...(this.isAdmin ? [{ text: 'Relatórios', value: 'reports', short: 'RL', icon: 'reports', description: 'Indicadores mensais' }] : []),
          { text: 'Projetos', value: 'projects', short: 'PR', icon: 'projects', description: 'Clientes e contratos' },
          ...(this.isAdmin ? [{ text: 'Usuários', value: 'users', short: 'US', icon: 'users', description: 'Acessos e permissões' }] : [])
        ]
      })

      return sections
    },
    sidebarPulse () {
      return {
        title: this.selectedBoardProjectId ? this.selectedBoardProjectName : (this.isClient ? this.clientProjectName : 'Operação ativa'),
        pending: this.dashboardSummary.pending || this.demandBoardInsights.pending || 0,
        projects: this.projects.length,
        drafts: this.demandDrafts.length
      }
    },
    clientUsers () {
      return this.users.filter((item) => item.role === 'client')
    },
    staffUsers () {
      return this.users.filter((item) => ['admin', 'dev'].includes(item.role))
    },
    filteredUsers () {
      return this.userDirectoryTab === 'clients' ? this.clientUsers : this.staffUsers
    },
    userDirectoryTabs () {
      return [
        { text: 'Clientes', value: 'clients', count: this.clientUsers.length },
        { text: 'Equipe', value: 'staff', count: this.staffUsers.length }
      ]
    },
    clientProjectContextOptions () {
      return [
        { text: 'Todos os projetos', value: 'all' },
        ...this.projects.map((project) => ({ text: project.name, value: project.id }))
      ]
    },
    selectedClientProjectLabel () {
      const option = this.clientProjectContextOptions.find((item) => item.value === this.clientProjectContextId)
      return (option && option.text) || 'Todos os projetos'
    },
    selectedClientProjectId () {
      return this.isClient && this.clientProjectContextId !== 'all' ? this.clientProjectContextId : ''
    },
    visibleProjects () {
      if (!this.selectedClientProjectId) return this.projects
      return this.projects.filter((project) => project.id === this.selectedClientProjectId)
    },
    dashboardSummary () {
      if (!this.selectedBoardProjectId && !this.selectedClientProjectId && !this.isClient) return this.summary
      const scopedDemands = this.summaryDemands.length ? this.summaryDemands : this.demands
      return {
        total: scopedDemands.length,
        pending: scopedDemands.filter((demand) => !this.isDemandCompleted(demand.status)).length,
        done: scopedDemands.filter((demand) => this.isDemandCompleted(demand.status)).length,
        highPriority: scopedDemands.filter((demand) => this.priorityValue(demand.priority) === 1 && !this.isDemandCompleted(demand.status)).length
      }
    },
    demandBoardInsights () {
      const active = this.filteredDemands.filter((demand) => !this.isDemandCompleted(demand.status))
      const overdue = active.filter((demand) => {
        if (!demand.dueDate) return false
        const dueDate = new Date(`${demand.dueDate}T23:59:59`)
        return dueDate < new Date()
      })
      return {
        total: this.filteredDemands.length,
        active: active.length,
        pending: active.length,
        done: this.filteredDemands.filter((demand) => this.isDemandCompleted(demand.status)).length,
        highPriority: active.filter((demand) => this.priorityValue(demand.priority) === 1).length,
        overdue: overdue.length,
        hours: this.filteredDemands.reduce((total, demand) => total + Number(demand.totalHours || 0), 0)
      }
    },
    demandPriorityBreakdown () {
      const total = Math.max(this.filteredDemands.length, 1)
      return this.priorities.map((priority) => {
        const count = this.filteredDemands.filter((demand) => this.priorityValue(demand.priority) === priority.value).length
        return {
          label: priority.text,
          count,
          width: `${Math.max(6, Math.round((count / total) * 100))}%`
        }
      })
    },
    demandNextFocus () {
      const now = new Date()
      return [...this.filteredDemands]
        .filter((demand) => !this.isDemandCompleted(demand.status))
        .sort((a, b) => {
          const priorityDelta = this.priorityValue(a.priority) - this.priorityValue(b.priority)
          if (priorityDelta) return priorityDelta
          const aTime = a.dueDate ? new Date(`${a.dueDate}T23:59:59`).getTime() : Number.MAX_SAFE_INTEGER
          const bTime = b.dueDate ? new Date(`${b.dueDate}T23:59:59`).getTime() : Number.MAX_SAFE_INTEGER
          const aOverdue = aTime < now.getTime() ? -1 : 0
          const bOverdue = bTime < now.getTime() ? -1 : 0
          return aOverdue - bOverdue || aTime - bTime || this.demandSortOrder(a) - this.demandSortOrder(b)
        })
        .slice(0, 5)
    },
    filteredDemands () {
      return this.demands.filter((demand) => this.demandMatchesDateFilter(demand, this.filters))
    },
    filteredArchivedDemands () {
      const query = String(this.archiveSearch || '').trim().toLowerCase()
      return this.archivedDemands.filter((demand) =>
        !query || [demand.displayId, demand.title, demand.company].some((value) => String(value || '').toLowerCase().includes(query))
      )
    },
    filteredGanttDemands () {
      return this.ganttDemands.filter((demand) => this.demandRangeMatchesDateFilter(demand, this.filters))
    },
    ganttTasks () {
      return this.filteredGanttDemands.map((demand) => {
        const { start, end } = this.resolveGanttRange(demand)
        const completed = this.isDemandCompleted(demand.status)
        const priorityKey = { 1: 'alta', 2: 'media', 3: 'baixa' }[this.priorityValue(demand.priority)]
        return {
          id: String(demand.id),
          name: demand.displayId || demand.title,
          start,
          end,
          progress: completed ? 100 : 0,
          dependencies: '',
          custom_class: `gantt-bar-priority-${priorityKey}`,
          displayId: demand.displayId,
          demand,
          title: demand.title,
          company: demand.company,
          status: demand.status,
          priorityLabel: this.priorityLabel(demand.priority),
          priorityKey,
          owner: demand.owner || 'Sem responsável',
          startLabel: this.formatDate(start),
          endLabel: this.formatDate(end),
          durationDays: Math.max(1, Math.round((new Date(`${end}T00:00:00`) - new Date(`${start}T00:00:00`)) / 86400000)),
          hours: Number(demand.totalHours || 0),
          hoursLabel: demand.totalHours ? `${demand.totalHours}h` : '—'
        }
      })
    },
    filteredGanttExportDemands () {
      const form = this.ganttExportForm
      return this.ganttDemands.filter((demand) => {
        const { start, end } = this.resolveGanttRange(demand)
        if (form.dateFrom && end < form.dateFrom) return false
        if (form.dateTo && start > form.dateTo) return false
        if (form.statuses.length && !form.statuses.includes(demand.status)) return false
        if (form.priorities.length && !form.priorities.includes(this.priorityValue(demand.priority))) return false
        return true
      })
    },
    openRequestsCount () {
      return this.requests.filter((request) => !['Concluida', 'Cancelada'].includes(request.status)).length
    },
    recentRequests () {
      return [...this.requests]
        .sort((a, b) => new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt))
        .slice(0, 5)
    },
    developmentDemands () {
      return this.demands
        .filter((demand) => {
          const status = String(demand.status || '').toLowerCase()
          return status.includes('andamento') || status.includes('desenvolvimento')
        })
        .slice(0, 5)
    },
    clientProjectName () {
      if (this.selectedClientProjectId) {
        const project = this.projects.find((item) => item.id === this.selectedClientProjectId)
        return (project && project.name) || 'Projeto selecionado'
      }
      if (this.isClient && this.projects.length > 1) return 'Todos os projetos'
      return (this.projects[0] && this.projects[0].name) || this.user.projectName || 'Projeto'
    },
    selectedBoardProjectName () {
      if (!this.selectedBoardProjectId) return ''
      const project = this.projects.find((item) => item.id === this.selectedBoardProjectId)
      return (project && project.name) || 'Projeto selecionado'
    },
    scheduleOwnerOptions () {
      return this.users
        .filter((user) => user.role === 'dev' && this.devUserBelongsToProject(user.id, this.selectedBoardProjectId))
        .map((user) => ({ text: user.name, value: user.id }))
    },
    selectedBoardProject () {
      if (!this.selectedBoardProjectId) return null
      return this.projects.find((item) => item.id === this.selectedBoardProjectId) || null
    },
    demandProjectScopeLabel () {
      if (this.isClient) return this.clientProjectName
      return this.selectedBoardProjectName || 'Todos os projetos'
    },
    demandHeroKicker () {
      if (this.isClient) return this.selectedClientProjectId ? 'Portal do cliente' : 'Portal do cliente · consolidado'
      return this.selectedBoardProjectId ? 'Pipeline do projeto' : 'Pipeline geral'
    },
    demandHeroTitle () {
      if (this.isClient) return this.clientProjectName || 'Projeto vinculado'
      return this.selectedBoardProjectId ? this.selectedBoardProjectName : 'Todas as demandas'
    },
    demandHeroDescription () {
      if (this.isClient && this.selectedClientProjectId) {
        return `Acompanhe entregas, prazos e proximos passos do projeto ${this.clientProjectName}.`
      }
      if (this.isClient) {
        return 'Acompanhe demandas, prazos e status de todos os projetos vinculados ao seu acesso.'
      }
      if (this.selectedBoardProjectId) {
        return `Prioridades, prazos, responsáveis e etapas do projeto ${this.selectedBoardProjectName}.`
      }
      return 'Visão consolidada das demandas de todos os projetos vinculados ao seu acesso.'
    },
    demandBoardKicker () {
      if (this.isClient) return this.selectedClientProjectId ? 'Projeto vinculado' : 'Todos os projetos vinculados'
      return this.selectedBoardProjectId ? 'Projeto selecionado' : 'Todos os projetos'
    },
    demandBoardTitle () {
      if (this.isClient) return this.clientProjectName
      return this.selectedBoardProjectId ? this.selectedBoardProjectName : 'Kanban consolidado'
    },
    demandBoardDescription () {
      if (this.selectedBoardProjectId || this.selectedClientProjectId) {
        return 'As colunas usam as etapas configuradas para este projeto.'
      }
      return 'As colunas usam etapas padrão para consolidar demandas de projetos diferentes.'
    },
    statusFilterOptions () {
      return [
        { text: 'Todas', value: 'all' },
        ...this.statusMoveOptions.map((status) => ({ text: status, value: status }))
      ]
    },
    priorityFilterOptions () {
      return [{ text: 'Todas', value: 'all' }, ...this.priorities]
    },
    requestStatusOptions () {
      return this.requestStatuses
    },
    requestStatusFilterOptions () {
      return [{ text: 'Todos', value: 'all' }, ...this.requestStatuses]
    },
    projectOptions () {
      return this.projects.map((project) => ({ text: project.name, value: project.id }))
    },
    projectOptionsWithAll () {
      return [{ text: 'Todos os projetos', value: '__all__' }, ...this.projectOptions]
    },
    requestDemandLinkOptions () {
      if (!this.selectedRequest) return []
      const linkedIds = new Set((this.selectedRequest.demands || []).map((demand) => demand.id))
      return this.summaryDemands
        .filter((demand) => demand.projectId === this.selectedRequest.projectId)
        .filter((demand) => !linkedIds.has(demand.id))
        .map((demand) => ({
          text: `${demand.displayId} · ${demand.title} · ${demand.status}`,
          value: demand.id
        }))
    },
    demandRequestLinkOptions () {
      if (!this.selectedDemand) return []
      return this.demandLinkRequests
        .filter((request) => request.projectId === this.detailForm.projectId)
        .filter((request) => request.id !== this.selectedDemand.requestId)
        .map((request) => ({
          text: `${request.displayId} · ${request.title} · ${request.status}`,
          value: request.id
        }))
    },
    boardProjectOptions () {
      return [
        { text: 'Todos os projetos', value: 'all' },
        ...this.projectOptions
      ]
    },
    mentionableUsers () {
      return this.users
        .filter((user) => user.username)
        .map((user) => ({
          id: user.id,
          name: user.name,
          username: user.username,
          email: user.email
        }))
    },
    filteredMentionUsers () {
      const query = String(this.mentionPopover.query || '').toLowerCase()
      return this.mentionableUsers
        .filter((user) => !query || user.username.includes(query) || String(user.name || '').toLowerCase().includes(query))
        .slice(0, 6)
    },
    selectedBoardProjectId () {
      if (this.boardProjectId !== 'all') return this.boardProjectId
      return ''
    },
    statusMoveOptions () {
      return this.demandLists.length ? this.demandLists.map((list) => list.name) : this.statuses
    },
    publishStatusOptions () {
      return this.statusMoveOptions.map((s) => ({ text: s, value: s }))
    },
    kanbanColumns () {
      const lists = this.demandLists.length
        ? this.demandLists
        : this.statuses.map((status, index) => ({ name: status, description: '', color: ['#71717a', '#94a3b8', '#f4b000', '#22c55e'][index] }))

      return lists.map((list) => ({
        status: list.name,
        label: list.name,
        description: list.description || '',
        color: list.color || '#00f0ff',
        className: this.kanbanColumnClass(list.name),
        items: this.filteredDemands
          .filter((demand) => demand.status === list.name)
          .sort((a, b) => this.compareDemandsInColumn(a, b)),
        hours: this.filteredDemands
          .filter((demand) => demand.status === list.name)
          .reduce((total, demand) => total + Number(demand.totalHours || 0), 0)
      }))
    }
  },
  async mounted () {
    window.addEventListener('resize', this.syncSidebarForViewport)
    window.addEventListener('popstate', this.handleWorkspacePopState)
    const params = new URLSearchParams(window.location.search)
    this.inviteToken = params.get('inviteToken') || ''
    if (this.inviteToken) {
      api.clearToken()
      await this.loadInviteInfo()
      this.booting = false
      return
    }

    this.resetToken = params.get('resetToken') || ''
    if (this.resetToken) {
      api.clearToken()
      this.authMode = 'reset'
      this.booting = false
      return
    }

    if (!api.hasToken()) {
      this.booting = false
      return
    }

    try {
      this.user = await api.me()
      this.activeView = this.initialWorkspaceView(params)
      this.syncSidebarForViewport()
      this.setupRealtime()
      await this.loadAll()
      await this.openRequestFromUrlParam(params)
      this.syncWorkspaceViewUrl()
    } catch (error) {
      api.clearToken()
      this.user = null
    } finally {
      this.booting = false
    }
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.syncSidebarForViewport)
    window.removeEventListener('popstate', this.handleWorkspacePopState)
  },
  methods: {
    isDemandCompleted (status) {
      return String(status || '').toLowerCase().includes('conclu')
    },
    setAuthMode (mode) {
      this.authMode = mode
      this.loginError = ''
      this.authMessage = ''
    },
    defaultWorkspaceView () {
      return this.isClient ? 'dashboard' : 'requests'
    },
    initialWorkspaceView (params = new URLSearchParams(window.location.search)) {
      const view = params.get('view') || ''
      if (this.isAllowedWorkspaceView(view)) return view
      return this.defaultWorkspaceView()
    },
    isAllowedWorkspaceView (view) {
      if (!view) return false
      if ((view === 'users' || view === 'reports') && !this.isAdmin) return false
      if (view === 'drafts') return this.isAdmin
      return this.navigationItems.some((item) => item.value === view)
    },
    syncWorkspaceViewUrl (replace = true) {
      if (!this.user || !this.isAllowedWorkspaceView(this.activeView) || this.activeView === 'drafts') return
      const url = new URL(window.location.href)
      url.searchParams.delete('inviteToken')
      url.searchParams.delete('resetToken')
      url.searchParams.set('view', this.activeView)
      if (this.activeView === 'demands' && this.selectedBoardProjectId) {
        url.searchParams.set('projectId', this.selectedBoardProjectId)
      } else {
        url.searchParams.delete('projectId')
      }
      const next = `${url.pathname}${url.search}${url.hash}`
      const current = `${window.location.pathname}${window.location.search}${window.location.hash}`
      if (next === current) return
      const method = replace ? 'replaceState' : 'pushState'
      window.history[method]({}, document.title, next)
    },
    isDesktopViewport () {
      return window.matchMedia('(min-width: 1121px)').matches
    },
    syncSidebarForViewport () {
      const isDesktop = this.isDesktopViewport()
      if (this.sidebarViewportWasDesktop === isDesktop) return
      this.sidebarViewportWasDesktop = isDesktop
      this.sidebarOpen = isDesktop
    },
    toggleSidebar () {
      this.sidebarOpen = !this.sidebarOpen
    },
    handleWorkspacePopState () {
      if (!this.user) return
      const params = new URLSearchParams(window.location.search)
      const view = this.initialWorkspaceView()
      const projectId = params.get('projectId')
      const routeProject = this.projects.find((project) => String(project.id) === String(projectId))
      let projectChanged = false
      if (view === 'demands' && routeProject) {
        projectChanged = String(this.boardProjectId) !== String(routeProject.id)
        this.boardProjectId = routeProject.id
        this.demandsMenuOpen = true
      }
      if (view === this.activeView) {
        if (projectChanged) this.changeBoardProject()
        return
      }
      this.activeView = view
      if (view === 'reports') this.loadReports()
      if (view === 'users') this.loadClientInvites()
      if (view === 'demands') this.changeBoardProject()
      if (view === 'requests') this.openRequestFromUrlParam()
    },
    async openRequestFromUrlParam (params = new URLSearchParams(window.location.search)) {
      const requestId = params.get('request')
      if (!requestId || !this.isAllowedWorkspaceView('requests')) return
      this.activeView = 'requests'
      await this.openRequestDetails({ id: requestId })
    },
    setupRealtime () {
      const socket = realtime.connect(api.getToken())
      if (!socket) return
      realtime.offMessageCreated(this.handleRealtimeMessage)
      realtime.offRequestUpdated(this.handleRealtimeRequest)
      realtime.onMessageCreated(this.handleRealtimeMessage)
      realtime.onRequestUpdated(this.handleRealtimeRequest)
    },
    async loadInviteInfo () {
      this.loggingIn = true
      this.loginError = ''
      try {
        this.inviteInfo = await api.getClientInvite(this.inviteToken)
        this.inviteForm.email = this.inviteInfo.email || ''
        this.authMode = 'invite'
      } catch (error) {
        this.authMode = 'login'
        this.loginError = this.errorMessage(error)
      } finally {
        this.loggingIn = false
      }
    },
    async registerInvite () {
      if (!this.$refs.inviteRegisterFormRef.validate()) return
      if (this.inviteForm.password !== this.inviteForm.confirmation) {
        this.loginError = 'As senhas não conferem'
        return
      }

      this.loggingIn = true
      this.loginError = ''
      try {
        const session = await api.registerClientInvite(this.inviteToken, {
          ...this.inviteForm,
          username: this.cleanUsername(this.inviteForm.username)
        })
        api.setToken(session.token)
        this.user = session.user
        this.activeView = this.defaultWorkspaceView()
        this.syncSidebarForViewport()
        this.setupRealtime()
        await this.loadAll()
        this.syncWorkspaceViewUrl()
      } catch (error) {
        this.loginError = this.errorMessage(error)
      } finally {
        this.loggingIn = false
      }
    },
    clearInviteMode () {
      this.inviteToken = ''
      this.inviteInfo = null
      this.inviteForm = { name: '', username: '', email: '', password: '', confirmation: '' }
      window.history.replaceState({}, document.title, window.location.pathname)
      this.setAuthMode('login')
    },
    normalizeInviteUsername () {
      this.inviteForm.username = this.cleanUsername(this.inviteForm.username)
    },
    async login () {
      if (!this.$refs.loginFormRef.validate()) return
      this.loggingIn = true
      this.loginError = ''
      this.authMessage = ''
      try {
        const session = await api.login(this.loginForm)
        api.setToken(session.token)
        this.user = session.user
        this.activeView = this.defaultWorkspaceView()
        this.syncSidebarForViewport()
        this.setupRealtime()
        await this.loadAll()
        this.syncWorkspaceViewUrl()
      } catch (error) {
        this.loginError = this.errorMessage(error)
      } finally {
        this.loggingIn = false
      }
    },
    async forgotPassword () {
      if (!this.$refs.forgotFormRef.validate()) return
      this.loggingIn = true
      this.loginError = ''
      this.authMessage = ''
      try {
        const response = await api.forgotPassword(this.forgotForm)
        this.authMessage = response.message || 'Confira seu e-mail para recuperar o acesso.'
      } catch (error) {
        this.loginError = this.errorMessage(error)
      } finally {
        this.loggingIn = false
      }
    },
    async resetPassword () {
      if (!this.$refs.resetFormRef.validate()) return
      if (this.resetForm.password !== this.resetForm.confirmation) {
        this.loginError = 'As senhas não conferem'
        return
      }

      this.loggingIn = true
      this.loginError = ''
      this.authMessage = ''
      try {
        const response = await api.resetPassword({ token: this.resetToken, password: this.resetForm.password })
        this.resetToken = ''
        this.resetForm = { password: '', confirmation: '' }
        this.authMode = 'login'
        this.authMessage = response.message || 'Senha alterada com sucesso.'
        window.history.replaceState({}, document.title, window.location.pathname)
      } catch (error) {
        this.loginError = this.errorMessage(error)
      } finally {
        this.loggingIn = false
      }
    },
    openProfileDialog () {
      this.userMenuOpen = false
      this.profileError = ''
      this.profileMessage = ''
      this.profilePasswordForm = { currentPassword: '', newPassword: '', confirmation: '' }
      this.profileDialog = true
      this.$nextTick(() => this.$refs.profilePasswordFormRef && this.$refs.profilePasswordFormRef.resetValidation())
    },
    closeProfileDialog () {
      this.profileDialog = false
      this.profileError = ''
      this.profileMessage = ''
      this.profilePasswordForm = { currentPassword: '', newPassword: '', confirmation: '' }
    },
    async changeOwnPassword () {
      if (!this.$refs.profilePasswordFormRef.validate()) return
      if (this.profilePasswordForm.newPassword !== this.profilePasswordForm.confirmation) {
        this.profileError = 'As senhas não conferem'
        return
      }

      this.savingProfile = true
      this.profileError = ''
      this.profileMessage = ''
      try {
        const response = await api.changePassword({
          currentPassword: this.profilePasswordForm.currentPassword,
          newPassword: this.profilePasswordForm.newPassword
        })
        this.profilePasswordForm = { currentPassword: '', newPassword: '', confirmation: '' }
        this.profileMessage = response.message || 'Senha alterada com sucesso.'
        this.$nextTick(() => this.$refs.profilePasswordFormRef && this.$refs.profilePasswordFormRef.resetValidation())
      } catch (error) {
        this.profileError = this.errorMessage(error)
      } finally {
        this.savingProfile = false
      }
    },
    logout () {
      api.clearToken()
      realtime.disconnect()
      this.user = null
      this.activeView = 'dashboard'
      this.sidebarOpen = false
      this.sidebarViewportWasDesktop = null
      this.userMenuOpen = false
      this.clientProjectMenuOpen = false
      this.clientProjectContextId = 'all'
      this.demands = []
      this.summaryDemands = []
      this.requests = []
      this.requestMessages = []
      this.demandLinkRequests = []
      this.projects = []
      this.users = []
      this.clientInvites = []
      this.reportRows = []
      this.demandLists = []
      this.summary = { total: 0, pending: 0, done: 0, highPriority: 0 }
      window.history.replaceState({}, document.title, window.location.pathname)
    },
    setWorkspaceView (view) {
      if (!this.isAllowedWorkspaceView(view)) return
      if (view === 'reports' && !this.isAdmin) return
      this.activeView = view
      if (view === 'demands') this.demandsMenuOpen = true
      this.syncWorkspaceViewUrl(false)
      this.error = ''
      if (!this.isDesktopViewport()) this.sidebarOpen = false
      this.userMenuOpen = false
      this.clientProjectMenuOpen = false
      if (view === 'reports') this.loadReports()
      if (view === 'users') this.loadClientInvites()
      if (view === 'drafts') this.loadDrafts()
    },
    isNavigationItemActive (item) {
      return item.value === 'demands'
        ? this.activeView === 'demands' && !this.selectedBoardProjectId
        : this.activeView === item.value
    },
    navigationItemBadge (item) {
      if (!this.sidebarOpen) return ''
      if (item.value === 'requests') return this.requests.length ? String(this.requests.length) : ''
      if (item.value === 'demands') return this.dashboardSummary.pending ? String(this.dashboardSummary.pending) : ''
      if (item.value === 'projects') return this.projects.length ? String(this.projects.length) : ''
      if (item.value === 'users') return this.users.length ? String(this.users.length) : ''
      return ''
    },
    handleNavigationItem (item) {
      if (item.value === 'demands') {
        if (!this.sidebarOpen && this.isDesktopViewport()) {
          this.sidebarOpen = true
          this.demandsMenuOpen = true
        } else {
          this.demandsMenuOpen = !this.demandsMenuOpen
        }
        if (!this.demandsMenuOpen) return
        if (!this.selectedBoardProjectId && this.projects.length === 1) {
          this.selectDemandProject(this.projects[0].id)
        }
        return
      }
      this.setWorkspaceView(item.value)
    },
    async selectAllDemandProjects () {
      this.boardProjectId = 'all'
      this.demandsMenuOpen = true
      this.activeView = 'demands'
      this.syncWorkspaceViewUrl(false)
      this.error = ''
      if (!this.isDesktopViewport()) this.sidebarOpen = false
      await this.changeBoardProject()
    },
    async selectDemandProject (projectId) {
      const project = this.projects.find((item) => String(item.id) === String(projectId))
      if (!project) return
      this.boardProjectId = project.id
      this.demandsMenuOpen = true
      this.activeView = 'demands'
      this.syncWorkspaceViewUrl(false)
      this.error = ''
      if (!this.isDesktopViewport()) this.sidebarOpen = false
      await this.changeBoardProject()
    },
    applyDemandProjectFromUrl () {
      const params = new URLSearchParams(window.location.search)
      const projectId = params.get('projectId')
      if (this.activeView !== 'demands' || !projectId) return
      const project = this.projects.find((item) => String(item.id) === String(projectId))
      if (!project) return
      this.boardProjectId = project.id
      this.demandsMenuOpen = true
    },
    activateWorkspaceView (view, replace = true) {
      if (!this.isAllowedWorkspaceView(view)) return false
      this.activeView = view
      this.syncWorkspaceViewUrl(replace)
      return true
    },
    async loadAll () {
      await this.loadProjects()
      this.applyDemandProjectFromUrl()
      this.ensureBoardProject()
      await Promise.all([
        this.loadProjectLists(),
        this.loadDemands(),
        this.loadDemandSummaryScope(),
        this.loadRequests(),
        this.loadSummary(),
        this.loadUsers(),
        this.isAdmin ? this.loadClientInvites() : Promise.resolve(),
        this.isAdmin ? this.loadReports() : Promise.resolve(),
        this.activeView === 'gantt' ? this.loadScheduleDemands({ silent: true }) : Promise.resolve()
      ])
    },
    async loadProjects () {
      try {
        this.projects = await api.listProjects()
      } catch (error) {
        this.error = this.errorMessage(error)
      }
    },
    async loadUsers () {
      if (!this.isAdmin) {
        this.users = this.user ? [this.user] : []
        return
      }
      try {
        this.users = await api.listUsers()
      } catch (error) {
        this.error = this.errorMessage(error)
      }
    },
    async loadClientInvites () {
      if (!this.isAdmin) {
        this.clientInvites = []
        return
      }
      try {
        this.clientInvites = await api.listClientInvites()
      } catch (error) {
        this.error = this.errorMessage(error)
      }
    },
    async changeRequestLifecycle (item, action) {
      if (this.requestActionBusy) return
      const messages = {
        archive: 'Arquivar esta solicitação? Ela poderá ser restaurada pelo filtro Arquivadas.',
        restore: 'Restaurar esta solicitação para a lista de ativas?',
        delete: 'Excluir esta solicitação? Ela deixará de aparecer no sistema. As demandas vinculadas serão preservadas.'
      }
      if (!window.confirm(messages[action])) return
      this.requestActionBusy = true
      this.error = ''
      try {
        const methods = { archive: 'archiveRequest', restore: 'restoreRequest', delete: 'deleteRequest' }
        await api[methods[action]](item.id)
        if (this.selectedRequest && this.selectedRequest.id === item.id) this.closeRequestDetails()
        await this.loadRequests()
      } catch (error) {
        this.error = this.errorMessage(error)
      } finally {
        this.requestActionBusy = false
      }
    },
    async loadRequests () {
      try {
        const params = { ...this.requestFilters }
        if (this.selectedClientProjectId) params.projectId = this.selectedClientProjectId
        if (params.projectId === 'all') delete params.projectId
        this.requests = await api.listRequests(params)
      } catch (error) {
        this.error = this.errorMessage(error)
      }
    },
    async loadDemands () {
      this.loading = true
      this.error = ''
      try {
        const params = { ...this.filters }
        if (this.selectedClientProjectId) params.projectId = this.selectedClientProjectId
        else if (this.selectedBoardProjectId) params.projectId = this.selectedBoardProjectId
        this.demands = await api.listDemands(params)
      } catch (error) {
        this.error = this.errorMessage(error)
      } finally {
        this.loading = false
      }
    },
    async loadProjectLists () {
      if (this.isClient && this.selectedClientProjectId) {
        try {
          this.demandLists = await api.listProjectLists(this.selectedClientProjectId)
        } catch (error) {
          this.error = this.errorMessage(error)
        }
        return
      }

      if (!this.selectedBoardProjectId) {
        this.demandLists = this.statuses.map((status, index) => ({ name: status, description: '', color: ['#71717a', '#94a3b8', '#f4b000', '#22c55e'][index] }))
        return
      }

      try {
        this.demandLists = await api.listProjectLists(this.selectedBoardProjectId)
      } catch (error) {
        this.error = this.errorMessage(error)
      }
    },
    async loadSummary () {
      try {
        this.summary = await api.summary()
      } catch (error) {
        this.error = this.errorMessage(error)
      }
    },
    async loadDemandSummaryScope () {
      const projectId = this.selectedClientProjectId || this.selectedBoardProjectId
      if (!projectId) {
        this.summaryDemands = []
        return
      }

      try {
        this.summaryDemands = await api.listDemands({ projectId })
      } catch (error) {
        this.error = this.errorMessage(error)
      }
    },
    async loadReports () {
      if (!this.isAdmin) return
      this.loadingReports = true
      try {
        const params = { year: this.reportYear }
        if (this.reportProjectId !== 'all') params.projectId = this.reportProjectId
        const report = await api.projectMonthlyReport(params)
        this.reportRows = report.rows || []
      } catch (error) {
        this.error = this.errorMessage(error)
      } finally {
        this.loadingReports = false
      }
    },
    openReportExportModal () {
      if (!this.isAdmin) return
      const selectedProjectId = this.reportProjectId !== 'all'
        ? this.reportProjectId
        : (this.projects[0]?.id || '')
      this.reportExportForm = {
        projectId: selectedProjectId,
        month: new Date().getMonth() + 1,
        year: this.reportYear
      }
      this.reportExportDialog = true
      this.$nextTick(() => this.$refs.reportExportFormRef && this.$refs.reportExportFormRef.resetValidation())
    },
    async exportHourMonthlyReport () {
      if (!this.isAdmin) return
      if (!this.$refs.reportExportFormRef.validate()) return
      if (!this.reportExportForm.projectId) {
        this.error = 'Selecione um projeto específico para exportar horas.'
        return
      }
      this.savingReportExport = true
      this.error = ''
      try {
        const params = { year: this.reportExportForm.year, month: this.reportExportForm.month, projectId: this.reportExportForm.projectId }
        const report = await api.hourMonthlyReport(params)
        const projectName = this.projects.find((project) => project.id === this.reportExportForm.projectId)?.name || 'Projeto'
        const logoDataUrl = await this.loadReportLogoDataUrl()
        const workbook = await this.buildHourReportWorkbook(report.rows || [], this.reportExportForm.year, this.reportExportForm.month, projectName, logoDataUrl)
        const blob = new Blob([workbook], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `relatorio-horas-${this.safeFileName(projectName)}-${this.reportExportForm.year}-${String(this.reportExportForm.month).padStart(2, '0')}.xlsx`
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
        this.reportExportDialog = false
      } catch (error) {
        this.error = this.errorMessage(error)
      } finally {
        this.savingReportExport = false
      }
    },
    async loadReportLogoDataUrl () {
      const response = await fetch(reportLogoUrl)
      const blob = await response.blob()
      const svgDataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result)
        reader.onerror = reject
        reader.readAsDataURL(blob)
      })
      return await this.renderReportLogoPng(svgDataUrl)
    },
    async renderReportLogoPng (svgDataUrl) {
      const image = await new Promise((resolve, reject) => {
        const img = new Image()
        img.onload = () => resolve(img)
        img.onerror = reject
        img.src = svgDataUrl
      })
      const canvas = document.createElement('canvas')
      canvas.width = 369
      canvas.height = 69
      const context = canvas.getContext('2d')
      context.fillStyle = '#ffffff'
      context.fillRect(0, 0, canvas.width, canvas.height)
      context.filter = 'invert(1)'
      context.drawImage(image, 0, 0, canvas.width, canvas.height)
      return canvas.toDataURL('image/png')
    },
    async buildHourReportWorkbook (rows, year, month, projectName, logoDataUrl) {
      const { default: ExcelJS } = await import('exceljs')
      const sheetName = `${this.fullMonthName(month - 1)} ${String(year).slice(-2)}`
      const totalHours = rows.reduce((sum, row) => sum + Number(row.totalHours || 0), 0)
      const workbook = new ExcelJS.Workbook()
      workbook.creator = 'Demandesk'
      workbook.created = new Date()
      const sheet = workbook.addWorksheet(sheetName)
      const thinBorder = { style: 'thin', color: { argb: 'FFE6E6E6' } }
      const summaryBorder = { style: 'thin', color: { argb: 'FFD9D9D9' } }
      const baseFont = { name: 'Montserrat', size: 13, color: { argb: 'FF000000' } }

      sheet.columns = [
        { key: 'id', width: 44 },
        { key: 'hours', width: 18 },
        { key: 'task', width: 145 }
      ]

      sheet.mergeCells('A1:C1')
      sheet.getRow(1).height = 90
      ;['A1', 'B1', 'C1'].forEach((address) => {
        sheet.getCell(address).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFFFF' } }
      })
      const logoId = workbook.addImage({ base64: logoDataUrl, extension: 'png' })
      sheet.addImage(logoId, {
        tl: { col: 0.08, row: 0.22 },
        ext: { width: 280, height: 52 },
        editAs: 'oneCell'
      })

      const summaryRows = [
        ['Total de saldo em horas mês passado', 0, ''],
        ['Total de saldo para o próximo mês', 0, ''],
        ['Total de horas trabalhadas', totalHours, ''],
        ['Total de horas', totalHours, `Relatório de horas (${projectName})`]
      ]

      summaryRows.forEach((values, index) => {
        const row = sheet.getRow(index + 2)
        row.height = 22
        row.values = values
        row.getCell(1).font = baseFont
        row.getCell(1).border = { bottom: summaryBorder }
        row.getCell(2).font = { ...baseFont, bold: true }
        row.getCell(2).numFmt = '0.0'
        row.getCell(2).alignment = { horizontal: 'center', vertical: 'middle' }
        row.getCell(2).border = { bottom: summaryBorder }
        row.getCell(3).font = { ...baseFont, bold: true }
        row.getCell(3).border = { bottom: summaryBorder }
      })

      sheet.getRow(6).height = 24
      const header = sheet.getRow(7)
      header.height = 26
      header.values = ['ID', 'Horas', 'Tarefas']
      header.eachCell((cell) => {
        cell.font = { ...baseFont, bold: true }
        cell.alignment = { horizontal: 'center', vertical: 'middle' }
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD9D9D9' } }
      })

      rows.forEach((item, index) => {
        const row = sheet.getRow(index + 8)
        row.height = 24
        row.values = [item.displayId || item.id || '', Number(item.totalHours || 0), item.title || item.description || '']
        row.getCell(1).font = baseFont
        row.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' }
        row.getCell(2).font = baseFont
        row.getCell(2).numFmt = '0.0'
        row.getCell(2).alignment = { horizontal: 'center', vertical: 'middle' }
        row.getCell(3).font = baseFont
        row.getCell(3).alignment = { vertical: 'middle', wrapText: true }
        row.eachCell((cell) => {
          cell.border = { bottom: thinBorder }
        })
      })

      return workbook.xlsx.writeBuffer()
    },
    fullMonthName (index) {
      return ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'][index]
    },
    safeFileName (value) {
      return String(value || 'geral')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-zA-Z0-9_-]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .toLowerCase()
    },
    resolveGanttRange (demand) {
      const start = demand.plannedStartDate || (demand.createdAt || new Date().toISOString()).slice(0, 10)
      const completed = this.isDemandCompleted(demand.status)
      let end = demand.plannedEndDate || (completed && demand.completedAt ? demand.completedAt.slice(0, 10) : (demand.dueDate || null))
      if (!end) {
        const fallback = new Date(`${start}T00:00:00`)
        fallback.setDate(fallback.getDate() + 1)
        end = fallback.toISOString().slice(0, 10)
      } else if (end < start) {
        end = start
      }
      return { start, end }
    },
    async openGanttView () {
      if (!this.selectedBoardProjectId) return
      this.activeView = 'gantt'
      this.syncWorkspaceViewUrl(false)
      await this.loadScheduleDemands()
    },
    async loadScheduleDemands ({ silent = false } = {}) {
      if (!this.selectedBoardProjectId) return
      // Silent refreshes keep the schedule mounted so collapsed rows and scroll survive.
      if (!silent) {
        this.error = ''
        this.loadingGantt = true
      }
      try {
        this.ganttDemands = await api.listDemands({ projectId: this.selectedBoardProjectId })
      } catch (error) {
        this.error = this.errorMessage(error)
        if (!silent) this.ganttDemands = []
      } finally {
        if (!silent) this.loadingGantt = false
      }
    },
    refreshScheduleDemands () {
      return this.loadScheduleDemands({ silent: true })
    },
    handleScheduleDemandCreated (demand) {
      this.ganttDemands = [...this.ganttDemands.filter((item) => item.id !== demand.id), demand]
      this.reconcileDemand(demand)
      this.loadSummary()
    },
    handleScheduleDemandUpdated (demand) {
      const index = this.ganttDemands.findIndex((item) => item.id === demand.id)
      if (index >= 0) this.$set(this.ganttDemands, index, demand)
      else this.ganttDemands.push(demand)
      this.reconcileDemand(demand)
      this.loadSummary()
    },
    openGanttExportModal () {
      this.ganttExportForm = { dateFrom: '', dateTo: '', statuses: [], priorities: [], withGanttBars: false }
      this.ganttExportDialog = true
    },
    async exportGanttToExcel () {
      this.savingGanttExport = true
      this.error = ''
      try {
        const rows = this.filteredGanttExportDemands
        const projectName = this.selectedBoardProjectName || 'Projeto'
        const logoDataUrl = await this.loadReportLogoDataUrl()
        const workbook = this.ganttExportForm.withGanttBars
          ? await this.buildGanttBarsWorkbook(rows, projectName, logoDataUrl)
          : await this.buildGanttExportWorkbook(rows, projectName, logoDataUrl)
        const blob = new Blob([workbook], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `gantt-${this.safeFileName(projectName)}-${new Date().toISOString().slice(0, 10)}.xlsx`
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
        this.ganttExportDialog = false
      } catch (error) {
        this.error = this.errorMessage(error)
      } finally {
        this.savingGanttExport = false
      }
    },
    async buildGanttExportWorkbook (rows, projectName, logoDataUrl) {
      const { default: ExcelJS } = await import('exceljs')
      const workbook = new ExcelJS.Workbook()
      workbook.creator = 'Demandesk'
      workbook.created = new Date()
      const sheet = workbook.addWorksheet('Gantt')
      const baseFont = { name: 'Montserrat', size: 11, color: { argb: 'FF000000' } }
      const thinBorder = { style: 'thin', color: { argb: 'FFE6E6E6' } }

      sheet.columns = [
        { header: '', key: 'code', width: 16 },
        { header: '', key: 'title', width: 40 },
        { header: '', key: 'company', width: 22 },
        { header: '', key: 'status', width: 18 },
        { header: '', key: 'priority', width: 12 },
        { header: '', key: 'owner', width: 22 },
        { header: '', key: 'hours', width: 12 },
        { header: '', key: 'createdAt', width: 16 },
        { header: '', key: 'completedAt', width: 18 },
        { header: '', key: 'duration', width: 14 }
      ]

      sheet.mergeCells('A1:J1')
      sheet.getRow(1).height = 60
      sheet.getCell('A1').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFFFF' } }
      const logoId = workbook.addImage({ base64: logoDataUrl, extension: 'png' })
      sheet.addImage(logoId, {
        tl: { col: 0.08, row: 0.18 },
        ext: { width: 220, height: 41 },
        editAs: 'oneCell'
      })

      sheet.getRow(2).values = [`Gantt (${projectName})`]
      sheet.getRow(2).getCell(1).font = { ...baseFont, bold: true, size: 14 }

      const totalHours = rows.reduce((sum, demand) => sum + Number(demand.totalHours || 0), 0)
      sheet.getRow(3).values = [`Total de horas: ${totalHours}h`]
      sheet.getRow(3).getCell(1).font = { ...baseFont, bold: true, color: { argb: 'FF555555' } }

      const headerRow = sheet.getRow(4)
      headerRow.height = 24
      headerRow.values = ['C\u00f3digo', 'T\u00edtulo', 'Empresa', 'Status', 'Prioridade', 'Respons\u00e1vel', 'Horas', 'Data de cria\u00e7\u00e3o', 'Data de conclus\u00e3o', 'Dura\u00e7\u00e3o (dias)']
      headerRow.eachCell((cell) => {
        cell.font = { ...baseFont, bold: true }
        cell.alignment = { horizontal: 'center', vertical: 'middle' }
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD9D9D9' } }
      })

      rows.forEach((demand, index) => {
        const { start, end } = this.resolveGanttRange(demand)
        const duration = Math.round((new Date(`${end}T00:00:00`) - new Date(`${start}T00:00:00`)) / 86400000)
        const row = sheet.getRow(index + 5)
        row.height = 22
        row.values = [
          demand.displayId || '',
          demand.title || '',
          demand.company || '',
          demand.status || '',
          this.priorityLabel(demand.priority),
          demand.owner || '',
          Number(demand.totalHours || 0),
          demand.createdAt ? this.formatDate(demand.createdAt.slice(0, 10)) : '',
          demand.completedAt ? this.formatDate(demand.completedAt.slice(0, 10)) : '',
          duration
        ]
        row.eachCell((cell) => {
          cell.font = baseFont
          cell.alignment = { vertical: 'middle' }
          cell.border = { bottom: thinBorder }
        })
      })

      return workbook.xlsx.writeBuffer()
    },
    async buildGanttBarsWorkbook (rows, projectName, logoDataUrl) {
      const { default: ExcelJS } = await import('exceljs')
      const workbook = new ExcelJS.Workbook()
      workbook.creator = 'Demandesk'
      workbook.created = new Date()
      const sheet = workbook.addWorksheet('Gantt')
      const baseFont = { name: 'Montserrat', size: 10, color: { argb: 'FF000000' } }
      const thinBorder = { style: 'thin', color: { argb: 'FFE6E6E6' } }
      const priorityColors = {
        1: 'FFB91C1C',
        2: 'FFB45309',
        3: 'FF15803D'
      }

      const ranges = rows.map((demand) => ({ demand, ...this.resolveGanttRange(demand) }))
      const infoColumnCount = 5
      if (!ranges.length) {
        sheet.columns = [{ header: '', key: 'empty', width: 40 }]
        sheet.getRow(1).values = ['Nenhuma demanda encontrada para os filtros selecionados.']
        return workbook.xlsx.writeBuffer()
      }

      const minDate = new Date(Math.min(...ranges.map((item) => new Date(`${item.start}T00:00:00`).getTime())))
      const maxDate = new Date(Math.max(...ranges.map((item) => new Date(`${item.end}T00:00:00`).getTime())))
      const days = []
      for (let cursor = new Date(minDate); cursor <= maxDate; cursor.setDate(cursor.getDate() + 1)) {
        days.push(new Date(cursor))
      }

      const infoColumns = [
        { header: '', key: 'code', width: 14 },
        { header: '', key: 'title', width: 32 },
        { header: '', key: 'owner', width: 20 },
        { header: '', key: 'priority', width: 12 },
        { header: '', key: 'hours', width: 10 }
      ]
      const dayColumns = days.map((day, index) => ({ header: '', key: `d${index}`, width: 3.2 }))
      sheet.columns = [...infoColumns, ...dayColumns]

      const totalCols = infoColumnCount + days.length
      sheet.mergeCells(1, 1, 1, totalCols)
      sheet.getRow(1).height = 60
      sheet.getCell(1, 1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFFFF' } }
      const logoId = workbook.addImage({ base64: logoDataUrl, extension: 'png' })
      sheet.addImage(logoId, {
        tl: { col: 0.08, row: 0.18 },
        ext: { width: 220, height: 41 },
        editAs: 'oneCell'
      })

      sheet.mergeCells(2, 1, 2, totalCols)
      sheet.getCell(2, 1).value = `Gantt (${projectName})`
      sheet.getCell(2, 1).font = { ...baseFont, bold: true, size: 14 }

      const totalHours = rows.reduce((sum, demand) => sum + Number(demand.totalHours || 0), 0)
      sheet.mergeCells(3, 1, 3, totalCols)
      sheet.getCell(3, 1).value = `Total de horas: ${totalHours}h`
      sheet.getCell(3, 1).font = { ...baseFont, bold: true, color: { argb: 'FF555555' } }

      const monthRow = sheet.getRow(4)
      const dayRow = sheet.getRow(5)
      monthRow.height = 18
      dayRow.height = 18
      let monthCursor = 0
      while (monthCursor < days.length) {
        const monthKey = `${days[monthCursor].getFullYear()}-${days[monthCursor].getMonth()}`
        let span = 1
        while (
          monthCursor + span < days.length &&
          `${days[monthCursor + span].getFullYear()}-${days[monthCursor + span].getMonth()}` === monthKey
        ) {
          span += 1
        }
        const startCol = infoColumnCount + monthCursor + 1
        const endCol = infoColumnCount + monthCursor + span
        if (endCol > startCol) sheet.mergeCells(4, startCol, 4, endCol)
        const monthCell = sheet.getCell(4, startCol)
        monthCell.value = this.fullMonthName(days[monthCursor].getMonth())
        monthCell.font = { ...baseFont, bold: true }
        monthCell.alignment = { horizontal: 'center' }
        monthCursor += span
      }
      days.forEach((day, index) => {
        const cell = dayRow.getCell(infoColumnCount + index + 1)
        cell.value = day.getDate()
        cell.font = { ...baseFont, size: 8 }
        cell.alignment = { horizontal: 'center' }
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD9D9D9' } }
      })
      ;['Código', 'Título', 'Responsável', 'Prioridade', 'Horas'].forEach((label, index) => {
        const cell = sheet.getCell(5, index + 1)
        cell.value = label
        cell.font = { ...baseFont, bold: true }
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD9D9D9' } }
      })

      ranges.forEach((item, rowIndex) => {
        const row = sheet.getRow(rowIndex + 6)
        row.height = 20
        row.getCell(1).value = item.demand.displayId || ''
        row.getCell(2).value = item.demand.title || ''
        row.getCell(3).value = item.demand.owner || ''
        row.getCell(4).value = this.priorityLabel(item.demand.priority)
        row.getCell(5).value = Number(item.demand.totalHours || 0)
        row.eachCell({ includeEmpty: false }, (cell, colNumber) => {
          if (colNumber <= infoColumnCount) {
            cell.font = baseFont
            cell.alignment = { vertical: 'middle' }
            cell.border = { bottom: thinBorder }
          }
        })

        const start = new Date(`${item.start}T00:00:00`)
        const end = new Date(`${item.end}T00:00:00`)
        const color = priorityColors[this.priorityValue(item.demand.priority)] || priorityColors[2]
        days.forEach((day, dayIndex) => {
          const cell = row.getCell(infoColumnCount + dayIndex + 1)
          if (day >= start && day <= end) {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: color } }
          }
          cell.border = { bottom: thinBorder }
        })
      })

      return workbook.xlsx.writeBuffer()
    },
    debouncedLoad () {
      clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => this.loadDemands(), 250)
    },
    handleDemandDateFilterChange () {
      this.inlineDemandComposer = { status: '', title: '', totalHours: '', dueDate: '' }
    },
    normalizeDateKey (value) {
      if (!value) return ''
      return String(value).slice(0, 10)
    },
    demandFilterDate (demand) {
      const completed = this.isDemandCompleted(demand.status)
      return this.normalizeDateKey((completed && demand.completedAt) || demand.dueDate || demand.createdAt)
    },
    demandMatchesDateFilter (demand, filters = this.filters) {
      const from = this.normalizeDateKey(filters.dateFrom)
      const to = this.normalizeDateKey(filters.dateTo)
      if (!from && !to) return true

      const date = this.demandFilterDate(demand)
      if (!date) return false
      if (from && date < from) return false
      if (to && date > to) return false
      return true
    },
    demandRangeMatchesDateFilter (demand, filters = this.filters) {
      const from = this.normalizeDateKey(filters.dateFrom)
      const to = this.normalizeDateKey(filters.dateTo)
      if (!from && !to) return true

      const { start, end } = this.resolveGanttRange(demand)
      if (from && end < from) return false
      if (to && start > to) return false
      return true
    },
    debouncedLoadRequests () {
      clearTimeout(this.requestSearchTimer)
      this.requestSearchTimer = setTimeout(() => this.loadRequests(), 250)
    },
    setStatus (status) {
      this.filters.status = status
      this.loadDemands()
    },
    async changeBoardProject () {
      this.filters.status = 'all'
      await Promise.all([this.loadProjectLists(), this.loadDemands(), this.loadDemandSummaryScope()])
      if (this.selectedBoardProjectId) {
        this.listForm.projectId = this.selectedBoardProjectId
      }
    },
    async changeClientProjectContext () {
      if (!this.isClient) return
      this.filters.status = 'all'
      this.requestFilters.projectId = this.clientProjectContextId
      this.boardProjectId = this.clientProjectContextId
      await Promise.all([this.loadProjectLists(), this.loadDemands(), this.loadDemandSummaryScope(), this.loadRequests()])
    },
    async selectClientProjectContext (projectId) {
      this.clientProjectContextId = projectId
      this.clientProjectMenuOpen = false
      await this.changeClientProjectContext()
    },
    openListModal () {
      const fallbackProjectId = (this.projects[0] && this.projects[0].id) || ''
      const currentProjectIsValid = this.projects.some((project) => project.id === this.listForm.projectId)
      this.listForm.projectId = this.selectedBoardProjectId || (currentProjectIsValid ? this.listForm.projectId : '') || fallbackProjectId
      this.listDialog = true
      this.$nextTick(() => this.$refs.listFormRef && this.$refs.listFormRef.resetValidation())
    },
    ensureBoardProject () {
      if (!this.isAdmin && this.projects.length === 1) {
        this.boardProjectId = this.projects[0].id
      }

      if (this.isClient) {
        if (this.clientProjectContextId !== 'all' && !this.projects.some((project) => project.id === this.clientProjectContextId)) {
          this.clientProjectContextId = 'all'
        }
        this.boardProjectId = this.clientProjectContextId
        this.requestFilters.projectId = this.clientProjectContextId
      }

      if (this.isAdmin && this.boardProjectId !== 'all' && !this.projects.some((project) => project.id === this.boardProjectId)) {
        this.boardProjectId = 'all'
      }

      if (!this.listForm.projectId && this.projects[0]) {
        this.listForm.projectId = this.selectedBoardProjectId || this.projects[0].id
      }
    },
    openDemandModal ({ lockProjectId = '' } = {}) {
      if (!this.isStaff) return
      if (lockProjectId) {
        this.form.projectId = lockProjectId
        this.applyProjectDefaults()
        this.demandFormProjectLocked = true
      } else {
        this.demandFormProjectLocked = false
        if (!this.form.projectId && this.projects.length) {
          this.form.projectId = this.projects.length === 1 ? this.projects[0].id : (this.selectedBoardProjectId || this.projects[0].id)
          this.applyProjectDefaults()
        }
      }
      this.demandDialog = true
      this.$nextTick(() => this.$refs.form && this.$refs.form.resetValidation())
    },
    openBulkDemandModal () {
      if (!this.isStaff) return
      const projectId = this.selectedBoardProjectId || (this.projects.length === 1 ? this.projects[0].id : '') || (this.projects[0]?.id || '')
      const defaultStatus = this.statusMoveOptions[0] || 'Pendente'
      this.bulkDemandForm = {
        projectId,
        defaultStatus,
        defaultPriority: 2,
        defaultOwnerUserId: '',
        rawText: '',
        items: []
      }
      this.bulkDemandDialog = true
      this.$nextTick(() => this.$refs.bulkDemandFormRef && this.$refs.bulkDemandFormRef.resetValidation())
    },
    resetBulkDemandSuggestions () {
      if (!this.devUserBelongsToProject(this.bulkDemandForm.defaultOwnerUserId, this.bulkDemandForm.projectId)) {
        this.bulkDemandForm.defaultOwnerUserId = ''
      }
      this.bulkDemandForm.items = this.bulkDemandForm.items.map((item) => ({
        ...item,
        ownerUserId: this.bulkDemandForm.defaultOwnerUserId
      }))
    },
    normalizeBulkDemandLine (line) {
      return String(line || '')
        .trim()
        .replace(/^(\s*[-*•‣▪▫]+|\s*\d+[\).\-\s]+|\s*\[[ xX]\]\s*|\s*☐\s*|\s*✅\s*)/u, '')
        .replace(/\s+/g, ' ')
        .trim()
    },
    suggestBulkDemands () {
      const seen = new Set()
      const items = String(this.bulkDemandForm.rawText || '')
        .split(/\r?\n/)
        .map((line) => this.normalizeBulkDemandLine(line))
        .filter((title) => {
          const key = title.toLowerCase()
          if (!title || seen.has(key)) return false
          seen.add(key)
          return true
        })
        .map((title, index) => ({
          key: `${Date.now()}-${index}`,
          title,
          status: this.bulkDemandForm.defaultStatus || 'Pendente',
          priority: this.bulkDemandForm.defaultPriority || 2,
          ownerUserId: this.bulkDemandForm.defaultOwnerUserId || ''
        }))
      this.bulkDemandForm.items = items
    },
    clearBulkDemandSuggestions () {
      this.bulkDemandForm.items = []
    },
    removeBulkDemand (index) {
      this.bulkDemandForm.items.splice(index, 1)
    },
    openRequestModal () {
      if (this.selectedClientProjectId) {
        this.requestForm.projectId = this.selectedClientProjectId
      } else if (!this.requestForm.projectId && this.projects.length) {
        this.requestForm.projectId = this.projects[0].id
      }
      this.requestDialog = true
      this.$nextTick(() => this.$refs.requestFormRef && this.$refs.requestFormRef.resetValidation())
    },
    async createRequest () {
      if (!this.$refs.requestFormRef.validate()) return
      this.savingRequest = true
      this.error = ''
      try {
        const created = await api.createRequest(this.requestForm)
        this.requestForm = { projectId: '', title: '', priority: 2, description: '' }
        this.requestDialog = false
        await this.loadRequests()
        await this.openRequestDetails(created)
      } catch (error) {
        this.error = this.errorMessage(error)
      } finally {
        this.savingRequest = false
      }
    },
    async openRequestDetails (request) {
      try {
        const detail = await api.getRequest(request.id)
        this.selectedRequest = detail
        this.requestDetailForm = {
          id: detail.id,
          title: detail.title || '',
          description: detail.description || '',
          status: detail.status || 'Aberta',
          priority: this.priorityValue(detail.priority),
          hours: detail.hours || 0,
          linkDemandId: ''
        }
        if (detail.projectId && detail.projectId !== (this.selectedClientProjectId || this.selectedBoardProjectId)) {
          this.summaryDemands = await api.listDemands({ projectId: detail.projectId })
        }
        this.requestMessageDraft = ''
        this.requestMessageAttachments = []
        this.requestDetailDialog = true
        realtime.joinRequest(detail.id)
        try {
          this.requestMessages = await api.listRequestMessages(detail.id)
        } catch (error) {
          this.requestMessages = []
          this.error = this.errorMessage(error)
        }
      } catch (error) {
        this.error = this.errorMessage(error)
      }
    },
    closeRequestDetails () {
      this.requestDetailDialog = false
      this.selectedRequest = null
      this.requestMessages = []
      this.requestMessageDraft = ''
      this.requestMessageAttachments = []
    },
    async saveRequestDetails () {
      if (!this.isStaff || !this.selectedRequest) return
      this.savingRequest = true
      try {
        const { linkDemandId, ...payload } = this.requestDetailForm
        const updated = await api.updateRequest(this.selectedRequest.id, payload)
        this.selectedRequest = updated
        this.requestDetailForm.linkDemandId = ''
        await this.loadRequests()
      } catch (error) {
        this.error = this.errorMessage(error)
      } finally {
        this.savingRequest = false
      }
    },
    async sendRequestMessage (nextStatus = '') {
      const text = String(this.requestMessageDraft || '').trim()
      const attachments = this.requestMessageAttachments.map((attachment) => ({ ...attachment }))
      if ((!text && !attachments.length) || !this.selectedRequest) return
      this.sendingRequestMessage = true
      this.requestMessageDraft = ''
      this.requestMessageAttachments = []
      const payload = { text, attachments }

      const sent = realtime.sendRequestMessage(this.selectedRequest.id, payload, async (result) => {
        if (result && result.ok) {
          try {
            await this.updateRequestStatusAfterReply(nextStatus)
          } catch (error) {
            this.error = this.errorMessage(error)
          } finally {
            this.sendingRequestMessage = false
          }
          return
        }
        this.requestMessageDraft = text
        this.requestMessageAttachments = attachments
        this.error = (result && result.message) || 'Não foi possível enviar a mensagem.'
        this.sendingRequestMessage = false
      })

      if (sent) return

      let messageCreated = false
      try {
        const message = await api.createRequestMessage(this.selectedRequest.id, payload)
        messageCreated = true
        this.addRequestMessage(message)
        await this.updateRequestStatusAfterReply(nextStatus)
      } catch (error) {
        if (!messageCreated) {
          this.requestMessageDraft = text
          this.requestMessageAttachments = attachments
        }
        this.error = this.errorMessage(error)
      } finally {
        this.sendingRequestMessage = false
      }
    },
    async updateRequestStatusAfterReply (status) {
      if (!status || !this.isStaff || !this.selectedRequest) return
      const { linkDemandId, ...requestForm } = this.requestDetailForm
      const payload = { ...requestForm, status }
      const updated = await api.updateRequest(this.selectedRequest.id, payload)
      this.requestDetailForm.status = updated.status || status
      this.selectedRequest = updated
      await this.loadRequests()
    },
    async createDemandForRequest (request = null) {
      if (!this.isStaff) return
      const source = request && request.id ? request : this.selectedRequest
      if (!source || !source.id) return

      this.savingRequest = true
      this.error = ''
      let detail = source
      try {
        detail = source.demands !== undefined ? source : await api.getRequest(source.id)
        this.selectedRequest = detail
        this.requestDetailForm = {
          id: detail.id,
          title: detail.title || '',
          description: detail.description || '',
          status: detail.status || 'Aberta',
          priority: this.priorityValue(detail.priority),
          hours: detail.hours || 0,
          linkDemandId: ''
        }
      } catch (error) {
        this.error = this.errorMessage(error)
        return
      } finally {
        this.savingRequest = false
      }

      const noteParts = [`Criada a partir da solicitação ${detail.displayId}`]
      if (detail.description) noteParts.push(`Contexto: ${detail.description}`)
      this.form = {
        projectId: detail.projectId,
        requestId: detail.id,
        title: detail.title,
        company: detail.projectName,
        contractType: 'Demandesk',
        priority: this.priorityValue(detail.priority),
        owner: '',
        ownerUserId: '',
        requesterUserId: detail.createdByRole === 'client' ? detail.createdByUserId : '',
        dueDate: '',
        status: 'Pendente',
        note: noteParts.join('\n\n')
      }
      this.applyProjectDefaults()
      this.requestDetailDialog = false
      this.boardProjectId = detail.projectId || this.boardProjectId
      this.demandDialog = true
      this.$nextTick(() => this.$refs.form && this.$refs.form.resetValidation())
    },
    async linkExistingDemandToRequest () {
      if (!this.isStaff || !this.selectedRequest || !this.requestDetailForm.linkDemandId) return
      this.savingRequest = true
      this.error = ''
      const requestId = this.selectedRequest.id
      try {
        const updated = await api.linkRequestDemand(requestId, this.requestDetailForm.linkDemandId)
        this.requestDetailForm.linkDemandId = ''
        this.selectedRequest = updated
        await Promise.all([this.loadRequests(), this.loadDemands(), this.loadDemandSummaryScope()])
        if (this.selectedRequest && this.selectedRequest.id === requestId) {
          this.selectedRequest = await api.getRequest(requestId)
        }
      } catch (error) {
        this.error = this.errorMessage(error)
      } finally {
        this.savingRequest = false
      }
    },
    openProjectModal () {
      if (!this.isAdmin) return
      this.projectDialog = true
      this.$nextTick(() => {
        if (this.$refs.projectFormRef) this.$refs.projectFormRef.resetValidation()
        if (this.$refs.projectNameInput) this.$refs.projectNameInput.focus()
      })
    },
    openUserModal (user = null) {
      if (!this.isAdmin) return
      if (user && user.id) {
        this.editingUserId = user.id
        this.userForm = {
          name: user.name || '',
          username: user.username || '',
          email: user.email || '',
          password: '',
          role: user.role || 'client',
          projectIds: user.role === 'admin' ? [] : [...(user.projectIds || [])],
          active: user.active !== false
        }
      } else {
        this.editingUserId = ''
        this.userForm = { name: '', username: '', email: '', password: '', role: 'client', projectIds: [], active: true }
      }
      this.userDialog = true
      this.$nextTick(() => this.$refs.userFormRef && this.$refs.userFormRef.resetValidation())
    },
    closeUserModal () {
      this.userDialog = false
      this.editingUserId = ''
      this.userForm = { name: '', username: '', email: '', password: '', role: 'client', projectIds: [], active: true }
    },
    prepareDemandForProject (project) {
      if (!this.isStaff || !project) return
      this.openDemandModal({ lockProjectId: project.id })
    },
    openDemandModalForColumn (column) {
      if (!this.isStaff) return
      const projectId = this.selectedBoardProjectId || (this.projects.length === 1 ? this.projects[0].id : '')
      this.form = {
        projectId,
        requestId: '',
        title: '',
        company: '',
        contractType: 'Demandesk',
        priority: 2,
        status: column.status || 'Pendente',
        owner: '',
        ownerUserId: '',
        requesterUserId: '',
        dueDate: '',
        note: ''
      }
      this.demandFormProjectLocked = Boolean(projectId)
      this.applyProjectDefaults()
      this.demandDialog = true
      this.$nextTick(() => this.$refs.form && this.$refs.form.resetValidation())
    },
    openInlineDemandComposer (column) {
      if (!this.isStaff) return
      const projectId = this.selectedBoardProjectId || (this.projects.length === 1 ? this.projects[0].id : '')
      if (!projectId) {
        this.error = 'Selecione um projeto para adicionar uma demanda rápida.'
        return
      }
      this.error = ''
      this.inlineDemandComposer = { status: column.status || 'Pendente', title: '' }
      this.$nextTick(() => {
        const ref = this.$refs[`inlineDemandTitle-${this.inlineDemandComposer.status}`]
        const input = Array.isArray(ref) ? ref[0] : ref
        if (input) input.focus()
      })
    },
    closeInlineDemandComposer () {
      this.inlineDemandComposer = { status: '', title: '', totalHours: '', dueDate: '' }
    },
    async createInlineDemand (column) {
      if (!this.isStaff || this.savingInlineDemand) return
      const title = String(this.inlineDemandComposer.title || '').trim()
      if (!title) return

      const projectId = this.selectedBoardProjectId || (this.projects.length === 1 ? this.projects[0].id : '')
      const project = this.projects.find((item) => item.id === projectId)
      if (!project) {
        this.error = 'Selecione um projeto para adicionar uma demanda rápida.'
        return
      }

      this.savingInlineDemand = true
      this.error = ''
      try {
        const created = await api.createDemand({
          projectId: project.id,
          title,
          company: project.name,
          contractType: project.contractType,
          priority: 2,
          status: column.status || this.inlineDemandComposer.status || 'Pendente',
          sortOrder: 0,
          owner: '',
          ownerUserId: '',
          dueDate: this.inlineDemandComposer.dueDate || '',
          totalHours: this.inlineDemandComposer.totalHours || '',
          note: ''
        })
        this.inlineDemandComposer = { status: column.status || 'Pendente', title: '', totalHours: '', dueDate: '' }
        this.reconcileDemand(created)
        await this.loadSummary()
        this.$nextTick(() => {
          const ref = this.$refs[`inlineDemandTitle-${this.inlineDemandComposer.status}`]
          const input = Array.isArray(ref) ? ref[0] : ref
          if (input) input.focus()
        })
      } catch (error) {
        this.error = this.errorMessage(error)
      } finally {
        this.savingInlineDemand = false
      }
    },
    async selectBoardProject (projectId) {
      this.boardProjectId = projectId || 'all'
      this.activateWorkspaceView('demands')
      await this.changeBoardProject()
    },
    async createDemand () {
      if (!this.isStaff || !this.$refs.form.validate()) return
      this.saving = true
      this.error = ''
      try {
        const project = this.selectedProject()
        await api.createDemand({ ...this.form, sortOrder: 0, owner: this.selectedDevName(this.form.ownerUserId), company: project.name, contractType: project.contractType })
        const linkedRequestId = this.form.requestId
        this.form = { projectId: '', requestId: '', title: '', company: '', contractType: 'Demandesk', priority: 2, status: 'Pendente', owner: '', ownerUserId: '', requesterUserId: '', dueDate: '', note: '' }
        this.demandDialog = false
        await this.loadAll()
        if (linkedRequestId && this.selectedRequest) {
          this.selectedRequest = await api.getRequest(linkedRequestId)
        }
      } catch (error) {
        this.error = this.errorMessage(error)
      } finally {
        this.saving = false
      }
    },
    async createBulkDemands () {
      if (!this.isStaff || !this.$refs.bulkDemandFormRef.validate()) return
      const project = this.projects.find((item) => item.id === this.bulkDemandForm.projectId)
      if (!project) {
        this.error = 'Selecione um projeto para criar as tarefas.'
        return
      }

      const items = this.bulkDemandForm.items
        .map((item) => ({ ...item, title: String(item.title || '').trim() }))
        .filter((item) => item.title)

      if (!items.length) {
        this.error = 'Revise a lista e mantenha pelo menos uma tarefa com título.'
        return
      }

      this.savingBulkDemands = true
      this.error = ''
      try {
        for (const item of items) {
          await api.createDemand({
            projectId: project.id,
            title: item.title,
            company: project.name,
            contractType: project.contractType,
            priority: item.priority || this.bulkDemandForm.defaultPriority || 2,
            status: item.status || this.bulkDemandForm.defaultStatus || 'Pendente',
            ownerUserId: item.ownerUserId || this.bulkDemandForm.defaultOwnerUserId || '',
            owner: this.selectedDevName(item.ownerUserId || this.bulkDemandForm.defaultOwnerUserId),
            dueDate: '',
            note: ''
          })
        }
        this.bulkDemandDialog = false
        this.bulkDemandForm = { projectId: '', defaultStatus: 'Pendente', defaultPriority: 2, defaultOwnerUserId: '', rawText: '', items: [] }
        if (this.boardProjectId === 'all') this.boardProjectId = project.id
        await this.loadAll()
      } catch (error) {
        this.error = this.errorMessage(error)
      } finally {
        this.savingBulkDemands = false
      }
    },
    openBulkDraftModal () {
      this.bulkDraftForm = { projectId: this.boardProjectId !== 'all' ? this.boardProjectId : '', defaultPriority: 2, rawText: '', items: [] }
      this.bulkDraftDialog = true
    },
    suggestBulkDrafts () {
      const lines = this.bulkDraftForm.rawText.split(/\r?\n/).map((l) => this.normalizeBulkDemandLine(l)).filter(Boolean)
      const seen = new Set()
      this.bulkDraftForm.items = lines
        .filter((title) => { const key = title.toLowerCase(); if (seen.has(key)) return false; seen.add(key); return true })
        .map((title, i) => ({ key: `draft-${i}-${Date.now()}`, title, priority: this.bulkDraftForm.defaultPriority || 2 }))
    },
    async createBulkDrafts () {
      if (!this.isAdmin) return
      const project = this.projects.find((p) => p.id === this.bulkDraftForm.projectId)
      if (!project) { this.error = 'Selecione um projeto.'; return }
      const items = this.bulkDraftForm.items.filter((i) => String(i.title || '').trim())
      if (!items.length) return
      this.savingBulkDrafts = true
      this.error = ''
      try {
        for (const item of items) {
          await api.createDemandDraft({
            projectId: project.id,
            title: String(item.title).trim(),
            contractType: project.contractType || 'Demandesk',
            priority: item.priority || 2,
          })
        }
        this.bulkDraftDialog = false
        this.bulkDraftForm = { projectId: '', defaultPriority: 2, rawText: '', items: [] }
        await this.loadDrafts()
      } catch (error) {
        this.error = this.errorMessage(error)
      } finally {
        this.savingBulkDrafts = false
      }
    },
    async loadDrafts () {
      if (!this.isAdmin) return
      this.loadingDrafts = true
      try {
        const drafts = await api.listDemandDrafts()
        this.demandDrafts = drafts.map((d) => ({ ...d, _editing: false, _editTitle: d.title, _publishStatus: '', _publishing: false }))
      } catch (error) {
        this.error = this.errorMessage(error)
      } finally {
        this.loadingDrafts = false
      }
    },
    startDraftEdit (draft) {
      draft._editTitle = draft.title
      draft._editing = true
    },
    cancelDraftEdit (draft) {
      draft._editing = false
    },
    async saveDraftEdit (draft) {
      const title = String(draft._editTitle || '').trim()
      if (!title) return
      try {
        await api.updateDemandDraft(draft.id, { title })
        draft.title = title
        draft._editing = false
      } catch (error) {
        this.error = this.errorMessage(error)
      }
    },
    async publishDraft (draft) {
      const status = draft._publishStatus || 'Pendente'
      draft._publishing = true
      try {
        const project = this.projects.find((p) => p.id === draft.projectId)
        await api.createDemand({
          projectId: draft.projectId,
          title: draft.title,
          company: project ? project.name : draft.company || '',
          contractType: draft.contractType || 'Demandesk',
          priority: this.priorityValue(draft.priority),
          status,
          owner: draft.owner || '',
          ownerUserId: draft.ownerUserId || '',
          dueDate: draft.dueDate || '',
          description: draft.description || '',
          attachments: draft.attachments || [],
          totalHours: draft.totalHours || '',
        })
        await api.deleteDemandDraft(draft.id)
        this.demandDrafts = this.demandDrafts.filter((d) => d.id !== draft.id)
        await this.loadAll()
      } catch (error) {
        this.error = this.errorMessage(error)
      } finally {
        draft._publishing = false
      }
    },
    async deleteDraft (draft) {
      try {
        await api.deleteDemandDraft(draft.id)
        this.demandDrafts = this.demandDrafts.filter((d) => d.id !== draft.id)
      } catch (error) {
        this.error = this.errorMessage(error)
      }
    },
    async createProjectList () {
      if (!this.isAdmin || !this.$refs.listFormRef.validate()) return
      const projectId = this.listForm.projectId || (this.projects[0] && this.projects[0].id) || ''
      if (!projectId) {
        this.error = 'Selecione um projeto para criar a lista.'
        return
      }

      this.savingList = true
      this.error = ''
      try {
        await api.createProjectList(projectId, { ...this.listForm, projectId })
        this.boardProjectId = projectId
        this.listForm = { projectId: this.boardProjectId, name: '', description: '', color: '#00f0ff' }
        this.listDialog = false
        await this.changeBoardProject()
      } catch (error) {
        this.error = this.errorMessage(error)
      } finally {
        this.savingList = false
      }
    },
    openDemandDetails (demand) {
      this.selectedDemand = demand
      this.demandSourceRequest = null
      this.demandSourceAttachments = []
      this.loadingDemandSourceAttachments = false
      this.detailForm = {
        id: demand.id,
        projectId: demand.projectId || '',
        title: demand.title || '',
        company: demand.company || '',
        contractType: demand.contractType || 'Demandesk',
        priority: this.priorityValue(demand.priority),
        status: demand.status || 'Pendente',
        owner: demand.owner || '',
        ownerUserId: demand.ownerUserId || '',
        requesterUserId: demand.requesterUserId || '',
        dueDate: demand.dueDate || '',
        description: demand.description || '',
        totalHours: demand.totalHours || '',
        attachments: (demand.attachments || []).map((attachment) => ({ ...attachment })),
        linkRequestId: ''
      }
      this.detailDialog = true
      this.loadDemandLinkRequests(demand.projectId, demand.id)
      if (demand.requestId) this.loadDemandSourceAttachments(demand.requestId, demand.id)
      this.$nextTick(() => this.$refs.detailFormRef && this.$refs.detailFormRef.resetValidation())
    },
    async openArchiveDialog () {
      if (!this.isStaff) return
      this.archiveSearch = ''
      this.archiveError = ''
      this.archiveDialog = true
      await this.loadArchivedDemands()
    },
    async loadArchivedDemands () {
      this.loadingArchived = true
      this.archiveError = ''
      try {
        this.archivedDemands = await api.listDemands({
          archived: 'only',
          ...(this.selectedBoardProjectId ? { projectId: this.selectedBoardProjectId } : {})
        })
      } catch (error) {
        this.archiveError = this.errorMessage(error)
      } finally {
        this.loadingArchived = false
      }
    },
    openArchivedDemandDetails (demand) {
      this.archiveDialog = false
      this.openDemandDetails(demand)
    },
    async setDemandArchived (demand, archived) {
      if (!this.isStaff || !demand || this.archivingDemandId) return
      this.archivingDemandId = demand.id
      this.error = ''
      try {
        const updated = await api.setDemandArchived(demand.id, archived)
        if (this.selectedDemand && this.selectedDemand.id === demand.id) this.closeDemandDetails()
        this.demands = this.demands.filter((item) => item.id !== demand.id)
        this.archivedDemands = archived
          ? [updated, ...this.archivedDemands.filter((item) => item.id !== demand.id)]
          : this.archivedDemands.filter((item) => item.id !== demand.id)
        await Promise.all([this.loadDemands(), this.loadDemandSummaryScope(), this.loadSummary()])
        if (this.activeView === 'gantt') await this.refreshScheduleDemands()
      } catch (error) {
        this.error = this.errorMessage(error)
      } finally {
        this.archivingDemandId = ''
      }
    },
    closeDemandDetails () {
      this.detailDialog = false
      this.selectedDemand = null
      this.demandSourceRequest = null
      this.demandSourceAttachments = []
      this.loadingDemandSourceAttachments = false
      this.demandLinkRequests = []
    },
    async loadDemandLinkRequests (projectId, demandId) {
      if (!projectId) {
        this.demandLinkRequests = []
        return
      }

      try {
        const requests = await api.listRequests({ projectId })
        if (!this.selectedDemand || this.selectedDemand.id !== demandId) return
        this.demandLinkRequests = requests
      } catch (error) {
        if (this.selectedDemand && this.selectedDemand.id === demandId) {
          this.demandLinkRequests = []
          this.error = this.errorMessage(error)
        }
      }
    },
    async loadDemandSourceAttachments (requestId, demandId) {
      this.loadingDemandSourceAttachments = true
      try {
        const [request, messages] = await Promise.all([
          api.getRequest(requestId),
          api.listRequestMessages(requestId)
        ])
        if (!this.selectedDemand || this.selectedDemand.id !== demandId) return
        this.demandSourceRequest = request
        this.demandSourceAttachments = messages.flatMap((message) => {
          return (message.attachments || []).map((attachment) => ({
            ...attachment,
            sourceId: `${message.id}-${attachment.id}`,
            authorName: message.authorName || 'Usuário',
            createdAt: message.createdAt
          }))
        })
      } catch (error) {
        if (this.selectedDemand && this.selectedDemand.id === demandId) {
          this.demandSourceAttachments = []
          this.error = this.errorMessage(error)
        }
      } finally {
        if (this.selectedDemand && this.selectedDemand.id === demandId) {
          this.loadingDemandSourceAttachments = false
        }
      }
    },
    async openLinkedRequestFromDemand (demand) {
      if (!demand || !demand.requestId) return
      this.detailDialog = false
      this.activateWorkspaceView('requests')
      await this.openRequestDetails({ id: demand.requestId })
    },
    async openLinkedDemandFromRequest (demand) {
      if (!demand || !demand.id) return
      try {
        const detail = await api.getDemand(demand.id)
        this.requestDetailDialog = false
        this.activateWorkspaceView('demands')
        this.openDemandDetails(detail)
      } catch (error) {
        this.error = this.errorMessage(error)
      }
    },
    async saveDemandDetails () {
      if (!this.isStaff || !this.selectedDemand || !this.$refs.detailFormRef.validate()) return
      this.savingDetail = true
      this.error = ''
      try {
        const project = this.projects.find((item) => item.id === this.detailForm.projectId) || {}
        const { linkRequestId, ...detailPayload } = this.detailForm
        const updated = await api.updateDemand(this.selectedDemand.id, {
          ...detailPayload,
          owner: this.selectedDevName(this.detailForm.ownerUserId),
          company: project.name || this.detailForm.company,
          contractType: project.contractType || this.detailForm.contractType
        })
        await this.loadAll()
        this.closeDemandDetails()
      } catch (error) {
        this.error = this.errorMessage(error)
      } finally {
        this.savingDetail = false
      }
    },
    async linkExistingRequestToDemand () {
      if (!this.isStaff || !this.selectedDemand || !this.detailForm.linkRequestId) return
      this.savingDetail = true
      this.error = ''
      const demandId = this.selectedDemand.id
      try {
        await api.linkRequestDemand(this.detailForm.linkRequestId, demandId)
        this.detailForm.linkRequestId = ''
        await Promise.all([this.loadRequests(), this.loadDemands(), this.loadDemandSummaryScope()])
        const updated = await api.getDemand(demandId)
        this.openDemandDetails(updated)
      } catch (error) {
        this.error = this.errorMessage(error)
      } finally {
        this.savingDetail = false
      }
    },
    applyDetailProjectDefaults () {
      const project = this.projects.find((item) => item.id === this.detailForm.projectId)
      if (!project) return
      this.detailForm.company = project.name
      this.detailForm.contractType = project.contractType
      if (!this.devUserBelongsToProject(this.detailForm.ownerUserId, this.detailForm.projectId)) {
        this.detailForm.ownerUserId = ''
        this.detailForm.owner = ''
      }
      this.detailForm.requesterUserId = ''
      this.detailForm.linkRequestId = ''
      if (this.selectedDemand) this.loadDemandLinkRequests(this.detailForm.projectId, this.selectedDemand.id)
    },
    openAttachmentPicker () {
      if (!this.isStaff || !this.$refs.attachmentInput) return
      this.$refs.attachmentInput.click()
    },
    async addAttachmentFiles (event) {
      const files = Array.from(event.target.files || [])
      if (!files.length) return
      try {
        this.validateAttachmentSelection(this.detailForm.attachments, files)
        const attachments = await Promise.all(files.map((file) => this.fileToAttachment(file)))
        this.detailForm.attachments = [...this.detailForm.attachments, ...attachments]
      } catch (error) {
        this.error = this.errorMessage(error)
      } finally {
        event.target.value = ''
      }
    },
    fileToAttachment (file) {
      const allowedTypes = this.attachmentAccept.split(',')
      if (!allowedTypes.includes(file.type)) {
        return Promise.reject(new Error('Tipo de anexo não permitido.'))
      }
      if (file.size > 5 * 1024 * 1024) {
        return Promise.reject(new Error('Cada anexo pode ter no máximo 5 MB.'))
      }

      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => {
          resolve({
            id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
            name: file.name,
            type: file.type,
            size: file.size,
            dataUrl: reader.result,
            createdAt: new Date().toISOString()
          })
        }
        reader.onerror = reject
        reader.readAsDataURL(file)
      })
    },
    validateAttachmentSelection (existing, files) {
      if (existing.length + files.length > 5) throw new Error('Use no máximo 5 anexos.')
      const total = [...existing, ...files].reduce((size, item) => size + Number(item.size || 0), 0)
      if (total > 10 * 1024 * 1024) throw new Error('Os anexos podem somar no máximo 10 MB.')
    },
    removeAttachment (attachmentId) {
      this.detailForm.attachments = this.detailForm.attachments.filter((attachment) => attachment.id !== attachmentId)
    },
    openRequestAttachmentPicker () {
      if (!this.$refs.requestAttachmentInput) return
      this.$refs.requestAttachmentInput.click()
    },
    async addRequestAttachmentFiles (event) {
      const files = Array.from(event.target.files || [])
      if (!files.length) return
      try {
        this.validateAttachmentSelection(this.requestMessageAttachments, files)
        const attachments = await Promise.all(files.map((file) => this.fileToAttachment(file)))
        this.requestMessageAttachments = [...this.requestMessageAttachments, ...attachments]
      } catch (error) {
        this.error = this.errorMessage(error)
      } finally {
        event.target.value = ''
      }
    },
    removeRequestAttachment (attachmentId) {
      this.requestMessageAttachments = this.requestMessageAttachments.filter((attachment) => attachment.id !== attachmentId)
    },
    isImageAttachment (attachment) {
      return String(attachment.type || '').startsWith('image/') || String(attachment.dataUrl || '').startsWith('data:image/')
    },
    attachmentIcon (attachment) {
      const type = String(attachment.type || '')
      if (type.includes('pdf')) return 'PDF'
      if (type.includes('spreadsheet') || type.includes('excel') || /\.(xls|xlsx|csv)$/i.test(attachment.name || '')) return 'XLS'
      if (type.includes('word') || /\.(doc|docx)$/i.test(attachment.name || '')) return 'DOC'
      return 'FILE'
    },
    async createProject () {
      if (!this.isAdmin || !this.$refs.projectFormRef.validate()) return
      this.savingProject = true
      this.error = ''
      try {
        const project = await api.createProject(this.projectForm)
        this.projectForm = { name: '', contractType: 'Demandesk', owner: '', notes: '' }
        this.projectDialog = false
        await this.loadProjects()
        this.form.projectId = project.id
        this.applyProjectDefaults()
      } catch (error) {
        this.error = this.errorMessage(error)
      } finally {
        this.savingProject = false
      }
    },
    async saveUser () {
      if (!this.isAdmin || !this.$refs.userFormRef.validate()) return
      this.savingUser = true
      this.error = ''
      try {
        const payload = {
          ...this.userForm,
          username: this.cleanUsername(this.userForm.username),
          projectIds: this.userForm.role === 'admin' ? [] : this.normalizedUserProjectIds()
        }
        if (this.editingUserId) {
          await api.updateUser(this.editingUserId, payload)
        } else {
          await api.createUser(payload)
        }
        this.closeUserModal()
        await this.loadUsers()
      } catch (error) {
        this.error = this.errorMessage(error)
      } finally {
        this.savingUser = false
      }
    },
    async toggleUserActive (user) {
      if (!this.isAdmin || !user || user.id === this.user.id) return
      this.savingUser = true
      this.error = ''
      try {
        await api.updateUser(user.id, {
          name: user.name,
          username: user.username,
          email: user.email,
          role: user.role,
          projectIds: user.role === 'admin' ? [] : user.projectIds || [],
          active: !user.active
        })
        await this.loadUsers()
      } catch (error) {
        this.error = this.errorMessage(error)
      } finally {
        this.savingUser = false
      }
    },
    openInviteModal () {
      if (!this.isAdmin) return
      this.generatedInviteUrl = ''
      this.clientInviteForm = { projectId: this.projects[0]?.id || '' }
      this.inviteDialog = true
      this.$nextTick(() => this.$refs.inviteFormRef && this.$refs.inviteFormRef.resetValidation())
    },
    async createInvite () {
      if (!this.isAdmin) return
      if (!this.$refs.inviteFormRef.validate()) return
      this.savingInvite = true
      this.error = ''
      try {
        const invite = await api.createClientInvite(this.clientInviteForm)
        this.generatedInviteUrl = invite.registerUrl
        await this.loadClientInvites()
        try {
          await navigator.clipboard.writeText(invite.registerUrl)
        } catch {}
      } catch (error) {
        this.error = this.errorMessage(error)
      } finally {
        this.savingInvite = false
      }
    },
    async copyInviteUrl (invite) {
      if (!invite || !invite.registerUrl) return
      try {
        await navigator.clipboard.writeText(invite.registerUrl)
      } catch (error) {
        this.error = this.errorMessage(error)
      }
    },
    async deleteInvite (invite) {
      if (!this.isAdmin || !invite) return
      this.savingInvite = true
      this.error = ''
      try {
        await api.deleteClientInvite(invite.id)
        if (this.generatedInviteUrl === invite.registerUrl) this.generatedInviteUrl = ''
        await this.loadClientInvites()
      } catch (error) {
        this.error = this.errorMessage(error)
      } finally {
        this.savingInvite = false
      }
    },
    inviteProjectLabel (invite) {
      const projectId = invite && invite.projectIds && invite.projectIds[0]
      const project = this.projects.find((item) => item.id === projectId)
      return project ? project.name : 'Projeto não encontrado'
    },
    openUserProjectsModal (user) {
      if (!this.isAdmin || !user) return
      this.selectedUserForProjects = user
      this.userProjectsForm.projectIds = [...(user.projectIds || [])]
      this.userProjectsDialog = true
    },
    async saveUserProjects () {
      if (!this.isAdmin || !this.selectedUserForProjects) return
      this.savingUserProjects = true
      this.error = ''
      try {
        await api.updateUserProjects(this.selectedUserForProjects.id, this.userProjectsForm.projectIds.filter(id => id !== '__all__'))
        this.userProjectsDialog = false
        await this.loadUsers()
      } catch (error) {
        this.error = this.errorMessage(error)
      } finally {
        this.savingUserProjects = false
      }
    },
    async copyUserEmail (user) {
      const email = user && user.email
      if (!email) return
      try {
        await navigator.clipboard.writeText(email)
      } catch {
        window.prompt('Copie o e-mail:', email)
      }
    },
    handleRealtimeMessage (message) {
      if (!this.selectedRequest || message.requestId !== this.selectedRequest.id) return
      this.addRequestMessage(message)
    },
    handleRealtimeRequest (request) {
      if (request.deletedAt || Object.prototype.hasOwnProperty.call(request, 'archivedAt')) {
        this.loadRequests()
        if (request.deletedAt && this.selectedRequest && this.selectedRequest.id === request.id) this.closeRequestDetails()
        if (request.deletedAt) return
      }
      const index = this.requests.findIndex((item) => item.id === request.id)
      if (index >= 0) this.$set(this.requests, index, { ...this.requests[index], ...request })
      if (this.selectedRequest && this.selectedRequest.id === request.id) {
        this.selectedRequest = { ...this.selectedRequest, ...request }
      }
    },
    addRequestMessage (message) {
      if (!message || this.requestMessages.some((item) => item.id === message.id)) return
      this.requestMessages.push(message)
    },
    normalizeUsername () {
      this.userForm.username = this.cleanUsername(this.userForm.username)
    },
    cleanUsername (value) {
      return String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/^@+/, '')
        .replace(/[^a-z0-9._-]+/g, '.')
        .replace(/^[._-]+|[._-]+$/g, '')
        .slice(0, 80)
    },
    normalizedUserProjectIds () {
      const value = this.userForm.projectIds
      const ids = Array.isArray(value) ? value : [value].filter(Boolean)
      return ids.filter(id => id !== '__all__')
    },
    normalizeUserProjectSelection () {
      if (this.userForm.role === 'admin') {
        this.userForm.projectIds = []
      } else {
        this.userForm.projectIds = this.normalizedUserProjectIds()
      }
    },
    handleProjectSelectAll (formKey) {
      const form = this[formKey]
      const allProjectIds = this.projects.map(p => p.id)
      if (form.projectIds.includes('__all__')) {
        const alreadyAll = form.projectIds.filter(id => id !== '__all__').length === allProjectIds.length
        form.projectIds = alreadyAll ? [] : allProjectIds
      }
    },
    selectedProject () {
      return this.projects.find((project) => project.id === this.form.projectId) || {}
    },
    applyProjectDefaults () {
      const project = this.selectedProject()
      if (!project.id) return
      this.form.company = project.name
      this.form.contractType = project.contractType
      if (!this.devUserBelongsToProject(this.form.ownerUserId, this.form.projectId)) {
        this.form.ownerUserId = ''
        this.form.owner = ''
      }
    },
    devOptionsForProject (projectId) {
      return this.users
        .filter((user) => user.role === 'dev' && this.devUserBelongsToProject(user.id, projectId))
        .map((user) => ({
          text: `${user.name} · @${user.username}`,
          value: user.id
        }))
    },
    devUserBelongsToProject (userId, projectId) {
      if (!userId || !projectId) return false
      const user = this.users.find((item) => item.id === userId)
      return Boolean(user && user.role === 'dev' && (user.projectIds || []).includes(projectId))
    },
    clientOptionsForProject (projectId) {
      if (!projectId) return []
      if (!this.projectClientsCache[projectId]) {
        this.loadProjectClients(projectId)
        return []
      }
      return this.projectClientsCache[projectId].map((user) => ({
        text: `${user.name} · @${user.username}`,
        value: user.id
      }))
    },
    async loadProjectClients (projectId) {
      if (!projectId || this.projectClientsCache[projectId]) return
      this.$set(this.projectClientsCache, projectId, [])
      try {
        const clients = await api.listProjectClients(projectId)
        this.$set(this.projectClientsCache, projectId, clients)
      } catch (error) {
        this.$delete(this.projectClientsCache, projectId)
      }
    },
    kanbanColumnClass (status) {
      const normalized = String(status || '').toLowerCase()
      if (normalized.includes('pendente')) return 'is-pending'
      if (normalized.includes('conclu')) return 'is-done'
      return 'is-progress'
    },
    selectedDevName (userId) {
      const user = this.users.find((item) => item.id === userId)
      return user ? user.name : ''
    },
    async updateDemand (demand, patch) {
      if (!this.isStaff) return
      const index = this.demands.findIndex((item) => item.id === demand.id)
      const previous = index >= 0 ? { ...this.demands[index] } : null
      const optimistic = { ...(previous || demand), ...patch }

      if (index >= 0) {
        this.$set(this.demands, index, optimistic)
      }

      try {
        const updated = await api.updateDemand(demand.id, patch)
        this.reconcileDemand(updated)
        await this.loadSummary()
        return updated
      } catch (error) {
        if (previous && index >= 0) {
          this.$set(this.demands, index, previous)
        }
        this.error = this.errorMessage(error)
      }
    },
    reconcileDemand (demand) {
      const index = this.demands.findIndex((item) => item.id === demand.id)
      const visible = this.demandMatchesCurrentView(demand)

      if (!visible && index >= 0) {
        this.demands.splice(index, 1)
        return
      }

      if (visible && index >= 0) {
        this.$set(this.demands, index, demand)
      } else if (visible) {
        this.demands.unshift(demand)
      }
    },
    demandMatchesCurrentView (demand) {
      if (this.selectedBoardProjectId && demand.projectId !== this.selectedBoardProjectId) return false
      if (this.filters.status !== 'all' && demand.status !== this.filters.status) return false
      if (this.filters.priority !== 'all' && this.priorityValue(demand.priority) !== this.priorityValue(this.filters.priority)) return false
      if (!this.demandMatchesDateFilter(demand, this.filters)) return false

      const search = String(this.filters.search || '').trim().toLowerCase()
      if (!search) return true

      return [
        demand.title,
        demand.company,
        demand.contractType,
        this.priorityLabel(demand.priority),
        demand.status,
        demand.owner,
        demand.description,
        ...(demand.notes || []).map((note) => note.text)
      ].some((value) => String(value || '').toLowerCase().includes(search))
    },
    async moveDemand (demand, status) {
      if (demand.status === status) return
      await this.reorderDemand(demand, status, null, 'end')
    },
    columnMoveTargets (column) {
      return this.statusMoveOptions.filter((status) => status !== column.status)
    },
    async moveColumnDemands (column, status) {
      if (!this.isStaff || !column || column.status === status || this.movingColumnStatus) return
      const sourceItems = [...(column.items || [])]
      if (!sourceItems.length) return

      this.movingColumnStatus = column.status
      this.error = ''
      const previous = this.demands.map((item) => ({ ...item }))
      const sourceIds = new Set(sourceItems.map((item) => item.id))
      const existingTargetItems = this.demands
        .filter((item) => item.status === status && !sourceIds.has(item.id))
        .sort((a, b) => this.demandSortOrder(a) - this.demandSortOrder(b))
      const updates = sourceItems
        .sort((a, b) => this.compareDemandsInColumn(a, b))
        .map((demand, index) => ({
          id: demand.id,
          status,
          sortOrder: (existingTargetItems.length + index + 1) * 1000,
        }))

      updates.forEach((patch) => {
        const index = this.demands.findIndex((item) => item.id === patch.id)
        if (index >= 0) this.$set(this.demands, index, { ...this.demands[index], ...patch })
      })

      try {
        await Promise.all(
          updates.map((patch) =>
            api.updateDemand(patch.id, { status: patch.status, sortOrder: patch.sortOrder })
          )
        )
        await this.loadAll()
      } catch (error) {
        this.demands = previous
        this.error = this.errorMessage(error)
      } finally {
        this.movingColumnStatus = ''
      }
    },
    startDraggingColumn (column, event) {
      if (!this.isAdmin || !this.selectedBoardProjectId || this.savingColumnOrder) {
        event.preventDefault()
        return
      }
      this.draggingDemandId = null
      this.draggingColumnStatus = column.status
      event.dataTransfer.effectAllowed = 'move'
      event.dataTransfer.setData('text/plain', column.status)
    },
    stopDraggingColumn () {
      this.draggingColumnStatus = ''
      this.columnDropTargetStatus = ''
      this.columnDropPlacement = ''
    },
    handleColumnDragOver (column, event) {
      if (!this.draggingColumnStatus || this.draggingColumnStatus === column.status) return
      const rect = event.currentTarget.getBoundingClientRect()
      this.columnDropTargetStatus = column.status
      this.columnDropPlacement = event.clientX < rect.left + rect.width / 2 ? 'before' : 'after'
      event.dataTransfer.dropEffect = 'move'
    },
    leaveColumnDragTarget (column, event) {
      if (column.status !== this.columnDropTargetStatus) return
      if (event.relatedTarget && event.currentTarget.contains(event.relatedTarget)) return
      this.columnDropTargetStatus = ''
      this.columnDropPlacement = ''
    },
    dropKanbanItem (column, targetDemand, event) {
      if (this.draggingColumnStatus) {
        const sourceStatus = this.draggingColumnStatus
        const placement = this.columnDropPlacement || 'before'
        this.stopDraggingColumn()
        this.reorderColumn(sourceStatus, column.status, placement)
        return
      }
      this.dropDemand(column.status, targetDemand, event)
    },
    moveColumnBy (column, direction) {
      const index = this.kanbanColumns.findIndex((item) => item.status === column.status)
      const target = this.kanbanColumns[index + direction]
      if (target) this.reorderColumn(column.status, target.status, direction < 0 ? 'before' : 'after')
    },
    async reorderColumn (sourceStatus, targetStatus, placement) {
      const projectId = this.selectedBoardProjectId
      if (!this.isAdmin || !projectId || this.savingColumnOrder || sourceStatus === targetStatus) return

      const previous = [...this.demandLists]
      const reordered = [...previous]
      const sourceIndex = reordered.findIndex((list) => list.name === sourceStatus)
      if (sourceIndex < 0) return
      const [source] = reordered.splice(sourceIndex, 1)
      const targetIndex = reordered.findIndex((list) => list.name === targetStatus)
      if (targetIndex < 0) return
      reordered.splice(targetIndex + (placement === 'after' ? 1 : 0), 0, source)
      if (reordered.every((list, index) => list.name === previous[index].name)) return

      this.demandLists = reordered
      this.savingColumnOrder = true
      this.error = ''
      try {
        const saved = await api.reorderProjectLists(projectId, reordered.map((list) => list.name))
        if (this.selectedBoardProjectId === projectId) this.demandLists = saved
      } catch (error) {
        if (this.selectedBoardProjectId === projectId) {
          this.demandLists = previous
          this.error = this.errorMessage(error)
        }
      } finally {
        this.savingColumnOrder = false
      }
    },
    startDragging (demand) {
      if (!this.isStaff) return
      this.stopDraggingColumn()
      this.draggingDemandId = demand.id
    },
    stopDragging () {
      this.draggingDemandId = null
    },
    async dropDemand (status, targetDemand = null, event = null) {
      if (!this.isStaff || !this.draggingDemandId) return
      const demand = this.demands.find((item) => item.id === this.draggingDemandId)
      this.stopDragging()
      if (!demand) return
      if (targetDemand && targetDemand.id === demand.id) return

      const placement = targetDemand ? this.dropPlacement(event) : 'end'
      await this.reorderDemand(demand, status, targetDemand, placement)
    },
    dropPlacement (event) {
      const rect = event && event.currentTarget && event.currentTarget.getBoundingClientRect()
      if (!rect) return 'end'
      return event.clientY < rect.top + rect.height / 2 ? 'before' : 'after'
    },
    async reorderDemand (demand, status, targetDemand = null, placement = 'end') {
      const previous = this.demands.map((item) => ({ ...item }))
      const sourceStatus = demand.status
      const targetItems = this.demands
        .filter((item) => item.status === status && item.id !== demand.id)
        .sort((a, b) => this.demandSortOrder(a) - this.demandSortOrder(b))

      let insertIndex = targetItems.length
      if (targetDemand) {
        const targetIndex = targetItems.findIndex((item) => item.id === targetDemand.id)
        if (targetIndex >= 0) {
          insertIndex = placement === 'before' ? targetIndex : targetIndex + 1
        }
      }

      targetItems.splice(insertIndex, 0, { ...demand, status })
      const changed = []

      targetItems.forEach((item, index) => {
        const sortOrder = (index + 1) * 1000
        const current = this.demands.find((entry) => entry.id === item.id)
        if (current && (current.status !== status || Number(current.sortOrder || 0) !== sortOrder)) {
          changed.push({ id: current.id, status, sortOrder })
          this.$set(this.demands, this.demands.findIndex((entry) => entry.id === current.id), { ...current, status, sortOrder })
        }
      })

      if (sourceStatus !== status) {
        this.resequenceLocalColumn(sourceStatus, changed)
      }

      if (!changed.length) return

      try {
        await Promise.all(changed.map((item) => api.updateDemand(item.id, { status: item.status, sortOrder: item.sortOrder })))
        await this.loadSummary()
      } catch (error) {
        this.demands = previous
        this.error = this.errorMessage(error)
      }
    },
    resequenceLocalColumn (status, changed) {
      this.demands
        .filter((item) => item.status === status)
        .sort((a, b) => this.demandSortOrder(a) - this.demandSortOrder(b))
        .forEach((item, index) => {
          const sortOrder = (index + 1) * 1000
          if (Number(item.sortOrder || 0) === sortOrder) return
          changed.push({ id: item.id, status, sortOrder })
          const demandIndex = this.demands.findIndex((entry) => entry.id === item.id)
          if (demandIndex >= 0) this.$set(this.demands, demandIndex, { ...item, sortOrder })
        })
    },
    demandSortOrder (demand) {
      return demand.sortOrder === null || demand.sortOrder === undefined || demand.sortOrder === ''
        ? 999999999
        : Number(demand.sortOrder)
    },
    compareDemandsInColumn (a, b) {
      const sortDiff = this.demandSortOrder(a) - this.demandSortOrder(b)
      if (sortDiff) return sortDiff
      return new Date(b.updatedAt || b.createdAt || 0) - new Date(a.updatedAt || a.createdAt || 0)
    },
    async addNote (demand) {
      const text = String(this.noteDrafts[demand.id] || '').trim()
      if (!text) return
      try {
        const mentions = this.mentionsFromText(text)
        const updated = await api.addNote(demand.id, text, mentions)
        this.$set(this.noteDrafts, demand.id, '')
        await this.loadAll()
        if (this.selectedDemand && this.selectedDemand.id === demand.id) {
          this.selectedDemand = updated
        }
        this.closeMentionPopover()
      } catch (error) {
        this.error = this.errorMessage(error)
      }
    },
    handleMentionTyping (demandId) {
      const text = String(this.noteDrafts[demandId] || '')
      const match = this.activeMentionMatch(text)

      if (!match) {
        this.closeMentionPopover()
        return
      }

      this.mentionPopover = {
        open: true,
        demandId,
        query: match.query,
        startIndex: match.startIndex
      }
    },
    activeMentionMatch (text) {
      const cursorText = String(text || '')
      const atIndex = cursorText.lastIndexOf('@')
      if (atIndex < 0) return null

      const before = cursorText[atIndex - 1] || ' '
      if (!/\s|[([{:;,.!?]/.test(before)) return null

      const query = cursorText.slice(atIndex + 1)
      if (/\s/.test(query) || /[^a-zA-Z0-9._-]/.test(query)) return null

      return { startIndex: atIndex, query: query.toLowerCase() }
    },
    selectMention (demandId, username) {
      const current = String(this.noteDrafts[demandId] || '')
      const startIndex = this.mentionPopover.startIndex >= 0 ? this.mentionPopover.startIndex : current.length
      const before = current.slice(0, startIndex)
      const afterQuery = current.slice(startIndex + 1 + String(this.mentionPopover.query || '').length)
      const spacer = afterQuery && !/^\s/.test(afterQuery) ? ' ' : ''

      this.$set(this.noteDrafts, demandId, `${before}@${username}${spacer}${afterQuery || ' '}`)
      this.closeMentionPopover()
    },
    closeMentionPopover () {
      this.mentionPopover = { open: false, demandId: '', query: '', startIndex: -1 }
    },
    mentionsFromText (text) {
      const usernames = this.extractMentionUsernames(text)
      return this.mentionableUsers.filter((user) => usernames.includes(user.username))
    },
    extractMentionUsernames (text) {
      const matches = String(text || '').match(/(^|[\s(.,;:!?])@([a-zA-Z0-9._-]{3,80})/g) || []
      return [...new Set(matches.map((match) => match.replace(/^.*@/, '').toLowerCase()))]
    },
    noteParts (text, demand = null) {
      const parts = []
      const normalizedText = String(text || '')
      const requestDisplayId = String(demand && demand.requestDisplayId ? demand.requestDisplayId : '').toUpperCase()
      const regex = /@([a-zA-Z0-9._-]{3,80})|\b(REQ-[A-Z0-9]{6,16})\b/g
      let lastIndex = 0
      let match

      while ((match = regex.exec(normalizedText))) {
        if (match.index > lastIndex) {
          parts.push({ text: normalizedText.slice(lastIndex, match.index), mention: false, request: false })
        }
        if (match[1]) {
          parts.push({ text: match[1], mention: true, request: false })
        } else {
          const token = match[2]
          parts.push({
            text: token,
            mention: false,
            request: Boolean(demand && demand.requestId && (!requestDisplayId || token.toUpperCase() === requestDisplayId))
          })
        }
        lastIndex = match.index + match[0].length
      }

      if (lastIndex < normalizedText.length) {
        parts.push({ text: normalizedText.slice(lastIndex), mention: false, request: false })
      }

      return parts.length ? parts : [{ text, mention: false, request: false }]
    },
    deleteDemand (demand) {
      if (!this.isStaff) return
      this.demandToDelete = demand
      this.confirmDeleteDemandDialog = true
    },
    async confirmDeleteDemand () {
      if (!this.demandToDelete) return
      this.deletingDemand = true
      try {
        await api.deleteDemand(this.demandToDelete.id)
        if (this.selectedDemand && this.selectedDemand.id === this.demandToDelete.id) {
          this.closeDemandDetails()
        }
        this.confirmDeleteDemandDialog = false
        this.demandToDelete = null
        await this.loadAll()
      } catch (error) {
        this.error = this.errorMessage(error)
      } finally {
        this.deletingDemand = false
      }
    },
    latestNote (demand) {
      if (!demand.notes.length) return 'Sem anotações ainda.'
      return demand.notes[demand.notes.length - 1].text
    },
    priorityColor (priority) {
      return { 1: 'error', 2: 'warning', 3: 'success' }[this.priorityValue(priority)] || 'secondary'
    },
    priorityValue (priority) {
      const text = String(priority || '')
        .trim()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()

      if (text === 'alta') return 1
      if (text === 'media') return 2
      if (text === 'baixa') return 3

      const number = Number(priority)
      return [1, 2, 3].includes(number) ? number : 2
    },
    priorityLabel (priority) {
      return { 1: 'Alta', 2: 'Media', 3: 'Baixa' }[this.priorityValue(priority)] || 'Media'
    },
    priorityClass (priority) {
      const normalized = this.priorityLabel(priority).toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      return `priority-${normalized}`
    },
    requestStatusClass (status) {
      const normalized = String(status || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
      if (normalized.includes('conclu')) return 'status-done'
      if (normalized.includes('cancelada')) return 'status-canceled'
      if (normalized.includes('cliente')) return 'status-waiting'
      if (normalized.includes('desenvolvimento')) return 'status-progress'
      if (normalized.includes('analise')) return 'status-review'
      return 'status-open'
    },
    requestStatusBadgeVariant (status) {
      const normalized = String(status || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
      if (normalized.includes('conclu')) return 'success'
      if (normalized.includes('cancel')) return 'outline'
      if (normalized.includes('cliente')) return 'warning'
      if (normalized.includes('desenvolvimento')) return 'success'
      if (normalized.includes('analise')) return 'info'
      return 'info'
    },
    requestAuthorLabel (request) {
      return request.createdByName || request.createdByEmail || 'cliente sem nome'
    },
    demandStatusClass (status) {
      const normalized = String(status || '').toLowerCase()
      if (normalized.includes('concluída')) return 'status-done'
      if (normalized.includes('andamento') || normalized.includes('desenvolvimento')) return 'status-progress'
      return 'status-open'
    },
    userRoleLabel (user) {
      return { admin: 'Admin', client: 'Cliente', dev: 'Dev' }[user.role] || user.role
    },
    userProjectLabel (user) {
      if (user.role === 'admin') return 'Todos os projetos'
      return (user.projectNames && user.projectNames.length) ? user.projectNames.join(', ') : (user.projectName || 'Sem projeto')
    },
    formatDate (value) {
      if (!value) return 'Sem prazo'
      const parts = value.split('-')
      return `${parts[2]}/${parts[1]}/${parts[0]}`
    },
    formatFileSize (size) {
      const value = Number(size || 0)
      if (value < 1024) return `${value} B`
      if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`
      return `${(value / 1024 / 1024).toFixed(1)} MB`
    },
    formatDateTime (value) {
      return new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(value))
    },
    formatReportHours (value) {
      const number = Number(value || 0)
      const formatted = number.toLocaleString('pt-BR', { maximumFractionDigits: 1 })
      return `${formatted}h`
    },
    requestAgeHours (demand) {
      const startedAt = demand.requestCreatedAt || demand.createdAt
      const timestamp = new Date(startedAt).getTime()
      if (!timestamp) return 0
      return Math.max(0, (Date.now() - timestamp) / 36e5)
    },
    requestAgeLabel (demand) {
      const hours = this.requestAgeHours(demand)
      if (hours < 1) return 'há menos de 1h'
      if (hours < 24) return `há ${Math.floor(hours)}h`
      const days = Math.floor(hours / 24)
      if (days < 30) return `há ${days}d`
      const months = Math.floor(days / 30)
      return `há ${months}m`
    },
    requestAgeClass (demand) {
      const hours = this.requestAgeHours(demand)
      if (hours >= 72) return 'age-critical'
      if (hours >= 24) return 'age-warning'
      return 'age-fresh'
    },
    requestAgeTitle (demand) {
      const openedAt = demand.requestCreatedAt || demand.createdAt
      const displayId = demand.requestDisplayId ? `${demand.requestDisplayId} · ` : ''
      return `${displayId}solicitação aberta em ${this.formatDateTime(openedAt)}`
    },
    errorMessage (error) {
      if (error.response && error.response.data && error.response.data.message) return error.response.data.message
      if (error.code === 'ECONNABORTED') return 'A API demorou para responder. Tente novamente.'
      if (error.message) return `Não foi possível completar a ação: ${error.message}`
      return 'Não foi possível completar a ação. Verifique se a API está rodando.'
    }
  }
}
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

* { box-sizing: border-box; }

html,
body,
#app,
.v-application,
.v-application :not(.v-icon):not(.material-icons):not(.mdi),
.v-application input,
.v-application textarea,
.v-application select,
.v-application button,
.v-application .v-label,
.v-application .v-input,
.v-application .v-btn,
.v-application .v-chip,
.v-application .v-list-item,
.v-application .v-card,
.v-application .v-menu__content {
  font-family: 'Inter', sans-serif !important;
}

.app-page {
  min-height: 100vh;
  padding: 20px 28px 34px;
  background:
    radial-gradient(circle at 16% 18%, rgba(0, 240, 255, 0.09), transparent 28%),
    radial-gradient(circle at 78% 8%, rgba(15, 78, 251, 0.1), transparent 24%),
    linear-gradient(rgba(255, 255, 255, 0.025) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.025) 1px, transparent 1px),
    var(--bg);
  background-size: auto, auto, 60px 60px, 60px 60px, auto;
  color: var(--text);
}

.center-state {
  display: grid;
  min-height: 80vh;
  place-items: center;
}

.login-shell {
  display: grid;
  min-height: calc(100vh - 54px);
  align-content: center;
  justify-items: center;
  gap: 28px;
}

.login-brand,
.brand-lockup {
  display: flex;
  align-items: center;
  gap: 18px;
}

.brand-wordmark {
  color: #ffffff;
  font-size: 1.42rem;
  font-weight: 900;
  letter-spacing: 0;
  line-height: 1;
}

.login-brand span,
.brand-lockup span {
  padding-left: 18px;
  border-left: 1px solid rgba(255, 255, 255, 0.14);
  color: rgba(255, 255, 255, 0.56);
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.login-card,
.hero-band,
.board-panel {
  border: 1px solid var(--line);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.045);
  backdrop-filter: blur(20px) saturate(150%);
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.36);
}

.login-card {
  width: min(520px, 100%);
  padding: 34px;
}

.login-card h1 {
  margin: 18px 0 12px;
  font-size: 2.4rem;
  line-height: 1.08;
}

.login-card p,
.section-title p,
.hero-copy p {
  color: var(--muted);
  line-height: 1.7;
}

.auth-link {
  display: block;
  margin: 16px auto 0;
  border: 0;
  background: transparent;
  color: var(--accent);
  font-size: 0.82rem;
  font-weight: 500;
  letter-spacing: 0;
  text-transform: none;
}

.auth-link:hover {
  color: var(--accent-hover);
  text-decoration: underline;
}

.topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  max-width: 1480px;
  min-height: 68px;
  margin: 0 auto 22px;
}

.topbar-nav,
.topbar-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.topbar-nav a {
  padding: 8px 16px;
  color: rgba(255, 255, 255, 0.82);
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.project-context-select {
  position: relative;
  display: grid;
  gap: 2px;
  min-width: 220px;
  max-width: 300px;
  padding: 5px 9px;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: rgba(255, 253, 248, 0.72);
}

.project-context-select span {
  color: var(--muted);
  font-size: 0.64rem;
  font-weight: 900;
  line-height: 1;
  text-transform: uppercase;
}

.project-context-trigger {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 8px;
  align-items: center;
  width: 100%;
  border: 0;
  background: transparent;
  color: var(--text);
  font-size: 0.8rem;
  font-weight: 800;
  outline: 0;
  text-align: left;
}

.project-context-trigger::after {
  content: '▾';
  color: var(--muted);
  font-size: 0.78rem;
}

.project-context-popover {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  z-index: 25;
  display: grid;
  gap: 4px;
  width: min(320px, 82vw);
  padding: 6px;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: var(--panel);
  box-shadow: 0 18px 42px rgba(37, 34, 29, 0.14);
}

.project-context-popover button {
  min-height: 34px;
  padding: 0 10px;
  border: 0;
  border-radius: 7px;
  background: transparent;
  color: var(--text);
  font-size: 0.8rem;
  font-weight: 800;
  text-align: left;
}

.project-context-popover button:hover,
.project-context-popover button.active {
  background: rgba(13, 124, 102, 0.08);
  color: var(--cyan);
}

.account-menu {
  position: relative;
}

.account-trigger {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 8px;
  align-items: center;
  min-height: 38px;
  max-width: 270px;
  padding: 6px 8px 6px 16px;
  border: 0;
  border-radius: 0;
  background: transparent;
  color: var(--text);
  text-align: left;
}

.account-trigger::after {
  content: '';
  display: inline-block;
  width: 6px;
  height: 6px;
  border-right: 1.5px solid var(--muted);
  border-bottom: 1.5px solid var(--muted);
  transform: rotate(45deg) translateY(-2px);
}

.account-trigger span {
  overflow: hidden;
  font-size: 0.8rem;
  font-weight: 800;
  line-height: 1.1;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.account-popover {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  z-index: 20;
  min-width: 160px;
  padding: 6px;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: var(--panel);
  box-shadow: 0 18px 42px rgba(37, 34, 29, 0.14);
}

.account-popover button {
  width: 100%;
  min-height: 34px;
  padding: 0 10px;
  border: 0;
  border-radius: 7px;
  background: transparent;
  color: var(--text);
  font-size: 0.8rem;
  font-weight: 800;
  text-align: left;
}

.account-popover button:hover {
  background: rgba(13, 124, 102, 0.08);
  color: var(--cyan);
}

.profile-summary {
  display: grid;
  gap: 4px;
  margin-bottom: 18px;
  padding: 14px;
  border: 1px solid rgba(33, 28, 20, 0.1);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.56);
}

.profile-summary strong {
  color: var(--text);
  font-size: 1rem;
}

.profile-summary span,
.profile-summary small {
  color: var(--muted);
  font-weight: 700;
}

.profile-password-form {
  display: grid;
  gap: 2px;
}

.modal-help-text {
  margin: 10px 0 0;
  color: var(--muted);
  font-size: 0.86rem;
  line-height: 1.4;
}

.invite-panel {
  margin-bottom: 14px;
}

.btn-glow,
.btn-ghost {
  min-height: 32px;
  padding: 0 12px;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0;
  text-transform: none;
  transition: background 0.14s, color 0.14s, border-color 0.14s;
}

.btn-glow {
  border: 0;
  background: var(--accent);
  color: #ffffff;
}

.btn-glow:hover {
  background: var(--accent-hover);
}

.btn-ghost {
  border: 1px solid var(--line);
  background: transparent;
  color: var(--muted);
}

.btn-ghost:hover {
  border-color: var(--accent-border);
  background: var(--accent-subtle);
  color: var(--accent);
}

.hero-band,
.work-grid {
  max-width: 1480px;
  margin-right: auto;
  margin-left: auto;
}

.hero-band {
  display: grid;
  grid-template-columns: minmax(0, 0.82fr) minmax(520px, 1fr);
  gap: 22px;
  align-items: end;
  min-height: 190px;
  padding: 24px 28px;
  background:
    radial-gradient(circle at 18% 48%, rgba(0, 240, 255, 0.11), transparent 32%),
    linear-gradient(120deg, rgba(255, 255, 255, 0.07), rgba(255, 255, 255, 0.025));
}

.eyebrow,
.section-title span,
.board-header span {
  color: rgba(0, 240, 255, 0.86);
  font-size: 0.74rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.hero-copy h1 {
  max-width: 620px;
  margin: 14px 0 10px;
  font-size: clamp(1.8rem, 2.5vw, 2.9rem);
  font-weight: 800;
  line-height: 1.08;
}

.gradient-text {
  background: linear-gradient(135deg, var(--cyan), var(--blue));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
}

.summary-box {
  display: grid;
  align-content: center;
  min-height: 132px;
  padding: 18px 16px;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: rgba(10, 10, 10, 0.42);
}

.summary-box span {
  order: 2;
  color: rgba(255, 255, 255, 0.72);
  font-size: 0.72rem;
  font-weight: 800;
  text-transform: uppercase;
}

.summary-box strong {
  display: block;
  order: 1;
  margin: 0 0 8px;
  font-size: clamp(2.6rem, 4.4vw, 4.2rem);
  line-height: 0.95;
}

.summary-box.warning strong { color: var(--warning); }
.summary-box.danger strong { color: var(--danger); }
.summary-box.success strong { color: var(--success); }

.work-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  margin-top: 0;
  align-items: start;
}

.board-panel {
  min-height: 650px;
  padding: 22px;
}

.section-title,
.board-header {
  margin-bottom: 18px;
}

.section-title h2,
.board-header h2 {
  margin: 6px 0;
  font-size: 1.42rem;
  font-weight: 800;
}

.board-header {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: center;
}

.board-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 10px;
}

.admin-tabs {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-bottom: 16px;
  padding: 4px;
  border: 1px solid var(--line);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.04);
}

.admin-tabs button {
  min-height: 34px;
  border-radius: 999px;
  color: var(--muted);
  font-weight: 800;
}

.admin-tabs button.active {
  background: linear-gradient(135deg, var(--cyan), var(--blue));
  color: #071114;
}

.stack-form {
  min-width: 0;
}

.two-columns,
.toolbar {
  display: grid;
  gap: 12px;
}

.two-columns { grid-template-columns: repeat(2, minmax(0, 1fr)); }

.two-columns.single-column { grid-template-columns: 1fr; }
.toolbar { grid-template-columns: minmax(220px, 1fr) 220px 180px 180px; }

.status-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin: 16px 0;
}

.primary-button,
.new-demand-button {
  min-height: 42px;
  border-radius: 999px;
  font-weight: 800;
  letter-spacing: 0;
}

.side-list {
  display: grid;
  gap: 10px;
  align-content: start;
  max-height: 430px;
  overflow: auto;
}

.side-list.compact {
  padding-top: 18px;
  border-top: 1px solid var(--line);
}

.side-item {
  display: grid;
  gap: 4px;
  padding: 12px;
  border: 1px solid rgba(0, 240, 255, 0.12);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.045);
}

.side-item strong { font-size: 0.94rem; }
.side-item span { color: var(--muted); font-size: 0.8rem; font-weight: 600; }

.modal-intro {
  margin: 0 0 18px;
  color: var(--muted);
  line-height: 1.6;
}

.admin-modal-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 360px;
  gap: 22px;
  align-items: start;
}

.admin-list {
  max-height: 520px;
  padding-left: 18px;
  border-left: 1px solid var(--line);
}

.board-state,
.empty-state {
  min-height: 300px;
}

.empty-state {
  display: grid;
  place-items: center;
  align-content: center;
  gap: 8px;
  border: 1px dashed rgba(0, 240, 255, 0.22);
  border-radius: 8px;
  color: var(--muted);
  text-align: center;
}

.kanban-board {
  display: flex;
  gap: 10px;
  align-items: start;
  overflow-x: auto;
  padding-bottom: 16px;
}

.kanban-column {
  display: flex;
  flex-direction: column;
  flex: 0 0 288px;
  min-height: 120px;
  padding: 10px 8px 6px;
  border-radius: 10px;
  background: #f1f0ee;
  overflow: visible;
}

.kanban-column.is-pending { background: #f1f0ee; }
.kanban-column.is-progress { background: #f1f0ee; }
.kanban-column.is-done { background: #f1f0ee; }

.kanban-column-header > div:first-child {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 30px;
  padding: 0 2px;
  border-radius: 8px;
  background: transparent;
}

.column-add-button {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  margin-top: 4px;
  padding: 7px 8px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: var(--muted);
  font-size: 0.82rem;
  font-weight: 500;
  text-align: left;
  transition: background 0.14s, color 0.14s;
}

.column-add-button:hover {
  background: rgba(0,0,0,0.05);
  color: var(--text);
}

.kanban-column-header span {
  font-size: 0.9rem;
  font-weight: 800;
  text-transform: uppercase;
}

.kanban-column-header strong {
  display: grid;
  min-width: 22px;
  height: 22px;
  padding: 0 7px;
  place-items: center;
  border-radius: 999px;
  background: rgba(148, 163, 184, 0.2);
  color: rgba(255, 255, 255, 0.78);
  font-size: 0.74rem;
}

.kanban-column-header small {
  min-height: 22px;
  padding: 3px 8px;
  border: 1px solid rgba(148, 163, 184, 0.28);
  border-radius: 999px;
  color: rgba(255, 255, 255, 0.72);
  font-size: 0.72rem;
  font-weight: 700;
}

.list-dot,
.card-project-dot {
  width: 14px;
  height: 14px;
  border: 2px solid var(--cyan);
  border-radius: 999px;
}

.kanban-column-description {
  min-height: 18px;
  margin: -4px 2px 6px;
  color: var(--muted);
  font-size: 0.78rem;
}

.kanban-empty {
  display: grid;
  min-height: 86px;
  place-items: center;
  padding: 16px;
  border: 1px dashed rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  color: var(--muted);
  font-size: 0.9rem;
  text-align: center;
}

.inline-demand-composer {
  display: grid;
  gap: 10px;
  margin-bottom: 10px;
}

.inline-demand-composer textarea {
  width: 100%;
  min-height: 80px;
  resize: none;
  padding: 10px;
  border: 2px solid var(--accent);
  border-radius: 8px;
  outline: none;
  background: var(--panel);
  color: var(--text);
  font: inherit;
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1.4;
  box-shadow: 0 4px 12px rgba(0,0,0,0.08);
}

.inline-demand-composer textarea::placeholder {
  color: var(--muted);
  font-weight: 400;
}

.inline-demand-extra {
  display: grid;
  grid-template-columns: 80px 1fr;
  gap: 6px;
}

.inline-demand-extra input {
  height: 28px;
  padding: 0 8px;
  border: 0;
  border-bottom: 1px solid var(--line);
  border-radius: 0;
  background: transparent;
  color: var(--text);
  font: inherit;
  font-size: 0.82rem;
  outline: none;
  width: 100%;
}

.inline-demand-extra input::placeholder { color: var(--muted); }

.inline-demand-extra input:focus {
  border-bottom-color: var(--accent);
}

.inline-demand-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}

.inline-demand-actions button {
  min-height: 30px;
  padding: 0 12px;
  border-radius: 6px;
  font-size: 0.82rem;
  font-weight: 500;
}

.inline-demand-actions button[type='submit'] {
  background: var(--accent);
  border: 0;
  color: #ffffff;
}

.inline-demand-actions button[type='submit']:disabled {
  opacity: 0.62;
}

.inline-demand-actions button[type='button'] {
  border: 0;
  background: transparent;
  color: var(--muted);
}

.demand-card {
  display: flex;
  flex-direction: column;
  min-height: 112px;
  padding: 12px;
  border-color: var(--line) !important;
  border-radius: var(--radius) !important;
  background: var(--panel) !important;
  color: var(--text) !important;
  cursor: grab;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.demand-card:hover {
  border-color: rgba(0, 240, 255, 0.28) !important;
  transform: translateY(-1px);
}

.demand-card.readonly { cursor: default; }
.demand-card.completed { background: rgba(110, 231, 168, 0.065) !important; }

.card-top {
  display: flex;
  justify-content: flex-start;
  gap: 10px;
  align-items: center;
}

.card-project-name {
  min-width: 0;
  overflow: hidden;
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.76rem;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.status-badge {
  padding: 4px 8px;
  border-radius: 999px;
  background: rgba(0, 240, 255, 0.08);
  color: var(--cyan);
  font-size: 0.66rem;
  font-weight: 800;
  text-transform: uppercase;
}

.quick-chip {
  height: 22px !important;
  padding: 0 8px !important;
  border-color: rgba(255, 255, 255, 0.22) !important;
  background: rgba(255, 255, 255, 0.09) !important;
  color: rgba(255, 255, 255, 0.84) !important;
  font-size: 0.64rem !important;
  font-weight: 800;
}

.quick-chip.error,
.quick-chip.warning,
.quick-chip.success {
  color: #ffffff !important;
}

.quick-chip .v-chip__content {
  color: inherit !important;
  line-height: 1;
}

.card-menu-wrap {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 8px;
}

.card-menu-button {
  display: grid;
  width: 24px;
  height: 24px;
  place-items: center;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.04);
  color: rgba(255, 255, 255, 0.8);
  font-size: 1.1rem;
  line-height: 1;
}

.card-menu-list {
  border: 1px solid rgba(0, 240, 255, 0.16);
  background: #101313 !important;
  color: var(--text) !important;
}

.card-menu-list .v-list-item__title {
  color: rgba(255, 255, 255, 0.82);
  font-size: 0.82rem;
}

.danger-text {
  color: var(--danger) !important;
}

.demand-card h3 {
  margin: 8px 0 9px;
  font-size: 0.9rem;
  font-weight: 800;
  line-height: 1.32;
}

.demand-display-id {
  display: block;
  margin-top: 8px;
  color: var(--cyan);
  font-size: 0.68rem;
  font-weight: 900;
  letter-spacing: 0;
}

.meta-row {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: auto;
  margin-bottom: 0;
}

.add-list-card {
  display: flex;
  flex: 0 0 180px;
  align-self: flex-start;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border: 1px dashed var(--line);
  border-radius: 10px;
  background: transparent;
  color: var(--muted);
  font-size: 0.875rem;
  font-weight: 500;
  transition: border-color 0.15s, color 0.15s, background 0.15s;
}

.add-list-card:hover {
  border-color: var(--accent);
  color: var(--accent);
  background: var(--accent-subtle);
}

.latest-note {
  min-height: 40px;
  margin: 0 0 10px;
  color: var(--muted);
  font-size: 0.9rem;
  line-height: 1.55;
}

.note-panel {
  border-top: 1px solid var(--line);
  background: transparent !important;
}

.notes-list {
  display: grid;
  gap: 8px;
  margin-bottom: 12px;
}

.note-item {
  display: grid;
  gap: 7px;
  padding: 10px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.075);
  color: rgba(255, 255, 255, 0.86);
  line-height: 1.45;
}

.note-item small {
  color: rgba(255, 255, 255, 0.58);
}

.note-item > span {
  color: rgba(255, 255, 255, 0.88);
}

.note-text mark {
  padding: 1px 4px;
  border-radius: 5px;
  background: rgba(0, 240, 255, 0.12);
  color: var(--cyan);
  font-weight: 800;
}

.note-request-link {
  appearance: none;
  border: 0;
  border-radius: 6px;
  background: rgba(0, 128, 111, 0.12);
  color: var(--teal);
  cursor: pointer;
  font: inherit;
  font-weight: 800;
  padding: 1px 5px;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.note-request-link:hover,
.note-request-link:focus-visible {
  background: rgba(0, 128, 111, 0.2);
  outline: 2px solid rgba(0, 128, 111, 0.28);
  outline-offset: 2px;
}

.mention-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.mention-list span {
  padding: 3px 8px;
  border: 1px solid rgba(0, 240, 255, 0.24);
  border-radius: 999px;
  background: rgba(0, 240, 255, 0.08);
  color: var(--cyan);
  font-size: 0.72rem;
  font-weight: 800;
}

.mention-popover {
  display: grid;
  gap: 2px;
  width: min(100%, 320px);
  margin: -6px 0 12px;
  padding: 6px;
  border: 1px solid rgba(0, 240, 255, 0.18);
  border-radius: 8px;
  background: #1b2022;
  box-shadow: 0 16px 42px rgba(0, 0, 0, 0.42);
}

.mention-popover button {
  display: grid;
  grid-template-columns: minmax(0, 0.8fr) minmax(0, 1.2fr);
  gap: 8px;
  align-items: center;
  padding: 9px 10px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  text-align: left;
}

.mention-popover button:hover {
  background: rgba(0, 240, 255, 0.08);
}

.mention-popover strong {
  overflow: hidden;
  color: var(--cyan);
  font-size: 0.78rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mention-popover span {
  overflow: hidden;
  color: rgba(255, 255, 255, 0.72);
  font-size: 0.75rem;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mention-popover > span {
  padding: 9px 10px;
  color: var(--muted);
}

.muted {
  color: var(--muted);
}

.v-card__actions { margin-top: auto; }

.modal-card {
  border: 1px solid var(--line);
  border-radius: var(--radius) !important;
  background: var(--panel) !important;
  color: var(--text) !important;
}

.modal-card .v-card__title { padding: 22px 24px 10px; }
.modal-card .v-card__title span { color: var(--cyan); font-size: 0.76rem; font-weight: 800; letter-spacing: 0.12em; text-transform: uppercase; }
.modal-card .v-card__title h2 { margin: 4px 0 0; font-size: 1.45rem; font-weight: 800; }
.modal-card .v-card__text { padding: 12px 24px 0; }
.modal-card .v-card__actions { padding: 12px 24px 22px; }
.modal-card .v-card__actions .v-btn:not(.primary) { color: var(--muted) !important; }

.project-multi-select .v-select__selections { gap: 4px; padding: 6px 0 4px; min-height: 42px; flex-wrap: wrap; }
.project-chip { background: var(--accent-subtle) !important; color: var(--accent) !important; border: 1px solid var(--accent-border) !important; font-size: 0.75rem !important; }
.project-chip-all { background: var(--accent-subtle) !important; color: var(--accent) !important; border: 1px solid var(--accent-border) !important; font-size: 0.75rem !important; font-weight: 600 !important; }
.project-select-all-item { border-bottom: 1px solid var(--line); margin-bottom: 4px; }

.user-modal-card { display: flex; flex-direction: column; }
.user-modal-body { flex: 1; padding-bottom: 0 !important; }
.user-modal-columns { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; }
.user-modal-left, .user-modal-right { display: flex; flex-direction: column; }
.user-modal-section-label { font-size: 0.72rem; font-weight: 600; color: var(--muted); letter-spacing: 0.06em; text-transform: uppercase; margin: 0 0 12px; }
.user-modal-admin-note { font-size: 0.8rem; color: var(--muted); line-height: 1.5; padding: 10px 12px; border: 1px solid var(--line); border-radius: 6px; }
.user-modal-actions { padding: 16px 24px 22px !important; justify-content: flex-end !important; }

.btn-delete-confirm { background: #e53935 !important; color: #ffffff !important; border: 0; border-radius: 4px; padding: 0 16px; min-height: 36px; font-size: 0.875rem; font-weight: 500; cursor: pointer; }
.btn-delete-confirm:disabled { opacity: 0.65; cursor: not-allowed; }

.modal-close span {
  color: rgba(255, 255, 255, 0.8);
  font-size: 1.7rem;
  line-height: 1;
}

.detail-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(240px, 300px);
  gap: 18px;
  align-items: start;
}

.detail-main-column,
.detail-side-column,
.detail-notes {
  min-width: 0;
}

.detail-main-column,
.detail-side-column {
  display: grid;
  gap: 8px;
}

.detail-modal .v-input {
  margin-bottom: 0;
}

.detail-modal .v-input__slot {
  margin-bottom: 0 !important;
  overflow: visible;
}

.detail-modal .v-text-field__details {
  min-height: 0;
  margin-bottom: 0;
}

.detail-side-panel {
  display: grid;
  gap: 2px;
  padding: 12px;
  border: 1px solid rgba(33, 28, 20, 0.1);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.56);
}

.detail-side-panel h3 {
  margin: 0 0 8px;
  color: var(--text);
  font-size: 0.95rem;
  font-weight: 800;
}

.three-columns {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}

.request-meta-fields {
  padding-top: 8px;
}

.demand-detail-form {
  padding-top: 8px;
}

.detail-notes {
  padding-top: 18px;
  border-top: 1px solid rgba(33, 28, 20, 0.12);
}

.detail-notes h3 {
  margin: 0 0 14px;
  color: var(--text);
  font-size: 1rem;
  font-weight: 800;
}

.source-attachments-panel {
  display: grid;
  gap: 10px;
  margin-bottom: 16px;
  padding: 12px;
  border: 1px solid rgba(13, 124, 102, 0.18);
  border-radius: 8px;
  background: rgba(13, 124, 102, 0.055);
}

.source-attachments-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.source-attachments-header span {
  display: block;
  color: var(--cyan);
  font-size: 0.7rem;
  font-weight: 900;
  text-transform: uppercase;
}

.source-attachments-header strong {
  display: block;
  margin-top: 2px;
  color: var(--text);
  font-size: 0.88rem;
}

.source-attachments-header button {
  min-height: 30px;
  padding: 0 10px;
  border: 1px solid rgba(13, 124, 102, 0.28);
  border-radius: 8px;
  background: rgba(255, 253, 248, 0.72);
  color: var(--cyan);
  font-size: 0.74rem;
  font-weight: 900;
}

.source-attachment-list {
  display: grid;
  gap: 7px;
}

.source-attachment-item {
  display: grid;
  grid-template-columns: 34px minmax(0, 1fr);
  gap: 8px;
  align-items: center;
  padding: 8px;
  border: 1px solid rgba(37, 34, 29, 0.08);
  border-radius: 8px;
  background: rgba(255, 253, 248, 0.75);
  color: var(--text);
  text-decoration: none;
}

.source-attachment-item > span {
  display: grid;
  width: 34px;
  height: 30px;
  place-items: center;
  border-radius: 7px;
  background: rgba(13, 124, 102, 0.1);
  color: var(--cyan);
  font-size: 0.68rem;
  font-weight: 900;
}

.source-attachment-item strong,
.source-attachment-item small {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.source-attachment-item strong {
  color: var(--text);
  font-size: 0.8rem;
}

.source-attachment-item small {
  margin-top: 2px;
  color: var(--muted);
  font-size: 0.7rem;
  font-weight: 700;
}

.attachment-dropzone {
  display: grid;
  grid-template-columns: 42px minmax(0, 1fr);
  gap: 12px;
  align-items: center;
  min-height: 72px;
  padding: 12px;
  border: 1px dashed rgba(13, 124, 102, 0.35);
  border-radius: 8px;
  background: rgba(13, 124, 102, 0.055);
  cursor: pointer;
}

.attachment-dropzone:hover,
.attachment-dropzone:focus {
  border-color: var(--cyan);
  background: rgba(13, 124, 102, 0.09);
  outline: none;
}

.attachment-native-input {
  display: none;
}

.attachment-icon {
  display: grid;
  width: 42px;
  height: 42px;
  place-items: center;
  border-radius: 8px;
  background: var(--cyan);
  color: #fffdf8;
  font-size: 1.25rem;
  font-weight: 800;
}

.attachment-dropzone strong {
  display: block;
  color: var(--text);
  font-size: 0.92rem;
  font-weight: 800;
}

.attachment-dropzone small {
  display: block;
  margin-top: 3px;
  color: var(--muted);
  font-size: 0.78rem;
  font-weight: 700;
  line-height: 1.35;
}

.attachment-list {
  display: grid;
  gap: 8px;
  margin-top: 12px;
}

.bulk-demand-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 4px 0 14px;
}

.bulk-demand-actions span {
  color: var(--muted);
  font-size: 0.86rem;
  font-weight: 700;
}

.bulk-demand-review {
  display: grid;
  gap: 10px;
  padding-top: 14px;
  border-top: 1px solid rgba(33, 28, 20, 0.12);
}

.bulk-demand-review-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.bulk-demand-review-head strong {
  color: var(--text);
  font-size: 1rem;
  font-weight: 800;
}

.bulk-demand-review-head button {
  border: 0;
  background: transparent;
  color: var(--teal);
  font-weight: 800;
}

.bulk-demand-row {
  display: grid;
  grid-template-columns: 32px minmax(260px, 1fr) minmax(150px, 180px) minmax(130px, 160px) 34px;
  gap: 8px;
  align-items: center;
}

.bulk-demand-index {
  display: grid;
  width: 28px;
  height: 28px;
  place-items: center;
  border-radius: 999px;
  background: rgba(13, 124, 102, 0.12);
  color: var(--teal);
  font-size: 0.82rem;
  font-weight: 800;
}

.icon-remove-button {
  display: grid;
  width: 32px;
  height: 32px;
  place-items: center;
  border: 1px solid rgba(239, 68, 68, 0.22);
  border-radius: 8px;
  background: rgba(239, 68, 68, 0.06);
  color: #b91c1c;
  font-size: 1.2rem;
  font-weight: 800;
}

.attachment-item {
  display: grid;
  grid-template-columns: 46px minmax(0, 1fr) auto;
  gap: 10px;
  align-items: center;
  padding: 9px;
  border: 1px solid rgba(37, 34, 29, 0.1);
  border-radius: 8px;
  background: var(--panel-2);
}

.attachment-preview {
  display: grid;
  width: 46px;
  height: 46px;
  place-items: center;
  overflow: hidden;
  border-radius: 8px;
  background: #fffdf8;
  color: var(--cyan);
  font-size: 0.62rem;
  font-weight: 800;
  text-decoration: none;
}

.attachment-preview.image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.attachment-copy {
  min-width: 0;
}

.attachment-copy a {
  display: block;
  overflow: hidden;
  color: var(--text);
  font-size: 0.86rem;
  font-weight: 800;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.attachment-copy small {
  display: block;
  margin-top: 2px;
  color: var(--muted);
  font-size: 0.76rem;
  font-weight: 700;
}

.attachment-item button,
.attachment-add-more {
  min-height: 32px;
  padding: 0 10px;
  border: 1px solid rgba(185, 68, 60, 0.22);
  border-radius: 8px;
  background: transparent;
  color: var(--danger);
  font-size: 0.76rem;
  font-weight: 800;
}

.attachment-add-more {
  justify-self: start;
  border-color: rgba(13, 124, 102, 0.28);
  color: var(--cyan);
}

.attachment-empty {
  display: grid;
  min-height: 40px;
  place-items: center;
  border: 1px dashed rgba(37, 34, 29, 0.14);
  border-radius: 8px;
  color: var(--muted);
  font-weight: 700;
}

.admin-modal .v-card__text {
  max-height: 72vh;
}

.detail-modal .v-card__text {
  max-height: 72vh;
}

.detail-modal .v-text-field--outlined,
.detail-modal .v-textarea--outlined,
.detail-modal .v-select.v-text-field--outlined {
  padding-top: 2px;
}

.detail-modal .v-text-field--outlined .v-label,
.detail-modal .v-textarea--outlined .v-label,
.detail-modal .v-select.v-text-field--outlined .v-label {
  background: var(--panel);
  padding: 0 4px;
}

.detail-main-column > .v-input:first-child {
  margin-top: 10px;
}

.v-text-field.v-text-field--outlined fieldset,
.v-select.v-text-field--outlined fieldset,
.v-textarea.v-text-field--outlined fieldset {
  border-color: rgba(255, 255, 255, 0.14);
}

.v-text-field input,
.v-textarea textarea,
.v-select__selection,
.v-label,
.v-input,
.v-icon {
  color: rgba(255, 255, 255, 0.82) !important;
}

.v-text-field--outlined.v-input--is-focused fieldset,
.v-select.v-input--is-focused fieldset,
.v-textarea.v-input--is-focused fieldset {
  border-color: var(--cyan) !important;
}

.v-chip.primary,
.v-btn.primary {
  background: linear-gradient(135deg, var(--cyan), var(--blue)) !important;
  color: #071114 !important;
  font-weight: 800;
}

@media (max-width: 1180px) {
  .hero-band,
  .work-grid {
    grid-template-columns: 1fr;
  }

  .board-compact-head {
    grid-template-columns: 1fr;
  }

  .topbar {
    flex-wrap: wrap;
  }

  .topbar-nav {
    order: 3;
    width: 100%;
    justify-content: center;
  }

  .admin-modal-grid {
    grid-template-columns: 1fr;
  }

  .admin-list {
    max-height: 300px;
    padding-top: 18px;
    padding-left: 0;
    border-top: 1px solid var(--line);
    border-left: 0;
  }
}

@media (max-width: 760px) {
  .app-page {
    padding: 0;
  }

  .resource-screen {
    padding: 16px;
  }

  .topbar {
    margin: 0;
    padding: 0 16px;
  }

  .login-card,
  .hero-band,
  .board-panel {
    padding: 16px;
  }

  .trello-board-panel {
    padding: 8px;
  }

  .board-compact-head {
    grid-template-columns: minmax(0, 1fr);
    padding: 8px;
  }

  .summary-grid,
  .two-columns,
  .three-columns,
  .toolbar,
  .request-toolbar,
  .request-detail-grid {
    grid-template-columns: 1fr;
  }

  .resource-actions {
    width: 100%;
    justify-content: stretch;
  }

  .resource-actions .v-btn {
    flex: 1 1 100%;
  }

  .board-header,
  .board-actions,
  .topbar-actions {
    align-items: stretch;
    flex-direction: column;
  }

  .brand-lockup {
    width: 100%;
    justify-content: space-between;
  }

  .brand-wordmark {
    font-size: 1.2rem;
  }

  .brand-lockup span,
  .login-brand span,
  .topbar-nav {
    display: none;
  }

  .hero-copy h1,
  .login-card h1 {
    font-size: 2.25rem;
  }

  .kanban-board {
    grid-template-columns: 1fr;
    overflow-x: visible;
  }

  .kanban-column {
    min-height: auto;
  }
}
/* shadcn-inspired redesign */

.v-application * {
  letter-spacing: 0 !important;
}

.app-page {
  padding: 0;
  background: var(--bg);
  color: var(--text);
}

.product-shell {
  --sidebar-width: 240px;
  --sidebar-collapsed-width: 56px;
  min-height: 100vh;
  padding: 0 0 0 calc(var(--sidebar-collapsed-width));
  transition: padding-left 0.22s ease;
  background: var(--bg);
}

.product-shell.sidebar-active {
  padding-left: var(--sidebar-width);
}

.topbar {
  max-width: none;
  min-height: 64px;
  margin: 0 -32px 20px;
  padding: 0 32px;
  border-bottom: 1px solid var(--line);
  background: var(--panel);
}

.brand-lockup,
.login-brand {
  gap: 12px;
}

.brand-mark {
  display: grid;
  width: 32px;
  height: 32px;
  place-items: center;
  border-radius: 7px;
  background: var(--accent);
  color: #ffffff;
  font-size: 0.9rem;
  font-weight: 800;
  flex-shrink: 0;
}

.brand-wordmark {
  color: var(--text);
  font-size: 1.12rem;
  font-weight: 800;
}

.login-brand .brand-wordmark {
  font-size: 1.55rem;
}

.login-brand span,
.brand-lockup span {
  display: block;
  padding-left: 0;
  border-left: 0;
  color: var(--muted);
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: none;
}

.workspace-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 0;
}

.workspace-layout.menu-open {
  grid-template-columns: minmax(0, 1fr);
  gap: 0;
}

.workspace-rail {
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  z-index: 22;
  display: grid;
  grid-template-columns: 1fr;
  align-content: start;
  gap: 8px;
  width: var(--sidebar-collapsed-width);
  min-height: 100vh;
  margin: 0;
  padding: 0 8px 14px;
  border: 0;
  border-radius: 0;
  background: var(--sidebar-bg);
  box-shadow: none;
  transform: translateX(0);
  transition: width 0.2s ease, padding 0.2s ease;
  overflow: hidden auto;
  opacity: 1;
  pointer-events: auto;
}

.workspace-rail.open {
  width: var(--sidebar-width);
  padding: 0 14px 14px;
  box-shadow: none;
}

.sidebar-backdrop {
  display: none;
}

.sidebar-backdrop.active {
  display: none;
}

.sidebar-mobile-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  height: 64px;
  flex-shrink: 0;
  margin-bottom: 6px;
  padding: 0;
  border-bottom: 1px solid var(--line);
}

.workspace-rail:not(.open) .sidebar-mobile-head {
  justify-content: center;
  border-bottom-color: transparent;
}

.workspace-rail * {
  color: var(--sidebar-text);
}

.workspace-rail:not(.open) .sidebar-brand-lockup > div,
.workspace-rail:not(.open) .sidebar-mobile-head button,
.workspace-rail:not(.open) .rail-section-label,
.workspace-rail:not(.open) .rail-item strong {
  display: none;
}

.sidebar-brand-lockup {
  display: flex;
  align-items: center;
  min-width: 0;
  gap: 10px;
}

.sidebar-brand-lockup .brand-mark {
  width: 38px;
  height: 38px;
}

.sidebar-brand-lockup .brand-wordmark {
  display: block;
  font-size: 1.08rem;
  color: #ffffff;
}

.sidebar-brand-lockup span {
  display: block;
  margin-top: 2px;
  color: var(--sidebar-muted);
  color: var(--muted);
  font-size: 0.76rem;
  font-weight: 800;
}

.sidebar-mobile-head button {
  display: none;
  min-height: 32px;
  padding: 0 10px;
  border: 1px solid var(--sidebar-line);
  border-radius: 6px;
  background: transparent;
  color: var(--sidebar-text);
  font-size: 0.75rem;
  font-weight: 600;
}

.rail-section {
  display: grid;
  gap: 6px;
}

.rail-section + .rail-section {
  margin-top: 2px;
  padding-top: 12px;
  border-top: 1px solid var(--sidebar-line);
}

.rail-section-label {
  overflow: hidden;
  padding: 0 4px;
  color: var(--sidebar-muted);
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  line-height: 1.2;
  text-align: left;
  text-overflow: ellipsis;
  text-transform: uppercase;
}

.rail-item {
  display: grid;
  grid-template-columns: 34px minmax(0, 1fr) auto;
  gap: 8px;
  align-items: center;
  justify-items: start;
  min-height: 36px;
  padding: 3px 6px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: var(--sidebar-text);
  text-align: left;
  transition: background 0.14s ease, color 0.14s ease;
}

.rail-item-wrap {
  min-width: 0;
}

.workspace-rail:not(.open) .rail-item {
  grid-template-columns: 36px;
  justify-content: center;
}

.workspace-rail:not(.open) .rail-caret,
.workspace-rail:not(.open) .rail-projects {
  display: none;
}

.rail-icon {
  display: grid;
  position: relative;
  width: 28px;
  height: 28px;
  place-items: center;
  border-radius: 6px;
  background: transparent;
  color: var(--sidebar-muted);
}

.rail-icon svg {
  display: block;
}

.rail-item strong {
  overflow: hidden;
  color: var(--sidebar-text);
  font-size: 0.84rem;
  font-weight: 500;
  line-height: 1.2;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rail-caret {
  display: flex;
  align-items: center;
  margin-left: auto;
  color: var(--sidebar-muted);
  line-height: 1;
  transition: transform 0.16s ease;
}

.rail-caret.open {
  transform: rotate(180deg);
}

.rail-projects {
  position: relative;
  display: grid;
  gap: 2px;
  margin: 2px 0 10px 18px;
  padding: 2px 0 2px 24px;
}

.rail-projects::before {
  content: '';
  position: absolute;
  top: 4px;
  bottom: 6px;
  left: 10px;
  width: 1px;
  background: var(--sidebar-line);
}

.rail-project-item {
  min-width: 0;
  min-height: 28px;
  padding: 0 8px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: var(--sidebar-muted);
  font-size: 0.74rem;
  font-weight: 500;
  overflow: hidden;
  text-align: left;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rail-project-item:hover,
.rail-project-item.active {
  background: var(--sidebar-active-bg);
  color: var(--sidebar-active);
}

.rail-project-item.active {
  background: transparent;
  color: rgba(255,255,255,0.6);
  box-shadow: none;
}

.rail-project-item.active::before {
  content: '';
  display: inline-block;
  width: 6px;
  height: 6px;
  margin-right: 7px;
  border-radius: 999px;
  background: var(--accent);
  vertical-align: 1px;
}

.rail-item.active,
.rail-item:hover {
  background: var(--sidebar-active-bg);
  color: var(--sidebar-active);
}

.rail-item:hover {
  transform: none;
}

.rail-item.active {
  background: var(--sidebar-active-bg);
}

.rail-item.active strong,
.rail-item:hover strong {
  color: var(--sidebar-active);
}

.rail-item.active .rail-icon,
.rail-item:hover .rail-icon {
  background: transparent;
  color: var(--sidebar-active);
}

.workspace-main {
  min-width: 0;
  min-height: 100vh;
  background: var(--bg);
}

.topbar-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-left: auto;
  padding-right: 8px;
}

.top-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
}

.mobile-menu-button {
  display: grid;
  place-items: center;
  width: 32px;
  min-width: 32px;
  min-height: 32px;
  padding: 0;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: var(--muted);
  transition: background 0.14s, color 0.14s;
}

.mobile-menu-button:hover {
  background: var(--panel-2);
  color: var(--text);
}

.btn-ghost,
.btn-glow {
  border-radius: 8px !important;
}

.btn-glow,
.btn-ghost {
  min-height: 32px;
  padding: 0 12px;
  border-radius: 6px;
  color: var(--text);
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0;
  text-transform: none;
}

.btn-glow {
  border-color: var(--accent);
  background: var(--accent);
  color: #ffffff !important;
  box-shadow: none;
}

.btn-glow:hover {
  background: var(--accent-hover);
  border-color: var(--accent-hover);
}

.btn-ghost {
  border-color: var(--line);
  background: var(--panel);
  color: var(--text);
}

.login-shell {
  min-height: 100vh;
  padding: 24px;
  background: var(--bg);
}

.login-card,
.hero-band,
.board-panel,
.modal-card {
  border: 1px solid var(--line);
  border-radius: var(--radius) !important;
  background: var(--panel) !important;
  box-shadow: var(--shadow);
}

.login-card {
  padding: 28px 32px 32px;
}

.login-card h1,
.hero-copy h1,
.board-header h2,
.modal-card .v-card__title h2 {
  color: var(--text);
  font-weight: 700;
}

.login-card h1 {
  font-size: 1.55rem;
  font-weight: 700;
  margin-bottom: 4px;
}

.login-card p,
.section-title p,
.hero-copy p,
.modal-intro {
  color: var(--muted);
  line-height: 1.58;
  font-size: 0.88rem;
}

.eyebrow,
.section-title span,
.board-header span,
.modal-card .v-card__title span {
  color: var(--accent);
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0;
  text-transform: none;
}

.hero-band,
.work-grid {
  max-width: none;
}

.hero-band {
  grid-template-columns: minmax(320px, 0.58fr) minmax(520px, 1fr);
  gap: 18px;
  align-items: center;
  min-height: auto;
  padding: 16px 18px;
  background: var(--panel) !important;
}

.hero-copy h1 {
  margin: 6px 0 6px;
  font-size: clamp(1.55rem, 2vw, 2.18rem);
  line-height: 1.12;
}

.gradient-text {
  background: none;
  -webkit-text-fill-color: currentColor;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
}

.summary-box {
  position: relative;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 12px;
  align-items: center;
  min-height: 72px;
  padding: 12px 14px;
  border-color: var(--line);
  background: var(--panel-2);
  overflow: hidden;
}

.summary-box::before {
  content: '';
  width: 4px;
  height: 34px;
  border-radius: 999px;
  background: var(--accent);
}

.summary-box strong {
  order: 1;
  min-width: 42px;
  margin: 0;
  color: var(--text);
  font-size: clamp(1.75rem, 2.3vw, 2.45rem);
  line-height: 1;
}

.summary-box span {
  order: 2;
  min-width: 0;
  color: var(--muted);
  font-size: 0.78rem;
  font-weight: 800;
  text-transform: none;
}

.summary-box.metric-warning::before { background: var(--warning); }
.summary-box.metric-danger::before { background: var(--danger); }
.summary-box.metric-success::before { background: var(--success); }

.summary-box.metric-warning strong { color: var(--warning); }
.summary-box.metric-danger strong { color: var(--danger); }
.summary-box.metric-success strong { color: var(--success); }

.work-grid {
  margin-top: 0;
}

.board-panel {
  min-height: calc(100vh - 196px);
  padding: 12px;
}

.trello-board-panel {
  min-height: calc(100vh - 96px);
  padding: 28px 32px 32px;
  background: transparent !important;
  box-shadow: none;
}

.board-compact-head {
  display: grid;
  grid-template-columns: minmax(160px, 260px) minmax(0, 1fr);
  align-items: center;
  gap: 20px;
  margin-bottom: 16px;
  padding: 14px 20px;
  border: 1px solid var(--line);
  border-radius: var(--radius);
  background: var(--panel);
}

.trello-toolbar {
  grid-template-columns: minmax(220px, 1fr) minmax(150px, 180px) minmax(150px, 180px);
  gap: 10px;
  margin-bottom: 0;
  padding: 0;
  border: 0;
  background: transparent;
}

.resource-screen {
  min-height: calc(100vh - 226px);
  padding: 28px 32px;
  border: 0;
  border-radius: 0;
  background: transparent;
  box-shadow: none;
}

.resource-screen.dashboard-screen {
  min-height: 0;
  padding: 28px 32px 20px;
}

.resource-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 24px;
  padding-bottom: 20px;
  border-bottom: 1px solid var(--line);
}

.dashboard-screen .resource-header {
  margin-bottom: 6px;
  padding-bottom: 8px;
}

.dashboard-screen .resource-header h2 {
  margin: 2px 0 2px;
  font-size: 1.45rem;
}

.dashboard-screen .resource-header p {
  font-size: 0.86rem;
  line-height: 1.35;
}

.resource-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 10px;
}

.request-toolbar {
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  margin-bottom: 16px;
}

.report-toolbar {
  grid-template-columns: 160px minmax(220px, 320px);
  justify-content: start;
}

.report-metrics {
  margin: 14px 0;
}

.report-table th:not(:first-child),
.report-table td:not(:first-child) {
  min-width: 76px;
  text-align: right;
}

.report-table td strong {
  display: block;
}

.report-table td small {
  color: var(--muted);
  font-weight: 700;
}

.request-detail-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(240px, 300px);
  gap: 18px;
  align-items: start;
}

.request-main-column,
.request-side-column {
  min-width: 0;
}

.request-side-column {
  display: grid;
  gap: 12px;
}

.request-side-panel {
  display: grid;
  gap: 2px;
  padding: 12px;
  border: 1px solid rgba(33, 28, 20, 0.1);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.56);
}

.request-side-panel h3 {
  margin: 0 0 8px;
  color: var(--text);
  font-size: 0.95rem;
  font-weight: 800;
}

.linked-demands {
  padding: 12px;
  border: 1px solid rgba(33, 28, 20, 0.1);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.56);
}

.linked-demand-item {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
  padding: 12px 0;
  border: 0;
  border-bottom: 1px solid rgba(33, 28, 20, 0.08);
  background: transparent;
  color: inherit;
  cursor: pointer;
  font: inherit;
  text-align: left;
  transition: background 0.15s ease, padding 0.15s ease, border-color 0.15s ease;
}

.linked-demand-item:hover,
.linked-demand-item:focus-visible {
  padding-inline: 10px;
  border-color: var(--accent-border);
  border-radius: var(--radius);
  background: var(--accent-subtle);
  outline: 2px solid var(--accent-border);
  outline-offset: 2px;
}

.linked-demand-item strong {
  color: var(--text);
  line-height: 1.25;
}

.linked-demand-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.request-chat {
  min-width: 0;
  margin-top: 14px;
  padding-top: 18px;
  border-top: 1px solid rgba(33, 28, 20, 0.12);
}

.request-chat h3 {
  margin: 0 0 14px;
}

.chat-list {
  display: grid;
  gap: 10px;
  margin-bottom: 14px;
}

.chat-message {
  width: min(92%, 520px);
  padding: 12px 14px;
  border: 1px solid rgba(33, 28, 20, 0.1);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.72);
}

.chat-message.mine {
  justify-self: end;
  border-color: rgba(0, 122, 96, 0.22);
  background: rgba(0, 122, 96, 0.08);
}

.chat-message.email-log {
  justify-self: stretch;
  width: 100%;
  border-color: rgba(37, 99, 235, 0.18);
  background: rgba(37, 99, 235, 0.07);
}

.chat-message.email-log strong {
  color: #1d4ed8;
}

.chat-message p {
  margin: 6px 0;
  color: #2b2925;
}

.message-attachments {
  display: grid;
  gap: 6px;
  margin: 8px 0;
}

.message-attachment {
  display: grid;
  grid-template-columns: 34px minmax(0, 1fr) auto;
  gap: 8px;
  align-items: center;
  padding: 7px;
  border: 1px solid rgba(13, 124, 102, 0.16);
  border-radius: 8px;
  background: rgba(255, 253, 248, 0.72);
  color: var(--text);
  text-decoration: none;
}

.message-attachment > span {
  display: grid;
  width: 34px;
  height: 28px;
  place-items: center;
  border-radius: 7px;
  background: rgba(13, 124, 102, 0.1);
  color: var(--cyan);
  font-size: 0.68rem;
  font-weight: 900;
}

.message-attachment strong {
  overflow: hidden;
  font-size: 0.78rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.message-attachment small {
  color: var(--muted);
  font-size: 0.72rem;
  font-weight: 800;
}

.request-attachment-box {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: -6px 0 10px;
}

.request-attachment-button {
  display: inline-grid;
  width: 32px;
  height: 32px;
  place-items: center;
  border: 1px solid rgba(37, 34, 29, 0.12);
  border-radius: 8px;
  background: rgba(255, 253, 248, 0.82);
  color: var(--cyan);
  font-size: 0.95rem;
  line-height: 1;
}

.request-attachment-button:hover,
.request-attachment-button:focus {
  border-color: rgba(13, 124, 102, 0.34);
  background: rgba(13, 124, 102, 0.08);
  outline: none;
}

.request-attachment-box > span {
  color: var(--muted);
  font-size: 0.76rem;
  font-weight: 700;
}

.request-attachment-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  min-width: 0;
}

.request-attachment-chips > span {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 28px;
  max-width: 100%;
  padding: 0 8px;
  border-radius: 999px;
  background: rgba(13, 124, 102, 0.1);
  color: var(--text);
  font-size: 0.76rem;
  font-weight: 800;
}

.request-attachment-chips button {
  border: 0;
  background: transparent;
  color: var(--danger);
  font-weight: 900;
}

.request-send-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.request-send-waiting {
  min-height: 36px;
  padding: 0 12px;
  border: 1px solid rgba(183, 121, 31, 0.28);
  border-radius: 8px;
  background: rgba(183, 121, 31, 0.08);
  color: #8a5a17;
  font-size: 0.78rem;
  font-weight: 900;
  text-transform: uppercase;
}

.request-send-waiting:disabled {
  cursor: wait;
  opacity: 0.58;
}

.resource-header span,
.form-kicker {
  color: var(--muted);
  font-size: 0.78rem;
  font-weight: 500;
}

.resource-header h2 {
  margin: 4px 0 6px;
  color: var(--text);
  font-size: 1.75rem;
  font-weight: 700;
  letter-spacing: -0.02em;
}

.resource-header p {
  max-width: 600px;
  margin: 0;
  color: var(--muted);
  font-size: 0.9rem;
}

.dashboard-screen {
  display: grid;
  align-content: start;
  gap: 10px;
}

.dashboard-metrics {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
}

.dashboard-metric {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 64px;
  padding: 12px 16px;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: var(--panel);
  border-left-width: 3px;
}

.dashboard-metric span {
  color: var(--muted);
  font-size: 0.72rem;
  font-weight: 800;
}

.dashboard-metric strong {
  color: var(--text);
  font-size: 1.35rem;
  line-height: 1;
}

.dashboard-metric.metric-info { border-left-color: var(--accent); }
.dashboard-metric.metric-progress { border-left-color: #2563eb; }
.dashboard-metric.metric-warning { border-left-color: var(--warning); }
.dashboard-metric.metric-success { border-left-color: var(--success); }

.dashboard-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 10px;
}

.dashboard-panel {
  border: 1px solid var(--line);
  border-radius: 8px;
  background: var(--panel);
  overflow: hidden;
  box-shadow: none;
}

.dashboard-panel .table-toolbar button {
  min-height: 30px;
  padding: 0 10px;
  border: 1px solid var(--line);
  border-radius: var(--radius);
  background: var(--panel);
  color: var(--text);
  font-size: 0.76rem;
  font-weight: 500;
}

.dashboard-list {
  display: grid;
  gap: 5px;
  padding: 6px;
}

.dashboard-list-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid hsl(var(--border));
  border-radius: 8px;
  background: hsl(var(--secondary) / 0.4);
  color: var(--text);
  text-align: left;
}

.dashboard-list-item:hover {
  border-color: hsl(var(--ring) / 0.5);
  background: hsl(var(--secondary) / 0.72);
}

.dashboard-item-main {
  display: grid;
  min-width: 0;
  gap: 4px;
}

.dashboard-list-item strong {
  overflow: hidden;
  font-size: 0.9rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dashboard-list-item small,
.dashboard-empty {
  color: var(--muted);
  font-size: 0.8rem;
  font-weight: 700;
}

.dashboard-badges {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 6px;
}

.status-badge,
.priority-badge {
  flex: none;
  min-height: 24px;
  padding: 4px 8px;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 900;
  line-height: 1;
}

.status-open {
  background: rgba(96, 165, 250, 0.16);
  color: #93c5fd;
}

.status-review {
  background: rgba(245, 158, 11, 0.16);
  color: #fcd34d;
}

.status-progress {
  background: rgba(45, 212, 191, 0.16);
  color: #5eead4;
}

.status-waiting {
  background: rgba(245, 158, 11, 0.16);
  color: #fcd34d;
}

.status-done {
  background: rgba(74, 222, 128, 0.16);
  color: #86efac;
}

.status-canceled {
  background: rgba(248, 113, 113, 0.16);
  color: #fca5a5;
}

.request-status-chip.v-chip {
  min-width: max-content;
  border: 1px solid currentColor !important;
  font-size: 0.72rem;
  text-transform: none;
}

.request-status-chip.v-chip.status-open {
  background: rgba(37, 99, 235, 0.1) !important;
  color: #1d4ed8 !important;
}

.request-status-chip.v-chip.status-review {
  background: rgba(183, 121, 31, 0.14) !important;
  color: #9a5d13 !important;
}

.request-status-chip.v-chip.status-progress {
  background: rgba(13, 124, 102, 0.12) !important;
  color: #0d7c66 !important;
}

.request-status-chip.v-chip.status-waiting {
  background: rgba(183, 121, 31, 0.14) !important;
  color: #9a5d13 !important;
}

.request-status-chip.v-chip.status-done {
  background: rgba(35, 133, 89, 0.13) !important;
  color: #238559 !important;
}

.request-status-chip.v-chip.status-canceled {
  background: rgba(188, 68, 62, 0.12) !important;
  color: #bc443e !important;
}

.request-status-chip .v-chip__content {
  color: inherit !important;
}

.priority-alta {
  background: rgba(188, 68, 62, 0.12);
  color: #bc443e;
}

.priority-media {
  background: rgba(183, 121, 31, 0.14);
  color: #9a5d13;
}

.priority-baixa {
  background: rgba(35, 133, 89, 0.13);
  color: #238559;
}

.dashboard-empty {
  padding: 20px 12px;
}

.table-panel {
  border: 1px solid var(--line);
  border-radius: 8px;
  background: var(--panel);
  overflow: hidden;
}

.user-tabs {
  display: inline-grid;
  grid-template-columns: repeat(2, minmax(132px, 1fr));
  gap: 4px;
  margin: 12px 14px 0;
  padding: 4px;
  border: 1px solid var(--line);
  border-radius: var(--radius);
  background: var(--panel-2);
}

.user-tabs button {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 38px;
  padding: 0 10px;
  border: 1px solid transparent;
  border-radius: 7px;
  color: var(--muted);
  font-weight: 800;
}

.user-tabs button.active,
.user-tabs button:hover {
  border-color: var(--accent-border);
  background: var(--accent-subtle);
  color: var(--text);
}

.user-tabs button span {
  display: grid;
  min-width: 24px;
  height: 24px;
  place-items: center;
  border-radius: 999px;
  background: rgba(37, 34, 29, 0.08);
  color: var(--muted);
  font-size: 0.75rem;
}

.user-tabs button.active span {
  background: var(--accent);
  color: #ffffff;
}

.table-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 54px;
  padding: 12px 14px;
  border-bottom: 1px solid var(--line);
  color: var(--muted);
}

.table-toolbar strong {
  color: var(--text);
  font-weight: 800;
}

.table-toolbar span {
  font-size: 0.82rem;
  font-weight: 700;
}

.resource-table {
  width: 100%;
  border-collapse: collapse;
  background: var(--panel);
}

.resource-table th,
.resource-table td {
  padding: 13px 14px;
  border-bottom: 1px solid var(--line);
  color: var(--muted);
  font-size: 0.85rem;
  text-align: left;
  vertical-align: middle;
}

.resource-table th {
  background: var(--panel-2);
  color: var(--muted);
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.resource-table td strong {
  display: block;
  overflow: hidden;
  color: var(--text);
  font-size: 0.9rem;
  font-weight: 800;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.resource-table td small {
  display: block;
  overflow: hidden;
  margin-top: 3px;
  color: var(--muted);
  font-size: 0.8rem;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.resource-table tr:last-child td {
  border-bottom: 0;
}

.resource-link {
  margin: 0;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  font-weight: 800;
  text-align: left;
  cursor: pointer;
}

.resource-link:hover {
  color: var(--accent);
  text-decoration: underline;
}

.request-title-link {
  display: block;
}

.actions-col {
  width: 210px;
}

.actions-cell {
  text-align: right;
  white-space: nowrap;
}

.actions-cell button {
  min-height: 30px;
  margin-left: 6px;
  padding: 0 10px;
  border: 1px solid var(--line);
  border-radius: var(--radius);
  background: var(--panel);
  color: var(--text);
  font-size: 0.76rem;
  font-weight: 500;
}

.actions-cell button:hover {
  border-color: var(--accent-border);
  background: var(--accent-subtle);
  color: var(--accent);
}

.resource-empty {
  display: grid;
  min-height: 160px;
  margin: 12px;
  place-items: center;
  border: 1px dashed rgba(37, 34, 29, 0.16);
  border-radius: 8px;
  color: var(--muted);
  font-weight: 700;
  text-align: center;
}

/* Rascunhos */
.bulk-actions-group {
  display: flex;
  align-items: center;
  gap: 4px;
}

.bulk-drafts-btn.active {
  border-color: var(--accent);
  color: var(--accent);
  background: var(--accent-subtle);
}

.draft-count-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  margin-left: 6px;
  padding: 0 5px;
  border-radius: 999px;
  background: var(--accent);
  color: #fff;
  font-size: 0.7rem;
  font-weight: 700;
}

.drafts-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
}

.draft-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 16px;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: var(--panel);
}

.draft-item-main {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.draft-item-title {
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--text);
}

.draft-edit-input {
  border: 0;
  border-bottom: 1px solid var(--accent);
  outline: none;
  font: inherit;
  font-size: 0.9rem;
  background: transparent;
  color: var(--text);
  width: 300px;
}

.draft-item-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.78rem;
  color: var(--muted);
}

.draft-priority {
  font-size: 0.72rem;
  font-weight: 600;
  padding: 1px 6px;
  border-radius: 4px;
}

.draft-priority.priority-alta { background: rgba(220,38,38,0.08); color: var(--danger); }
.draft-priority.priority-media { background: rgba(245,158,11,0.1); color: #b45309; }
.draft-priority.priority-baixa { background: rgba(22,163,74,0.08); color: #16a34a; }

.draft-author { color: var(--muted); font-style: italic; }

.draft-item-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.draft-publish-group {
  display: flex;
  align-items: center;
  gap: 6px;
}

.draft-status-select {
  width: 140px;
}

.draft-status-select .v-input__slot { min-height: 32px !important; }
.draft-status-select .v-select__selections { padding: 0 !important; font-size: 0.8rem; }

.draft-btn {
  height: 32px;
  padding: 0 12px;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
}

.draft-btn-edit {
  border: 1px solid var(--line);
  background: transparent;
  color: var(--muted);
}

.draft-btn-edit:hover { border-color: var(--accent); color: var(--accent); }

.draft-btn-publish {
  border: 0;
  background: var(--accent);
  color: hsl(var(--primary-foreground)) !important;
}

.draft-btn-publish:disabled { opacity: 0.6; cursor: not-allowed; }

.draft-btn-delete {
  border: 1px solid transparent;
  background: transparent;
  color: var(--muted);
}

.draft-btn-delete:hover { color: var(--danger); border-color: var(--danger); }

.link-existing-demand {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 10px;
  align-items: center;
  margin: 12px 0;
}

.demand-request-link {
  grid-template-columns: 1fr;
  align-items: stretch;
  gap: 8px;
  margin: 0;
}

.demand-request-link h3 {
  margin-bottom: 0;
  white-space: nowrap;
}

.demand-request-link .v-input {
  width: 100%;
}

.demand-request-link .v-btn {
  margin-top: 0;
}

.board-header {
  align-items: center;
  margin-bottom: 16px;
  padding: 0;
  border: 0;
  background: transparent;
}

.board-header h2 {
  margin: 4px 0 6px;
  color: var(--text);
  font-size: 1.75rem;
  font-weight: 700;
  letter-spacing: -0.02em;
}

.board-header p {
  margin: 0;
  color: var(--muted);
  font-size: 0.9rem;
  line-height: 1.4;
}

.board-context-strip {
  display: grid;
  gap: 3px;
  min-width: 0;
  margin-bottom: 0;
  padding: 0;
  color: var(--muted);
  font-size: 0.72rem;
}

.board-context-strip strong {
  overflow: hidden;
  color: var(--text);
  font-size: 1.1rem;
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1.2;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.board-context-strip span {
  overflow: hidden;
  color: var(--muted);
  font-weight: 500;
  font-size: 0.78rem;
  line-height: 1.3;
  text-overflow: ellipsis;
  text-transform: none;
  white-space: nowrap;
}

.toolbar {
  grid-template-columns: minmax(260px, 1fr) minmax(190px, 240px) 168px 168px;
  gap: 10px;
  padding: 0;
  border: 0;
  background: transparent;
}

.board-actions {
  gap: 8px;
}

.new-demand-button {
  min-width: 100px;
  min-height: 32px !important;
  border-radius: 6px !important;
  font-size: 0.8rem !important;
  font-weight: 600 !important;
  text-transform: none !important;
}

.new-demand-button.action-primary {
  background: var(--accent) !important;
  color: #ffffff !important;
}

.new-demand-button.action-secondary {
  border-color: var(--line) !important;
  background: var(--panel) !important;
  color: var(--text) !important;
}

.new-demand-button .v-btn__content {
  color: inherit !important;
}

.theme--light.v-btn.primary,
.v-application .primary.v-btn {
  background: var(--accent) !important;
  color: #ffffff !important;
  border-radius: 8px !important;
  font-weight: 600 !important;
  letter-spacing: 0 !important;
  text-transform: none !important;
}

.theme--light.v-btn.primary:hover,
.v-application .primary.v-btn:hover {
  background: var(--accent-hover) !important;
}

.theme--light.v-btn.primary .v-btn__content,
.v-application .primary.v-btn .v-btn__content,
.btn-glow,
.btn-glow * {
  color: #ffffff !important;
}

.v-btn.v-btn--outlined.primary--text .v-btn__content {
  color: var(--accent) !important;
}

.v-btn:not(.primary):not(.v-btn--icon) {
  border-radius: 8px !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
  font-weight: 500 !important;
}

.status-tabs {
  gap: 8px;
  margin: 12px 0;
}

.status-tabs .v-btn {
  border-radius: 8px;
  text-transform: none;
}

.kanban-board {
  gap: 10px;
  padding-bottom: 16px;
}

.kanban-column {
  border: 0;
  box-shadow: none;
}

.kanban-column::before {
  display: none;
}

.kanban-column.is-pending { background: #f1f0ee !important; }
.kanban-column.is-progress { background: #f1f0ee !important; }
.kanban-column.is-done { background: #f1f0ee !important; }

.kanban-column-header > div:first-child {
  display: flex;
  align-items: center;
  min-height: 30px;
  gap: 7px;
}

.kanban-column-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(37, 34, 29, 0.09);
}

.column-header-add {
  display: grid;
  place-items: center;
  width: 22px;
  height: 22px;
  flex-shrink: 0;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: var(--muted);
  transition: background 0.15s, color 0.15s;
}

.column-header-actions {
  display: flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
}

.column-menu-button {
  display: grid;
  place-items: center;
  width: 22px;
  height: 22px;
  flex-shrink: 0;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: var(--muted);
  transition: background 0.15s, color 0.15s;
}

.column-menu-button:hover {
  background: var(--accent-subtle);
  color: var(--accent);
}

.column-header-add:hover {
  background: var(--accent-subtle);
  color: var(--accent);
}

.kanban-column-header span {
  color: var(--text);
  font-size: 0.84rem;
  text-transform: none;
}

.kanban-column-header .column-count,
.kanban-column-header .column-hours {
  display: inline-flex !important;
  align-items: center;
  min-width: 0;
  height: auto;
  padding: 3px 8px;
  border: 1px solid var(--line);
  border-radius: 999px;
  background: transparent;
  color: var(--muted);
  font-size: 0.72rem;
  font-weight: 500;
  line-height: 1;
}

.list-dot,
.card-project-dot {
  border-color: var(--cyan);
}

.kanban-column-description,
.kanban-empty {
  color: var(--muted);
}

.kanban-column-description {
  display: none;
}

.kanban-empty {
  min-height: 72px;
  border-color: rgba(37, 34, 29, 0.14);
  background: transparent;
  font-size: 0.82rem;
}

.demand-card {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0;
  margin-bottom: 8px;
  padding: 0;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: var(--panel);
  color: var(--text);
  box-shadow: 0 1px 2px rgba(0,0,0,0.06);
  cursor: pointer;
  transition: box-shadow 0.15s, border-color 0.15s;
  overflow: hidden;
}

.demand-card:hover {
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  border-color: var(--accent-border);
}

.demand-card.completed {
  opacity: 0.7;
}

.card-labels {
  display: flex;
  gap: 4px;
  padding: 6px 10px 0;
}

.card-label {
  height: 4px;
  width: 36px;
  border-radius: 999px;
  background: var(--line);
}

.card-label.priority-alta { background: var(--danger); }
.card-label.priority-media { background: var(--warning); }
.card-label.priority-baixa { background: var(--success); }

.card-body {
  padding: 8px 10px 6px;
}

.card-title-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 6px;
}

.card-title {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text);
  line-height: 1.4;
}

.card-project-name {
  margin: 4px 0 0;
  color: var(--muted);
  font-size: 0.75rem;
  font-weight: 400;
  line-height: 1.3;
}

.card-menu-button {
  display: grid;
  place-items: center;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: var(--muted);
  opacity: 0;
  transition: opacity 0.14s, background 0.14s;
}

.demand-card:hover .card-menu-button {
  opacity: 1;
}

.card-menu-button:hover {
  background: var(--panel-2);
  color: var(--text);
}

.card-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  padding: 6px 10px 8px;
  border-top: 1px solid var(--line);
  margin-top: 4px;
}

.card-footer-left,
.card-footer-right {
  display: flex;
  align-items: center;
  gap: 6px;
}

.demand-display-id {
  color: var(--muted);
  font-size: 0.7rem;
  font-weight: 500;
}

.card-meta-icon {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  color: var(--muted);
  font-size: 0.7rem;
}

.card-hour-tag {
  display: inline-flex;
  align-items: center;
  min-height: 20px;
  padding: 2px 6px;
  border-radius: 999px;
  color: hsl(var(--foreground));
  font-size: 0.7rem;
  font-weight: 700;
  line-height: 1;
}

.card-due {
  font-size: 0.72rem;
  font-weight: 500;
  color: var(--muted);
  padding: 2px 6px;
  border-radius: 4px;
  background: var(--panel-2);
}

.card-due.done {
  background: rgba(22, 163, 74, 0.08);
  color: #16a34a;
}

.card-due.overdue {
  background: rgba(220, 38, 38, 0.08);
  color: var(--danger);
}

.card-avatar {
  display: grid;
  place-items: center;
  width: 22px;
  height: 22px;
  border-radius: 999px;
  background: var(--accent-subtle);
  color: var(--accent);
  font-size: 0.68rem;
  font-weight: 700;
}

.meta-row {
  gap: 6px;
  margin-top: 9px;
}

.request-age-pill,
.priority-pill,
.muted-meta {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  min-height: 20px;
  padding: 0 7px;
  border-radius: 999px;
  font-size: 0.68rem;
  font-weight: 850;
  line-height: 1;
}

.request-age-pill {
  border: 1px solid transparent;
}

.request-age-pill > span {
  width: 6px;
  height: 6px;
  border-radius: 999px;
  background: currentColor;
}

.request-age-pill.age-fresh {
  border-color: rgba(13, 124, 102, 0.18);
  background: rgba(13, 124, 102, 0.1);
  color: #0d7c66;
}

.request-age-pill.age-warning {
  border-color: rgba(183, 121, 31, 0.22);
  background: rgba(183, 121, 31, 0.13);
  color: #8a5a17;
}

.request-age-pill.age-critical {
  border-color: rgba(176, 69, 88, 0.24);
  background: rgba(176, 69, 88, 0.12);
  color: #b04558;
}

.priority-pill {
  background: rgba(13, 124, 102, 0.1);
  color: var(--cyan);
}

.priority-pill.priority-alta {
  background: rgba(176, 69, 88, 0.11);
  color: var(--danger);
}

.priority-pill.priority-media {
  background: rgba(183, 121, 31, 0.12);
  color: #8a5a17;
}

.priority-pill.priority-baixa {
  background: rgba(13, 124, 102, 0.1);
  color: var(--cyan);
}

.muted-meta {
  background: rgba(37, 34, 29, 0.055);
  color: var(--muted);
}


.modal-card {
  background: var(--panel) !important;
  color: var(--text) !important;
}

.admin-tabs {
  border-color: var(--line);
  background: var(--panel-2);
}

.admin-tabs button {
  color: var(--muted);
}

.admin-tabs button.active {
  background: var(--accent);
  color: #ffffff;
}

.side-item,
.note-item,
.attachment-item {
  border-color: var(--line);
  background: var(--panel-2);
}

.side-item strong,
.note-item > span {
  color: var(--text);
}

.side-item span,
.note-item small,
.attachment-item span,
.attachment-item button,
.muted {
  color: var(--muted);
}

.detail-notes,
.admin-list,
.side-list.compact {
  border-color: var(--line);
}

.mention-popover,
.card-menu-list {
  border-color: var(--line);
  background: var(--panel) !important;
  color: var(--text) !important;
}

.card-menu-list .v-list-item__title {
  color: var(--text);
}

.v-text-field.v-text-field--outlined fieldset,
.v-select.v-text-field--outlined fieldset,
.v-textarea.v-text-field--outlined fieldset {
  border-color: var(--line);
  border-radius: 8px !important;
}

.v-text-field--outlined .v-input__slot,
.v-select.v-text-field--outlined .v-input__slot,
.v-textarea.v-text-field--outlined .v-input__slot {
  background: var(--panel-2) !important;
}

.v-text-field input,
.v-textarea textarea,
.v-select__selection,
.v-label,
.v-input,
.v-icon {
  color: var(--text) !important;
}

.theme--light.v-input input,
.theme--light.v-input textarea {
  color: var(--text) !important;
}

.theme--light.v-label {
  color: var(--muted) !important;
}

.v-text-field--outlined.v-input--is-focused fieldset,
.v-select.v-input--is-focused fieldset,
.v-textarea.v-input--is-focused fieldset {
  border-color: var(--accent) !important;
}

.v-select__slot .v-input__append-inner .v-icon,
.v-input__append-inner .v-icon {
  color: var(--muted) !important;
}

.v-chip.primary,
.v-btn.primary {
  background: var(--accent) !important;
  color: #ffffff !important;
}

.v-btn.v-btn--outlined.primary--text {
  border-color: var(--accent-border) !important;
  color: var(--accent) !important;
}

.modal-close span {
  color: var(--text);
}

.product-shell {
  background: var(--bg);
}

.topbar {
  min-height: 56px;
  margin: 0;
  border-bottom: 1px solid var(--line);
  background: rgba(255, 255, 255, 0.94);
  backdrop-filter: blur(10px);
}

.mobile-menu-button,
.account-trigger,
.project-context-select,
.btn-glow,
.btn-ghost,
.new-demand-button,
.draft-btn,
.inline-demand-actions button,
.add-list-card,
.dashboard-panel .table-toolbar button {
  border-radius: 6px !important;
}

.mobile-menu-button,
.account-trigger,
.project-context-select {
  border: 1px solid var(--line);
  background: var(--panel);
}

.mobile-menu-button:hover,
.account-trigger:hover,
.project-context-select:hover {
  background: var(--panel-2);
}

.btn-glow,
.theme--light.v-btn.primary,
.v-application .primary.v-btn,
.v-chip.primary,
.v-btn.primary {
  background: var(--accent) !important;
  color: hsl(var(--primary-foreground)) !important;
  box-shadow: var(--shadow) !important;
}

.btn-glow:hover,
.theme--light.v-btn.primary:hover,
.v-application .primary.v-btn:hover {
  background: var(--accent-hover) !important;
}

.btn-ghost,
.new-demand-button.action-secondary,
.v-btn.v-btn--outlined.primary--text {
  border: 1px solid var(--line) !important;
  background: var(--panel) !important;
  color: var(--text) !important;
}

.btn-ghost:hover,
.new-demand-button.action-secondary:hover,
.v-btn.v-btn--outlined.primary--text:hover {
  border-color: hsl(var(--ring) / 0.28) !important;
  background: var(--panel-2) !important;
  color: var(--text) !important;
}

.workspace-rail {
  border-right: 1px solid var(--sidebar-line);
  background: var(--sidebar-bg);
}

.workspace-rail * {
  color: var(--sidebar-text);
}

.sidebar-brand-lockup .brand-wordmark {
  color: var(--sidebar-text);
}

.sidebar-brand-lockup span,
.rail-section-label {
  color: var(--sidebar-muted);
}

.workspace-rail .brand-mark {
  background: var(--accent);
  color: hsl(var(--primary-foreground));
}

.rail-item {
  min-height: 38px;
  border: 1px solid transparent;
  border-radius: 6px;
  color: var(--sidebar-text);
}

.rail-item:hover {
  border-color: var(--sidebar-line);
  background: hsl(var(--background) / 0.72);
}

.rail-item.active {
  border-color: var(--sidebar-line);
  background: var(--sidebar-active-bg);
  box-shadow: var(--shadow);
}

.rail-item.active strong,
.rail-item.active .rail-icon {
  color: var(--sidebar-active);
}

.workspace-content {
  padding: 22px 32px 32px;
}

.resource-screen,
.board-panel,
.table-panel,
.dashboard-panel,
.dashboard-metric,
.login-card,
.modal-card {
  border: 1px solid var(--line) !important;
  border-radius: var(--radius) !important;
  background: var(--panel) !important;
  box-shadow: var(--shadow) !important;
}

.resource-screen,
.board-panel,
.dashboard-panel {
  padding: 20px;
}

.login-shell {
  min-height: 100vh;
  padding: 24px;
  background:
    linear-gradient(180deg, hsl(var(--muted-token) / 0.45), transparent 36%),
    var(--bg);
}

.login-card {
  backdrop-filter: none;
}

.login-card h1,
.resource-header h2,
.board-header h2,
.board-context-strip strong {
  color: var(--text);
  font-weight: 700;
}

.eyebrow,
.section-title span,
.board-header span,
.resource-header span,
.modal-card .v-card__title span,
.source-attachments-header span {
  color: var(--muted);
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: none;
}

.dashboard-metric {
  border-left-width: 1px;
}

.dashboard-metric strong {
  color: var(--text);
}

.dashboard-metric span,
.dashboard-list-item small,
.dashboard-empty,
.resource-header p,
.board-header p,
.board-context-strip span {
  color: var(--muted);
}

.table-panel {
  overflow: hidden;
}

.resource-table {
  background: var(--panel);
}

.resource-table th {
  background: var(--panel-2);
  color: var(--muted);
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: none;
}

.resource-table td {
  color: var(--text);
}

.resource-table tbody tr:hover td,
.dashboard-list-item:hover {
  background: hsl(var(--muted-token) / 0.58);
}

.kanban-board {
  gap: 12px;
}

.kanban-column {
  border: 1px solid var(--line) !important;
  background: var(--panel-2) !important;
}

.kanban-column.is-pending,
.kanban-column.is-progress,
.kanban-column.is-done {
  background: var(--panel-2) !important;
}

.kanban-column-header {
  border-bottom-color: var(--line);
}

.column-header-add,
.column-menu-button,
.card-menu-button {
  border-radius: 6px;
}

.column-header-add:hover,
.column-menu-button:hover,
.card-menu-button:hover {
  background: var(--accent-subtle);
  color: var(--text);
}

.demand-card,
.side-item,
.note-item,
.attachment-item,
.source-attachment-item,
.detail-side-panel,
.profile-summary,
.draft-item {
  border: 1px solid var(--line) !important;
  border-radius: var(--radius) !important;
  background: var(--panel) !important;
  color: var(--text) !important;
  box-shadow: var(--shadow);
}

.demand-card:hover {
  border-color: hsl(var(--ring) / 0.24) !important;
  box-shadow: var(--shadow-md);
}

.card-footer,
.note-panel,
.detail-notes,
.bulk-demand-review {
  border-color: var(--line);
}

.status-badge,
.priority-pill,
.muted-meta,
.request-age-pill,
.card-hour-tag,
.card-due,
.v-chip:not(.primary) {
  border: 1px solid var(--line) !important;
  background: var(--panel-2) !important;
  color: var(--muted) !important;
}

.priority-pill.priority-alta,
.request-age-pill.age-critical,
.card-due.overdue {
  border-color: rgba(220, 38, 38, 0.18) !important;
  background: rgba(220, 38, 38, 0.08) !important;
  color: var(--danger) !important;
}

.priority-pill.priority-media,
.request-age-pill.age-warning {
  border-color: rgba(217, 119, 6, 0.18) !important;
  background: rgba(217, 119, 6, 0.08) !important;
  color: #92400e !important;
}

.priority-pill.priority-baixa,
.request-age-pill.age-fresh,
.card-due.done {
  border-color: rgba(22, 163, 74, 0.18) !important;
  background: rgba(22, 163, 74, 0.08) !important;
  color: var(--success) !important;
}

.v-text-field.v-text-field--outlined fieldset,
.v-select.v-text-field--outlined fieldset,
.v-textarea.v-text-field--outlined fieldset {
  border-color: hsl(var(--input)) !important;
  border-radius: 6px !important;
}

.v-text-field--outlined .v-input__slot,
.v-select.v-text-field--outlined .v-input__slot,
.v-textarea.v-text-field--outlined .v-input__slot {
  background: var(--panel) !important;
  min-height: 38px !important;
}

.v-text-field--outlined.v-input--is-focused fieldset,
.v-select.v-input--is-focused fieldset,
.v-textarea.v-input--is-focused fieldset {
  border-color: hsl(var(--ring)) !important;
  box-shadow: 0 0 0 2px hsl(var(--ring) / 0.12);
}

.v-menu__content,
.account-popover,
.project-context-popover,
.mention-popover,
.card-menu-list {
  border: 1px solid var(--line) !important;
  border-radius: 8px !important;
  background: hsl(var(--popover)) !important;
  color: hsl(var(--popover-foreground)) !important;
  box-shadow: var(--shadow-md) !important;
}

.account-popover button:hover,
.project-context-popover button:hover,
.project-context-popover button.active,
.card-menu-list .v-list-item:hover {
  background: var(--panel-2) !important;
  color: var(--text) !important;
}

/* shadcn structural dark refactor */
.v-application {
  background: var(--bg) !important;
}

.app-page,
.product-shell,
.workspace-main {
  background: var(--bg) !important;
  color: var(--text);
}

.product-shell {
  --sidebar-width: 252px;
  --sidebar-collapsed-width: 64px;
  padding-left: var(--sidebar-collapsed-width);
}

.product-shell.sidebar-active {
  padding-left: var(--sidebar-width);
}

.topbar {
  position: sticky;
  top: 0;
  z-index: 18;
  min-height: 52px;
  padding: 0 18px;
  border-bottom: 1px solid var(--line);
  background: hsl(var(--background) / 0.9);
  backdrop-filter: blur(14px);
}

.topbar-actions {
  margin-left: auto;
}

.top-actions,
.bulk-actions-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.workspace-rail {
  width: var(--sidebar-collapsed-width);
  padding: 10px 10px 18px;
  border-right: 1px solid var(--line);
  background: var(--sidebar-bg);
}

.workspace-rail.open {
  width: var(--sidebar-width);
  padding: 10px 12px 18px;
}

.sidebar-mobile-head {
  height: 52px;
  border-bottom-color: var(--line);
}

.brand-mark {
  width: 32px;
  height: 32px;
  border: 1px solid hsl(var(--border));
  border-radius: 7px;
  background: hsl(0 0% 9%);
  color: var(--text);
}

.workspace-rail .brand-mark {
  background: hsl(0 0% 9%);
  color: var(--text);
}

.brand-wordmark,
.sidebar-brand-lockup .brand-wordmark {
  color: var(--text);
}

.login-brand span,
.brand-lockup span,
.sidebar-brand-lockup span,
.rail-section-label {
  color: var(--muted);
}

.rail-section {
  gap: 4px;
}

.rail-section + .rail-section {
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid var(--line);
}

.rail-item {
  min-height: 36px;
  grid-template-columns: 32px minmax(0, 1fr) auto;
  gap: 8px;
  padding: 6px 8px;
  border: 1px solid transparent;
  border-radius: 6px;
  color: var(--muted);
}

.workspace-rail:not(.open) .rail-item {
  grid-template-columns: 32px;
  justify-content: center;
  padding: 6px;
}

.rail-icon {
  width: 30px;
  height: 30px;
  border-radius: 6px;
  background: transparent;
  color: var(--muted);
}

.rail-item strong {
  color: inherit;
  font-size: 0.83rem;
  font-weight: 600;
}

.rail-item:hover,
.rail-item.active {
  border-color: var(--line);
  background: hsl(var(--secondary) / 0.85);
  color: var(--text);
}

.rail-item.active .rail-icon {
  background: var(--accent);
  color: hsl(var(--primary-foreground));
}

.rail-projects {
  margin: 4px 0 4px 40px;
  padding-left: 10px;
  border-left: 1px solid var(--line);
}

.rail-projects::before {
  display: none;
}

.rail-project-item {
  min-height: 28px;
  border-radius: 5px;
  color: var(--muted);
  font-size: 0.78rem;
}

.rail-project-item:hover,
.rail-project-item.active {
  background: hsl(var(--secondary));
  color: var(--text);
}

.workspace-main {
  min-height: calc(100vh - 52px);
  padding: 18px;
}

.work-grid {
  max-width: none;
}

.resource-screen,
.board-panel,
.dashboard-panel,
.table-panel,
.dashboard-metric,
.login-card,
.modal-card {
  border: 1px solid var(--line) !important;
  border-radius: var(--radius) !important;
  background: var(--panel) !important;
  color: var(--text) !important;
  box-shadow: none !important;
}

.trello-board-panel {
  min-height: calc(100vh - 88px);
  padding: 16px;
  background: transparent !important;
  border-color: transparent !important;
}

.board-compact-head {
  display: grid;
  grid-template-columns: minmax(180px, 260px) minmax(280px, 1fr);
  gap: 12px;
  align-items: end;
  margin-bottom: 14px;
  padding: 14px 16px;
  border: 1px solid var(--line);
  border-radius: var(--radius);
  background: var(--panel);
}

.board-context-strip {
  gap: 2px;
}

.board-context-strip strong {
  color: var(--text);
  font-size: 1rem;
  font-weight: 700;
}

.board-context-strip span {
  color: var(--muted);
  font-size: 0.75rem;
}

.toolbar,
.request-toolbar {
  grid-template-columns: minmax(280px, 1fr) minmax(150px, 180px) minmax(130px, 160px);
  gap: 8px;
}

.request-toolbar {
  grid-template-columns: minmax(280px, 1fr) repeat(3, minmax(140px, 180px));
}

.kanban-board {
  gap: 12px;
  align-items: stretch;
  padding-bottom: 18px;
}

.kanban-workspace {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 12px;
  align-items: start;
  min-width: 0;
}

.kanban-workspace .kanban-board {
  min-width: 0;
}

.kanban-summary-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
  min-width: 0;
  padding: 12px 16px;
  border: 1px solid var(--line);
  border-radius: var(--radius);
  background: var(--panel);
}

.kanban-summary-title {
  display: grid;
  gap: 1px;
  flex: 0 1 auto;
  min-width: 0;
}

.kanban-summary-title span {
  color: var(--muted);
  font-size: 0.68rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.kanban-summary-title strong {
  overflow: hidden;
  color: var(--text);
  font-size: 0.95rem;
  font-weight: 800;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.kanban-summary-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 36px;
  flex: 1 1 auto;
  padding: 0 20px;
}

.kanban-summary-stats > div {
  display: grid;
  gap: 1px;
}

.kanban-summary-stats span {
  color: var(--muted);
  font-size: 0.68rem;
  font-weight: 700;
}

.kanban-summary-stats strong {
  color: var(--text);
  font-size: 1.05rem;
  font-weight: 800;
}

.kanban-summary-toggle {
  flex: 0 0 auto;
  height: 34px;
  padding: 0 14px;
  border: 1px solid var(--line);
  border-radius: 999px;
  background: transparent;
  color: var(--text);
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 140ms ease, background-color 140ms ease;
}

.kanban-summary-toggle:hover {
  border-color: var(--accent);
}

.kanban-summary-toggle.active {
  border-color: var(--accent);
  background: var(--panel-2);
}

.kanban-summary-toggle svg {
  flex-shrink: 0;
}

.kanban-summary-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
  margin-left: auto;
}

.kanban-summary-actions .btn-ghost,
.kanban-summary-actions .btn-glow {
  height: 34px;
  min-height: 34px;
  padding: 0 14px;
  border-radius: 999px;
  font-size: 0.8rem;
  white-space: nowrap;
}

.kanban-insights-panel {
  display: grid;
  gap: 12px;
  min-width: 0;
  padding: 4px 0 8px;
}

.kanban-insights-panel-expanded {
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
}

.insight-card,
.insight-grid > div {
  border: 1px solid var(--line);
  border-radius: var(--radius);
  background: var(--panel);
  color: var(--text);
}

.insight-card {
  display: grid;
  gap: 12px;
  padding: 14px;
}

.insight-card-primary {
  background:
    linear-gradient(135deg, hsl(var(--accent-token) / 0.72), transparent 64%),
    var(--panel);
}

.insight-card > span,
.insight-card header span,
.insight-grid span,
.priority-distribution > div > span:not(.priority-dot):not(.priority-name),
.focus-list button span,
.focus-list button small {
  color: var(--muted);
  font-size: 0.73rem;
  font-weight: 600;
}

.insight-card strong,
.insight-grid strong,
.focus-list button strong {
  color: var(--text);
}

.insight-card-primary strong {
  font-size: 1.05rem;
}

.insight-card p,
.focus-list p {
  margin: 0;
  color: var(--muted);
  font-size: 0.82rem;
  line-height: 1.45;
}

.insight-card header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.insight-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.insight-grid > div {
  display: grid;
  gap: 6px;
  min-height: 74px;
  padding: 12px;
}

.insight-grid strong {
  font-size: 1.35rem;
  line-height: 1;
}

.priority-distribution {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
}

.priority-distribution > div {
  display: flex !important;
  align-items: center !important;
  justify-content: center;
  gap: 6px;
  height: 30px !important;
  min-height: 0 !important;
  padding: 0 10px !important;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: hsl(var(--secondary) / 0.4);
  border-left: 3px solid var(--priority-color, var(--accent));
}

.priority-distribution > div > strong {
  color: var(--text);
  font-size: 0.95rem;
  font-weight: 800;
  line-height: 1 !important;
}

.priority-name {
  color: var(--muted);
  font-size: 0.68rem;
  font-weight: 700;
  line-height: 1 !important;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.priority-distribution .priority-alta { --priority-color: var(--danger); }
.priority-distribution .priority-media { --priority-color: var(--warning); }
.priority-distribution .priority-baixa { --priority-color: var(--success); }

.priority-ratio-bar {
  display: flex;
  gap: 3px;
  height: 8px;
  margin-top: 14px;
}

.priority-ratio-bar i {
  display: block;
  flex: 0 0 var(--fill, 0%);
  min-width: 4px;
  border-radius: 999px;
  background: var(--priority-color, var(--accent));
  opacity: 0.9;
}

.priority-ratio-bar .priority-alta { --priority-color: var(--danger); }
.priority-ratio-bar .priority-media { --priority-color: var(--warning); }
.priority-ratio-bar .priority-baixa { --priority-color: var(--success); }

.priority-hint {
  margin: 10px 0 0 !important;
  color: var(--muted);
  font-size: 0.74rem !important;
  line-height: 1.4 !important;
}

.focus-list {
  display: grid;
  gap: 8px;
  max-height: 260px;
  overflow-y: auto;
  padding-right: 2px;
}

.focus-list button {
  display: grid;
  gap: 4px;
  width: 100%;
  padding: 10px;
  border: 1px solid var(--line);
  border-radius: 6px;
  background: hsl(var(--secondary) / 0.48);
  text-align: left;
}

.focus-list button:hover {
  border-color: hsl(var(--ring) / 0.42);
  background: hsl(var(--secondary));
}

.focus-list button strong {
  overflow: hidden;
  font-size: 0.84rem;
  font-weight: 600;
  line-height: 1.3;
}

.insight-actions {
  grid-template-columns: 1fr;
}

.insight-actions button {
  min-height: 36px;
  border: 1px solid var(--line);
  border-radius: 6px;
  background: transparent;
  color: var(--text);
  font-weight: 600;
}

.insight-actions button:first-child {
  border-color: transparent;
  background: var(--accent);
  color: hsl(var(--primary-foreground));
}

.insight-actions button:hover {
  background: hsl(var(--secondary));
}

.insight-actions button:first-child:hover {
  background: var(--accent-hover);
}

.kanban-column {
  flex-basis: 300px;
  min-height: 176px;
  padding: 10px;
  border: 1px solid var(--line) !important;
  border-radius: var(--radius);
  background: hsl(var(--secondary) / 0.58) !important;
}

.kanban-column-header {
  margin-bottom: 8px;
  padding: 0 0 10px;
  border-bottom: 1px solid var(--line);
}

.kanban-column-header span {
  color: var(--text);
  font-size: 0.83rem;
  font-weight: 700;
}

.kanban-column-header .column-count,
.kanban-column-header .column-hours,
.status-badge,
.priority-pill,
.muted-meta,
.request-age-pill,
.card-due,
.v-chip:not(.primary) {
  border: 1px solid var(--line) !important;
  background: hsl(var(--secondary)) !important;
  color: var(--muted) !important;
}

.list-dot,
.card-project-dot {
  width: 10px;
  height: 10px;
  border-width: 2px;
  border-color: var(--accent);
}

.column-header-add,
.column-menu-button,
.card-menu-button {
  color: var(--muted);
  border-radius: 6px;
}

.column-header-add:hover,
.column-menu-button:hover,
.card-menu-button:hover {
  background: hsl(var(--secondary));
  color: var(--text);
}

.kanban-empty {
  min-height: 82px;
  border: 1px dashed var(--line);
  border-radius: var(--radius);
  background: transparent;
  color: var(--muted);
}

.column-add-button,
.add-list-card {
  border: 1px dashed var(--line);
  border-radius: var(--radius);
  background: transparent;
  color: var(--muted);
}

.column-add-button:hover,
.add-list-card:hover {
  border-color: hsl(var(--ring) / 0.48);
  background: var(--accent-subtle);
  color: hsl(var(--accent-foreground));
}

.demand-card,
.side-item,
.note-item,
.attachment-item,
.source-attachment-item,
.detail-side-panel,
.profile-summary,
.draft-item {
  border: 1px solid var(--line) !important;
  border-radius: var(--radius) !important;
  background: hsl(var(--card)) !important;
  color: var(--text) !important;
  box-shadow: none !important;
}

.demand-card:hover {
  border-color: hsl(var(--ring) / 0.42) !important;
  background: hsl(0 0% 9%) !important;
  box-shadow: 0 0 0 1px hsl(var(--ring) / 0.18) !important;
}

.card-labels {
  padding-top: 8px;
}

.card-label {
  height: 3px;
  background: var(--line);
}

.card-label.priority-alta { background: var(--danger); }
.card-label.priority-media { background: var(--warning); }
.card-label.priority-baixa { background: var(--accent); }

.card-title,
.demand-card h3,
.resource-table td strong,
.dashboard-list-item strong,
.table-toolbar strong {
  color: var(--text);
}

.card-project-name,
.latest-note,
.resource-table td small,
.dashboard-list-item small,
.table-toolbar span,
.resource-header p,
.board-header p {
  color: var(--muted);
}

.card-footer,
.note-panel,
.detail-notes,
.bulk-demand-review {
  border-color: var(--line);
}

.card-avatar {
  background: hsl(var(--secondary));
  color: var(--text);
}

.priority-pill.priority-alta,
.request-age-pill.age-critical,
.card-due.overdue {
  border-color: rgba(248, 113, 113, 0.25) !important;
  background: rgba(248, 113, 113, 0.09) !important;
  color: var(--danger) !important;
}

.priority-pill.priority-media,
.request-age-pill.age-warning {
  border-color: rgba(245, 158, 11, 0.24) !important;
  background: rgba(245, 158, 11, 0.09) !important;
  color: var(--warning) !important;
}

.priority-pill.priority-baixa,
.request-age-pill.age-fresh,
.card-due.done {
  border-color: hsl(var(--ring) / 0.24) !important;
  background: hsl(var(--accent-token) / 0.6) !important;
  color: hsl(var(--accent-foreground)) !important;
}

.mobile-menu-button,
.account-trigger,
.project-context-select,
.btn-glow,
.btn-ghost,
.new-demand-button,
.draft-btn,
.inline-demand-actions button,
.dashboard-panel .table-toolbar button,
.actions-cell button,
.resource-link {
  border-radius: 6px !important;
}

.btn-glow,
.theme--light.v-btn.primary,
.v-application .primary.v-btn,
.v-chip.primary,
.v-btn.primary,
.inline-demand-actions button[type='submit'] {
  border: 1px solid transparent !important;
  background: var(--accent) !important;
  color: hsl(var(--primary-foreground)) !important;
  box-shadow: none !important;
}

.btn-glow:hover,
.theme--light.v-btn.primary:hover,
.v-application .primary.v-btn:hover,
.inline-demand-actions button[type='submit']:hover {
  background: var(--accent-hover) !important;
}

.btn-ghost,
.new-demand-button.action-secondary,
.v-btn.v-btn--outlined.primary--text,
.mobile-menu-button,
.account-trigger,
.project-context-select,
.actions-cell button {
  border: 1px solid var(--line) !important;
  background: transparent !important;
  color: var(--text) !important;
}

.btn-ghost:hover,
.new-demand-button.action-secondary:hover,
.v-btn.v-btn--outlined.primary--text:hover,
.mobile-menu-button:hover,
.account-trigger:hover,
.project-context-select:hover,
.actions-cell button:hover {
  background: hsl(var(--secondary)) !important;
  color: var(--text) !important;
}

.v-text-field.v-text-field--outlined fieldset,
.v-select.v-text-field--outlined fieldset,
.v-textarea.v-text-field--outlined fieldset {
  border-color: hsl(var(--input)) !important;
  border-radius: 6px !important;
}

.v-text-field--outlined .v-input__slot,
.v-select.v-text-field--outlined .v-input__slot,
.v-textarea.v-text-field--outlined .v-input__slot {
  min-height: 38px !important;
  background: hsl(var(--secondary) / 0.62) !important;
}

.v-text-field input,
.v-textarea textarea,
.v-select__selection,
.v-label,
.v-input,
.v-icon,
.theme--light.v-input input,
.theme--light.v-input textarea {
  color: var(--text) !important;
}

.theme--light.v-label,
.v-input input::placeholder,
.v-textarea textarea::placeholder {
  color: var(--muted) !important;
}

.v-text-field--outlined.v-input--is-focused fieldset,
.v-select.v-input--is-focused fieldset,
.v-textarea.v-input--is-focused fieldset {
  border-color: hsl(var(--ring)) !important;
  box-shadow: 0 0 0 2px hsl(var(--ring) / 0.18);
}

.table-panel,
.dashboard-panel,
.dashboard-metric,
.resource-screen {
  background: var(--panel) !important;
}

.resource-table {
  background: var(--panel);
}

.resource-table th {
  background: hsl(var(--secondary) / 0.72);
  color: var(--muted);
}

.resource-table th,
.resource-table td {
  border-bottom-color: var(--line);
}

.resource-table tbody tr:hover td,
.dashboard-list-item:hover {
  background: hsl(var(--secondary) / 0.72);
}

.dashboard-metric {
  border-left-width: 1px;
}

.dashboard-metric.metric-info,
.dashboard-metric.metric-progress,
.dashboard-metric.metric-warning,
.dashboard-metric.metric-success {
  border-left-color: var(--line);
}

.resource-empty,
.dashboard-empty {
  border-color: var(--line);
  background: transparent;
  color: var(--muted);
}

.v-menu__content,
.account-popover,
.project-context-popover,
.mention-popover,
.card-menu-list {
  border: 1px solid var(--line) !important;
  border-radius: var(--radius) !important;
  background: hsl(var(--popover)) !important;
  color: hsl(var(--popover-foreground)) !important;
  box-shadow: var(--shadow-md) !important;
}

.card-menu-list .v-list-item__title {
  color: var(--text);
}

.account-popover button:hover,
.project-context-popover button:hover,
.project-context-popover button.active,
.card-menu-list .v-list-item:hover {
  background: hsl(var(--secondary)) !important;
  color: var(--text) !important;
}

.modal-card .v-card__title span,
.resource-header span,
.board-header span,
.eyebrow {
  color: var(--muted);
}

.login-shell {
  background:
    radial-gradient(circle at 50% 8%, hsl(var(--accent-token) / 0.42), transparent 28%),
    var(--bg);
}

@media (max-width: 1120px) {
  .product-shell {
    padding-left: 0;
  }

  .product-shell.sidebar-active {
    padding-left: 0;
  }

  .topbar {
    gap: 10px;
    margin: 0;
    padding: 0 16px;
  }

  .workspace-layout {
    grid-template-columns: 1fr;
  }

  .workspace-layout.menu-open {
    grid-template-columns: 1fr;
    gap: 0;
  }

  .workspace-rail {
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    z-index: 40;
    width: min(86vw, 328px);
    min-height: 100vh;
    padding: 14px;
    border: 0;
    border-radius: 0 12px 12px 0;
    box-shadow: 4px 0 32px rgba(0, 0, 0, 0.4);
    transform: translateX(-104%);
    opacity: 0;
    pointer-events: none;
  }

  .workspace-rail.open {
    width: min(86vw, 328px);
    transform: translateX(0);
    opacity: 1;
    pointer-events: auto;
  }

  .workspace-rail:not(.open) .sidebar-brand-lockup > div,
  .workspace-rail:not(.open) .rail-section-label,
  .workspace-rail:not(.open) .rail-item strong {
    display: block;
  }

  .sidebar-mobile-head button {
    display: block;
  }

  .sidebar-backdrop {
    position: fixed;
    inset: 0;
    z-index: 30;
    display: block;
    background: rgba(37, 34, 29, 0.38);
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.18s ease;
  }

  .sidebar-backdrop.active {
    display: block;
    opacity: 1;
    pointer-events: auto;
  }

  .hero-band {
    grid-template-columns: 1fr;
  }

  .table-panel {
    overflow-x: auto;
  }

  .kanban-workspace {
    grid-template-columns: 1fr;
  }

  .kanban-insights-panel {
    position: static;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .kanban-insights-panel .insight-card-primary,
  .kanban-insights-panel .insight-actions {
    grid-column: 1 / -1;
  }
}

@media (min-width: 761px) and (max-width: 1120px) {
  .product-shell {
    padding-left: var(--sidebar-collapsed-width);
  }

  .product-shell.sidebar-active {
    padding-left: var(--sidebar-width);
  }

  .workspace-rail {
    z-index: 22;
    width: var(--sidebar-collapsed-width);
    padding: 14px 8px;
    border: 0;
    border-radius: 0;
    box-shadow: none;
    transform: translateX(0);
    opacity: 1;
    pointer-events: auto;
  }

  .workspace-rail.open {
    width: var(--sidebar-width);
    padding: 14px;
  }

  .workspace-rail:not(.open) .sidebar-brand-lockup > div,
  .workspace-rail:not(.open) .sidebar-mobile-head button,
  .workspace-rail:not(.open) .rail-section-label,
  .workspace-rail:not(.open) .rail-item strong {
    display: none;
  }

  .workspace-rail:not(.open) .rail-item {
    grid-template-columns: 36px;
    justify-content: center;
  }

  .sidebar-mobile-head button {
    display: none;
  }

  .sidebar-backdrop,
  .sidebar-backdrop.active {
    display: none;
  }
}

@media (max-width: 760px) {
  .product-shell {
    padding: 8px;
  }

  .product-shell.sidebar-active {
    padding-left: 8px;
  }

  .topbar {
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 8px;
    align-items: stretch;
    margin-bottom: 8px;
    padding: 12px;
    border: 1px solid var(--line);
    border-radius: var(--radius);
    background: var(--panel);
  }

  .brand-lockup {
    width: 100%;
    justify-content: flex-start;
  }

  .brand-mark {
    width: 36px;
    height: 36px;
  }

  .brand-wordmark {
    font-size: 1rem;
  }

  .brand-lockup span {
    display: block;
    font-size: 0.72rem;
  }

  .topbar-actions {
    display: grid;
    grid-template-columns: 1fr auto;
    grid-column: 1 / -1;
    width: 100%;
    gap: 8px;
    margin-left: 0;
  }

  .account-menu {
    grid-column: 1 / -1;
  }

  .account-trigger {
    width: 100%;
    max-width: none;
  }

  .account-popover {
    right: 0;
    left: 0;
  }

  .project-context-select {
    grid-column: 1 / -1;
    width: 100%;
    max-width: none;
  }

  .project-context-popover {
    right: 0;
    left: 0;
    width: auto;
  }

  .btn-glow {
    grid-column: 1;
    width: 100%;
    min-height: 42px;
  }

  .btn-ghost {
    grid-column: 2;
    min-height: 42px;
    padding: 0 14px;
  }

  .workspace-rail {
    padding: 12px;
    width: min(88vw, 320px);
  }

  .sidebar-mobile-head {
    margin-bottom: 4px;
  }

  .rail-item {
    min-width: 0;
    min-height: 54px;
    grid-template-columns: 38px 1fr;
    gap: 10px;
    justify-items: start;
    align-items: center;
    padding: 9px 10px;
    white-space: nowrap;
  }

  .workspace-rail:not(.open) .rail-item {
    grid-template-columns: 38px 1fr;
    justify-content: stretch;
  }

  .rail-section {
    gap: 6px;
  }

  .rail-section + .rail-section {
    margin-top: 8px;
    padding-top: 12px;
  }

  .rail-section-label {
    padding: 0 2px;
    font-size: 0.7rem;
    text-align: left;
  }

  .rail-item > .rail-icon {
    display: grid;
    width: 32px;
    height: 32px;
    place-items: center;
    border-radius: 8px;
    background: var(--panel-2);
    color: var(--text);
    font-size: 0.7rem;
  }

  .rail-item strong {
    font-size: 0.86rem;
  }

  .rail-item.active > .rail-icon {
    background: var(--accent);
    color: #ffffff;
  }

  .hero-band,
  .board-panel,
  .resource-screen,
  .login-card {
    padding: 14px;
  }

  .trello-board-panel {
    padding: 8px;
  }

  .board-compact-head {
    grid-template-columns: minmax(0, 1fr);
    padding: 8px;
  }

  .resource-header {
    display: grid;
  }

  .resource-header p {
    max-width: 100%;
    line-height: 1.45;
  }

  .resource-header .new-demand-button {
    width: 100%;
  }

  .toolbar,
  .trello-toolbar,
  .request-toolbar,
  .summary-grid,
  .dashboard-metrics,
  .dashboard-grid,
  .two-columns,
  .three-columns {
    grid-template-columns: 1fr;
  }

  .toolbar,
  .trello-toolbar,
  .request-toolbar,
  .toolbar .v-input,
  .trello-toolbar .v-input,
  .request-toolbar .v-input {
    width: 100%;
    min-width: 0;
  }

  .summary-box {
    min-height: 56px;
  }

  .kanban-board {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    overflow-x: visible;
  }

  .kanban-workspace,
  .kanban-workspace .kanban-board,
  .kanban-insights-panel {
    width: 100%;
    max-width: 100%;
    min-width: 0;
  }

  .kanban-insights-panel,
  .insight-grid {
    grid-template-columns: 1fr;
  }

  .kanban-column,
  .add-list-card {
    flex-basis: auto;
    width: 100%;
    max-width: 100%;
    min-height: auto;
  }

  .table-toolbar {
    display: grid;
  }

  .user-tabs {
    display: grid;
    grid-template-columns: 1fr 1fr;
    margin: 10px;
  }

  .table-panel {
    overflow: visible;
  }

  .resource-table,
  .resource-table tbody,
  .resource-table tr,
  .resource-table td {
    display: block;
    width: 100%;
  }

  .resource-table thead {
    display: none;
  }

  .resource-table {
    background: transparent;
  }

  .resource-table tbody {
    display: grid;
    gap: 10px;
    padding: 10px;
  }

  .resource-table tr {
    border: 1px solid var(--line);
    border-radius: 8px;
    background: var(--panel);
    overflow: hidden;
  }

  .resource-table th,
  .resource-table td {
    border-bottom: 1px solid rgba(37, 34, 29, 0.08);
  }

  .resource-table td {
    display: grid;
    grid-template-columns: 108px minmax(0, 1fr);
    gap: 12px;
    align-items: center;
    min-height: 44px;
    padding: 10px 12px;
  }

  .resource-table td::before {
    content: attr(data-label);
    color: var(--muted);
    font-size: 0.72rem;
    font-weight: 800;
  }

  .resource-table tr:last-child td {
    border-bottom: 1px solid rgba(37, 34, 29, 0.08);
  }

  .resource-table td:last-child {
    border-bottom: 0;
  }

  .resource-table td strong,
  .resource-table td small {
    white-space: normal;
  }

  .resource-table td > .v-chip {
    justify-self: start;
  }

  .actions-cell {
    display: grid;
    justify-content: flex-start;
    gap: 8px;
    text-align: left;
    white-space: normal;
  }

  .actions-cell::before {
    align-self: center;
  }

  .actions-cell button {
    margin-left: 0;
    width: 100%;
  }

  .attachment-dropzone {
    grid-template-columns: 1fr;
    justify-items: start;
  }

  .attachment-item {
    grid-template-columns: 42px minmax(0, 1fr);
  }

  .attachment-item button {
    grid-column: 1 / -1;
    width: 100%;
  }
}

/* shadcn reference palette and shapes */

.v-application {
  background: hsl(var(--background)) !important;
}

.app-page,
.product-shell,
.workspace-main {
  background: hsl(var(--background)) !important;
}

.topbar,
.workspace-rail,
.board-compact-head,
.resource-screen,
.board-panel,
.dashboard-panel,
.table-panel,
.login-card,
.modal-card,
.kanban-column,
.demand-card,
.insight-card,
.insight-grid > div,
.add-list-card,
.side-item,
.dashboard-metric,
.summary-box,
.detail-side-panel,
.detail-notes,
.attachment-dropzone,
.attachment-item,
.request-detail-panel,
.draft-item {
  border-color: hsl(var(--border)) !important;
  border-radius: 14px !important;
  background: hsl(var(--card)) !important;
  color: hsl(var(--foreground)) !important;
  box-shadow: none !important;
}

.topbar,
.workspace-rail {
  background: hsl(0 0% 4.5%) !important;
}

.trello-board-panel {
  border-color: transparent !important;
  background: transparent !important;
}

.board-compact-head {
  padding: 14px;
}

.kanban-column {
  background: hsl(0 0% 9%) !important;
}

.demand-card,
.insight-card,
.insight-grid > div,
.add-list-card {
  background: hsl(0 0% 11%) !important;
}

.demand-card:hover,
.focus-list button:hover,
.rail-item:hover,
.project-context-trigger:hover,
.account-trigger:hover {
  border-color: hsl(0 0% 26%) !important;
  background: hsl(0 0% 13%) !important;
  transform: none;
}

.brand-mark,
.mobile-menu-button,
.column-menu-button,
.card-menu-button,
.project-context-trigger,
.account-trigger,
.request-attachment-button,
.attachment-preview,
.rail-icon {
  border-color: hsl(var(--border)) !important;
  border-radius: 10px !important;
  background: hsl(var(--secondary)) !important;
  color: hsl(var(--foreground)) !important;
}

.rail-item {
  border-radius: 10px !important;
  color: hsl(var(--muted-foreground)) !important;
}

.rail-item.active,
.rail-item.active:hover,
.workspace-rail:not(.open) .rail-item.active {
  border-color: hsl(var(--border)) !important;
  background: hsl(var(--secondary)) !important;
  color: hsl(var(--foreground)) !important;
}

.rail-item.active > .rail-icon,
.workspace-rail:not(.open) .rail-item.active > .rail-icon {
  background: hsl(var(--foreground)) !important;
  color: hsl(var(--background)) !important;
}

.btn-glow,
.primary-button,
.new-demand-button,
.theme--light.v-btn.primary,
.theme--light.v-btn.primary:hover,
.inline-demand-actions button[type="submit"],
.insight-actions button:first-child,
.column-add-button:hover {
  border: 1px solid hsl(var(--primary)) !important;
  border-radius: 10px !important;
  background: hsl(var(--primary)) !important;
  color: hsl(var(--primary-foreground)) !important;
  box-shadow: none !important;
}

.v-application .v-btn.primary.white--text,
.v-application .v-btn.primary .white--text,
.v-application .v-btn.primary .v-btn__content {
  color: hsl(var(--primary-foreground)) !important;
}

.btn-ghost,
.bulk-drafts-btn,
.inline-demand-actions button,
.insight-actions button,
.column-add-button,
.add-list-card {
  border: 1px solid hsl(var(--border)) !important;
  border-radius: 10px !important;
  background: transparent !important;
  color: hsl(var(--foreground)) !important;
  box-shadow: none !important;
}

.btn-ghost:hover,
.bulk-drafts-btn:hover,
.inline-demand-actions button:hover,
.insight-actions button:hover,
.column-add-button:hover,
.add-list-card:hover {
  background: hsl(var(--secondary)) !important;
}

.theme--light.v-input,
.theme--light.v-input .v-input__slot,
.inline-demand-composer textarea,
.inline-demand-extra input,
.request-attachment-box,
.mention-popover,
.v-menu__content,
.card-menu-list {
  border-color: hsl(var(--border)) !important;
  border-radius: 12px !important;
  background: hsl(var(--input)) !important;
  color: hsl(var(--foreground)) !important;
  box-shadow: none !important;
}

.theme--light.v-input input,
.theme--light.v-input textarea,
.theme--light.v-select .v-select__selection,
.theme--light.v-label,
.theme--light.v-icon {
  color: hsl(var(--foreground)) !important;
}

.theme--light.v-label,
.board-context-strip span,
.resource-header span,
.dashboard-metric span,
.summary-box span,
.insight-card span,
.insight-card p,
.insight-grid span,
.card-project-name,
.muted,
.muted-meta,
.rail-section-label,
.brand-lockup span,
.login-brand span {
  color: hsl(var(--muted-foreground)) !important;
}

.card-label.priority-alta,
.priority-pill.priority-alta {
  background: hsl(var(--foreground)) !important;
}

.card-label.priority-media,
.priority-pill.priority-media {
  background: hsl(0 0% 48%) !important;
}

.card-label.priority-baixa,
.priority-pill.priority-baixa {
  background: hsl(0 0% 28%) !important;
}

.list-dot {
  border-color: hsl(0 0% 48%) !important;
}

.kanban-column.is-done .list-dot {
  border-color: hsl(0 0% 72%) !important;
}

.insight-card-primary {
  background: hsl(0 0% 12%) !important;
}

.priority-distribution i {
  background: hsl(var(--foreground)) !important;
}

.focus-list button {
  border-color: hsl(var(--border)) !important;
  border-radius: 12px !important;
  background: hsl(var(--secondary)) !important;
  color: hsl(var(--foreground)) !important;
}

.v-alert,
.v-chip {
  border-radius: 999px !important;
}

/* Dialog validation fixes after shadcn component migration */

.theme--light.v-dialog,
.theme--light.v-card.modal-card,
.v-dialog .modal-card {
  border: 1px solid hsl(var(--border)) !important;
  border-radius: 14px !important;
  background: hsl(var(--card)) !important;
  color: hsl(var(--foreground)) !important;
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.52) !important;
}

.detail-modal .v-card__title,
.detail-modal .v-card__text,
.detail-modal .v-card__actions {
  background: transparent !important;
  color: hsl(var(--foreground)) !important;
}

.detail-modal .v-card__title span {
  color: hsl(var(--muted-foreground)) !important;
  letter-spacing: 0;
  text-transform: none;
}

.detail-modal .v-card__title h2 {
  color: hsl(var(--foreground)) !important;
  line-height: 1.2;
}

.detail-side-panel,
.request-side-panel,
.linked-demands,
.source-attachments-panel,
.detail-notes,
.chat-message,
.message-attachment {
  border: 1px solid hsl(var(--border)) !important;
  border-radius: 14px !important;
  background: hsl(var(--card)) !important;
  color: hsl(var(--foreground)) !important;
  box-shadow: none !important;
}

.request-side-panel,
.linked-demands,
.detail-side-panel {
  gap: 10px;
  padding: 16px;
}

.request-side-panel h3,
.linked-demands strong,
.detail-side-panel h3,
.detail-notes h3,
.request-chat h3 {
  color: hsl(var(--foreground)) !important;
}

.chat-message {
  width: min(92%, 560px);
}

.chat-message.mine,
.chat-message.email-log {
  background: hsl(var(--secondary)) !important;
}

.chat-message.email-log {
  border-color: hsl(217 80% 30% / 0.56) !important;
}

.chat-message.email-log strong {
  color: hsl(217 90% 68%) !important;
}

.chat-message p,
.chat-message b {
  color: hsl(var(--foreground)) !important;
}

.message-attachment {
  background: hsl(var(--secondary)) !important;
  text-decoration: none;
}

.message-attachment > span,
.attachment-icon,
.attachment-preview,
.request-attachment-button {
  background: hsl(var(--primary)) !important;
  color: hsl(var(--primary-foreground)) !important;
}

.request-attachment-box {
  min-height: 42px;
  padding: 5px 8px;
  border: 1px solid hsl(var(--border)) !important;
  border-radius: 12px !important;
  background: hsl(var(--input)) !important;
}

.request-send-waiting {
  border-color: hsl(38 90% 28%) !important;
  background: hsl(38 90% 14%) !important;
  color: hsl(38 92% 62%) !important;
}

.request-transform-button {
  min-height: 36px !important;
  border-radius: 8px !important;
  text-transform: none !important;
}

.request-convert-empty,
.request-conversion-callout {
  display: grid;
  gap: 8px;
  padding: 12px;
  border: 1px solid hsl(var(--border));
  border-radius: 8px;
  background: hsl(var(--secondary));
}

.request-convert-empty strong,
.request-conversion-callout strong {
  color: hsl(var(--foreground));
  font-size: 0.9rem;
}

.request-convert-empty span,
.request-conversion-callout span {
  color: hsl(var(--muted-foreground));
  font-size: 0.82rem;
  line-height: 1.4;
}

.request-conversion-callout {
  margin-bottom: 14px;
}

.detail-modal .theme--light.v-input,
.detail-modal .theme--light.v-input .v-input__slot {
  background: hsl(var(--input)) !important;
}

.detail-modal .v-text-field--outlined .v-label,
.detail-modal .v-textarea--outlined .v-label,
.detail-modal .v-select.v-text-field--outlined .v-label {
  background: hsl(var(--input)) !important;
  color: hsl(var(--muted-foreground)) !important;
}

.detail-modal .theme--light.v-input input,
.detail-modal .theme--light.v-input textarea,
.detail-modal .theme--light.v-select .v-select__selection {
  color: hsl(var(--foreground)) !important;
  -webkit-text-fill-color: hsl(var(--foreground)) !important;
}

.modal-card .v-input--is-disabled input,
.modal-card .v-input--is-disabled textarea,
.modal-card .v-input--is-disabled .v-select__selection {
  color: hsl(var(--foreground) / 0.74) !important;
  -webkit-text-fill-color: hsl(var(--foreground) / 0.74) !important;
}

.v-select-locked {
  opacity: 0.74;
  pointer-events: none;
}

.v-select-locked .v-input__append-inner {
  display: none !important;
}

.link-existing-demand,
.link-existing-demand .table-toolbar {
  border-color: hsl(var(--border)) !important;
  background: transparent !important;
}

.linked-demand-item {
  border-bottom-color: hsl(var(--border)) !important;
}

.linked-demand-item:hover,
.linked-demand-item:focus-visible {
  background: hsl(var(--secondary)) !important;
  outline-color: hsl(var(--border)) !important;
}

/* Header filters */

.topbar {
  display: grid !important;
  grid-template-columns: auto minmax(0, 1fr) auto;
  gap: 12px !important;
  align-items: center;
  max-width: none !important;
  margin: 0 !important;
  border-radius: 0 !important;
}

.topbar-context {
  display: flex;
  flex-wrap: nowrap;
  gap: 16px;
  align-items: center;
  min-width: 0;
}

.topbar-project-label {
  flex: 0 1 220px;
  display: grid;
  gap: 2px;
  min-width: 0;
}

.topbar-search {
  flex: 1 1 auto;
  display: flex;
  justify-content: center;
  min-width: 0;
}

.topbar-search .ui-field {
  width: 100%;
  max-width: 420px;
}

.topbar-filters-toggle {
  position: relative;
  flex: 0 0 auto;
  outline: none;
}

.filters-trigger {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 14px;
  border: 1px solid hsl(var(--border));
  border-radius: 999px;
  background: hsl(var(--input));
  color: hsl(var(--foreground));
  font-size: 0.84rem;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 140ms ease, background-color 140ms ease;
}

.filters-trigger:hover {
  border-color: hsl(var(--ring) / 0.6);
}

.filters-trigger.active {
  border-color: hsl(var(--ring));
  background: hsl(var(--secondary));
}

.filters-count-badge {
  display: inline-grid;
  place-items: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 999px;
  background: hsl(var(--primary));
  color: hsl(var(--primary-foreground));
  font-size: 0.68rem;
  font-weight: 800;
}

.topbar-project-label span {
  overflow: hidden;
  color: hsl(var(--muted-foreground));
  font-size: 0.68rem;
  font-weight: 800;
  line-height: 1;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.topbar-project-label strong {
  overflow: hidden;
  color: hsl(var(--foreground));
  font-size: 0.92rem;
  font-weight: 800;
  line-height: 1.15;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.topbar-filter-group {
  position: absolute;
  z-index: 40;
  top: calc(100% + 8px);
  left: 0;
  display: grid;
  grid-template-columns: minmax(220px, 1fr);
  gap: 10px;
  width: max-content;
  min-width: 260px;
  max-width: min(420px, 90vw);
  padding: 14px;
  border: 1px solid hsl(var(--border));
  border-radius: 12px;
  background: hsl(var(--popover));
  box-shadow: 0 18px 44px rgba(0, 0, 0, 0.36);
}

.topbar-search .ui-field,
.topbar-filter-group .ui-field {
  gap: 0;
  min-height: 36px;
  width: 100%;
}

.topbar-search .ui-input,
.topbar-filter-group .ui-input,
.topbar-filter-group .ui-select {
  height: 36px;
  min-height: 36px;
  border-radius: 8px;
  border-color: hsl(var(--border)) !important;
  background-color: hsl(var(--input));
  color: hsl(var(--foreground)) !important;
  font-size: 0.84rem;
  line-height: 1.2;
  box-shadow: none !important;
}

.topbar-filter-group .ui-input:focus,
.topbar-filter-group .ui-select:focus {
  border-color: hsl(var(--ring)) !important;
  box-shadow: 0 0 0 1px hsl(var(--ring) / 0.22) !important;
}

.topbar-filter-group .ui-input::placeholder {
  color: hsl(var(--muted-foreground)) !important;
}

.topbar-date-filter {
  display: grid;
  gap: 7px;
  min-width: 260px;
}

.topbar-date-filter > span {
  color: hsl(var(--muted-foreground));
  font-size: 0.68rem;
  font-weight: 800;
  line-height: 1;
  text-transform: uppercase;
}

.topbar-date-filter > div {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 8px;
}

.topbar-search .ui-field > span,
.topbar-filter-group .ui-field > span {
  display: none !important;
}

.topbar-filter-group .theme--light.v-input,
.topbar-filter-group .v-input {
  margin: 0 !important;
  padding: 0 !important;
}

.topbar-filter-group .v-input__control,
.topbar-filter-group .v-input__slot {
  min-height: 36px !important;
}

.topbar-filter-group .v-input__slot {
  margin: 0 !important;
  border-radius: 8px !important;
  background: hsl(var(--input)) !important;
  box-shadow: none !important;
}

.topbar-filter-group .v-text-field__details,
.topbar-filter-group .v-label {
  display: none !important;
}

.topbar-filter-group .v-text-field input,
.topbar-filter-group .v-select__selection,
.topbar-filter-group .theme--light.v-input input {
  color: hsl(var(--foreground)) !important;
  caret-color: hsl(var(--foreground)) !important;
}

.topbar-filter-group .v-text-field.v-text-field--outlined fieldset,
.topbar-filter-group .v-select.v-text-field--outlined fieldset {
  border-color: hsl(var(--border)) !important;
}

.topbar-filter-group .v-text-field--outlined.v-input--is-focused fieldset,
.topbar-filter-group .v-select.v-input--is-focused fieldset {
  border-color: hsl(var(--ring)) !important;
  box-shadow: 0 0 0 1px hsl(var(--ring) / 0.22) !important;
}

.topbar-actions {
  min-width: max-content;
}

.board-panel.trello-board-panel {
  padding-top: 12px;
}

.shadcn-screen.requests-screen {
  padding-top: 18px;
}

/* Dialog layout stability */

.detail-modal .v-card__title {
  align-items: flex-start;
  padding: 24px 26px 8px !important;
}

.detail-modal .v-card__title h2 {
  max-width: min(860px, 72vw);
  font-size: clamp(1.35rem, 2vw, 1.9rem) !important;
}

.detail-modal .v-card__text {
  padding: 10px 26px 0 !important;
}

.detail-modal .v-card__actions {
  padding: 16px 26px 24px !important;
}

.detail-grid,
.request-detail-grid {
  grid-template-columns: minmax(0, 1fr) minmax(300px, 340px) !important;
  gap: 20px !important;
}

.request-main-column,
.detail-main-column {
  display: grid;
  gap: 12px;
  min-width: 0;
}

.request-side-column,
.detail-side-column {
  gap: 12px !important;
  min-width: 0;
}

.detail-side-panel,
.request-side-panel,
.linked-demands,
.detail-notes {
  border-radius: 14px !important;
}

.detail-modal .v-input {
  margin: 0 !important;
}

.detail-modal .v-input__slot {
  min-height: 42px !important;
}

.detail-modal .v-textarea .v-input__slot {
  min-height: 120px !important;
}

.detail-modal .v-text-field--outlined .v-label,
.detail-modal .v-textarea--outlined .v-label,
.detail-modal .v-select.v-text-field--outlined .v-label {
  top: 7px !important;
  max-width: calc(100% - 24px);
  background: transparent !important;
  padding: 0 !important;
}

.detail-modal .v-text-field--outlined.v-input--is-label-active .v-label,
.detail-modal .v-textarea--outlined.v-input--is-label-active .v-label,
.detail-modal .v-select.v-text-field--outlined.v-input--is-label-active .v-label {
  transform: translateY(-18px) scale(0.78) !important;
  transform-origin: left top;
}

.detail-modal .v-text-field__slot,
.detail-modal .v-select__slot {
  align-items: center;
}

.link-existing-demand {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 8px;
  align-items: center;
}

.demand-request-link.link-existing-demand {
  grid-template-columns: minmax(0, 1fr);
}

.linked-demands .table-toolbar {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  min-height: auto;
  padding: 0 0 12px;
}

.linked-demand-item strong {
  white-space: normal;
}

.request-chat {
  display: grid;
  gap: 12px;
}

.request-attachment-box {
  margin: -12px 0 0;
}

@media (max-width: 1260px) {
  .topbar {
    grid-template-columns: auto minmax(0, 1fr);
  }

  .topbar-actions {
    grid-column: 1 / -1;
    justify-content: flex-end;
  }
}

@media (max-width: 980px) {
  .topbar {
    width: 100%;
    min-width: 0;
    padding-right: 12px !important;
    padding-left: 12px !important;
    overflow: hidden;
  }

  .topbar-context {
    min-width: 0;
    overflow: hidden;
  }

  .topbar-project-label {
    display: none;
  }

  .topbar-search .ui-field {
    max-width: none;
  }

  .topbar-filter-group {
    left: auto;
    right: 0;
    max-width: min(320px, 88vw);
  }

  .topbar-actions {
    width: 100%;
    min-width: 0;
  }

  .topbar-actions > * {
    max-width: 100%;
  }

  .detail-grid,
  .request-detail-grid {
    grid-template-columns: minmax(0, 1fr) !important;
  }

  .detail-modal .v-card__title h2 {
    max-width: 100%;
  }
}

.shadcn-screen {
  display: grid;
  gap: 18px;
  padding: 28px 32px 32px;
  border: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
}

.shadcn-page-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 18px;
  padding: 0 0 18px;
  border-bottom: 1px solid hsl(var(--border));
}

.shadcn-page-header h2 {
  margin: 8px 0 0;
  color: hsl(var(--foreground));
  font-size: 1.65rem;
  font-weight: 800;
  line-height: 1.1;
}

.shadcn-page-header p {
  max-width: 680px;
  margin: 10px 0 0;
  color: hsl(var(--muted-foreground));
  font-size: 0.92rem;
  line-height: 1.55;
}

.shadcn-page-header span {
  color: hsl(var(--muted-foreground));
  font-size: 0.72rem;
  font-weight: 800;
}

.shadcn-filter-row {
  display: grid;
  grid-template-columns: minmax(260px, 1fr) minmax(170px, 220px) minmax(150px, 190px) minmax(150px, 190px);
  gap: 10px;
  align-items: end;
}

.requests-table-card {
  overflow: hidden;
}

.requests-table-card .shadcn-card-header {
  min-height: auto;
  padding: 16px 18px;
  border-bottom: 1px solid hsl(var(--border));
}

.requests-table-card .shadcn-card-header > div {
  display: grid;
  gap: 4px;
}

.requests-table-card .resource-table {
  background: transparent;
}

.requests-table-card .resource-table th {
  height: 38px;
  background: hsl(0 0% 12%);
  color: hsl(var(--muted-foreground));
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: none;
}

.requests-table-card .resource-table td {
  height: 56px;
  border-bottom-color: hsl(var(--border));
  color: hsl(var(--foreground));
}

.requests-table-card .resource-table tbody tr:hover td {
  background: hsl(var(--secondary));
}

.requests-table-card .resource-link {
  color: hsl(var(--foreground));
}

.requests-table-card .request-code-link {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace;
  font-size: 0.82rem;
}

.requests-table-card .request-title-link {
  font-weight: 800;
}

.requests-table-card .actions-cell {
  text-align: right;
}

.requests-table-card .actions-cell .ui-button {
  min-width: 68px;
}

@media (max-width: 1120px) {
  .shadcn-filter-row {
    grid-template-columns: minmax(0, 1fr) minmax(160px, 220px);
  }
}

@media (max-width: 760px) {
  .shadcn-screen {
    padding: 18px 8px;
  }

  .shadcn-page-header {
    display: grid;
    align-items: start;
  }

  .shadcn-filter-row {
    grid-template-columns: minmax(0, 1fr);
  }

  .requests-table-card .resource-table tbody {
    padding: 8px;
  }

  .requests-table-card .resource-table tr {
    background: hsl(var(--card));
  }

  .requests-table-card .resource-table td {
    height: auto;
  }

  .requests-table-card .actions-cell {
    text-align: left;
  }
}

/* Sidebar productivity refinement */

.product-shell {
  --sidebar-width: 276px;
  --sidebar-collapsed-width: 68px;
}

.workspace-rail {
  padding: 12px 10px 14px !important;
  border-right: 1px solid hsl(var(--border)) !important;
  background:
    linear-gradient(180deg, hsl(0 0% 8%), hsl(var(--background)) 58%),
    hsl(var(--background)) !important;
}

.workspace-rail.open {
  padding: 12px 14px 14px !important;
}

.sidebar-mobile-head {
  height: 48px !important;
  margin-bottom: 8px !important;
  border-bottom: 0 !important;
}

.sidebar-brand-lockup {
  gap: 10px;
}

.workspace-rail .brand-mark {
  width: 34px !important;
  height: 34px !important;
  border-radius: 9px !important;
  background: hsl(var(--primary)) !important;
  color: hsl(var(--primary-foreground)) !important;
  box-shadow: 0 0 0 1px hsl(var(--border));
}

.sidebar-brand-lockup .brand-wordmark {
  font-size: 1rem !important;
  letter-spacing: 0;
}

.sidebar-brand-lockup span {
  margin-top: 1px !important;
  color: hsl(var(--muted-foreground)) !important;
  font-size: 0.72rem !important;
  font-weight: 700 !important;
}

.sidebar-productivity-card {
  display: grid;
  gap: 8px;
  margin: 2px 0 12px;
  padding: 12px;
  border: 1px solid hsl(var(--border));
  border-radius: 10px;
  background: hsl(var(--card));
}

.sidebar-productivity-card > span {
  color: hsl(var(--muted-foreground));
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.sidebar-productivity-card > strong {
  overflow: hidden;
  color: hsl(var(--foreground));
  font-size: 0.9rem;
  font-weight: 800;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sidebar-productivity-card > div {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.sidebar-productivity-card small {
  min-height: 22px;
  padding: 3px 7px;
  border: 1px solid hsl(var(--border));
  border-radius: 999px;
  color: hsl(var(--muted-foreground));
  font-size: 0.68rem;
  font-weight: 700;
  line-height: 1.3;
}

.rail-section {
  gap: 5px !important;
}

.rail-section + .rail-section {
  margin-top: 12px !important;
  padding-top: 12px !important;
  border-top: 1px solid hsl(var(--border)) !important;
}

.rail-section-label {
  padding: 0 8px !important;
  color: hsl(var(--muted-foreground)) !important;
  font-size: 0.66rem !important;
  letter-spacing: 0.08em !important;
}

.rail-item {
  position: relative;
  grid-template-columns: 32px minmax(0, 1fr) auto auto !important;
  width: 100%;
  min-height: 42px !important;
  padding: 6px 8px !important;
  border: 1px solid transparent !important;
  border-radius: 9px !important;
  color: hsl(var(--muted-foreground)) !important;
}

.rail-item::before {
  content: '';
  position: absolute;
  top: 10px;
  bottom: 10px;
  left: -8px;
  width: 2px;
  border-radius: 999px;
  background: transparent;
}

.rail-item-main {
  display: grid;
  gap: 2px;
  min-width: 0;
}

.rail-item-main strong {
  overflow: hidden;
  color: inherit !important;
  font-size: 0.84rem !important;
  font-weight: 750 !important;
  line-height: 1.1;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rail-item-main small {
  overflow: hidden;
  color: hsl(var(--muted-foreground));
  font-size: 0.68rem;
  font-weight: 600;
  line-height: 1.1;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rail-icon {
  width: 30px !important;
  height: 30px !important;
  border-radius: 8px !important;
  background: hsl(var(--secondary) / 0.42) !important;
  color: hsl(var(--muted-foreground)) !important;
}

.rail-item-badge {
  min-width: 24px;
  height: 22px;
  padding: 0 7px;
  border: 1px solid hsl(var(--border));
  border-radius: 999px;
  color: hsl(var(--muted-foreground));
  font-size: 0.68rem;
  font-weight: 800;
  line-height: 20px;
  text-align: center;
}

.rail-caret {
  color: hsl(var(--muted-foreground)) !important;
}

.rail-item:hover,
.rail-item.active {
  border-color: hsl(var(--border)) !important;
  background: hsl(var(--secondary) / 0.68) !important;
  color: hsl(var(--foreground)) !important;
}

.rail-item.active::before {
  background: hsl(var(--primary));
}

.rail-item.active .rail-icon {
  background: hsl(var(--primary)) !important;
  color: hsl(var(--primary-foreground)) !important;
}

.rail-item.active .rail-item-main small,
.rail-item:hover .rail-item-main small {
  color: hsl(var(--muted-foreground));
}

.rail-projects {
  display: grid;
  gap: 4px !important;
  margin: 6px 0 6px 40px !important;
  padding: 0 0 0 10px !important;
  border-left: 1px solid hsl(var(--border)) !important;
}

.rail-projects-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-height: 26px;
  padding: 0 2px 2px 0;
}

.rail-projects-head span {
  color: hsl(var(--muted-foreground));
  font-size: 0.66rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.rail-projects-head button {
  min-height: 24px;
  padding: 0 8px;
  border: 1px solid hsl(var(--border));
  border-radius: 999px;
  background: transparent;
  color: hsl(var(--muted-foreground));
  font-size: 0.68rem;
  font-weight: 800;
}

.rail-projects-head button.active,
.rail-projects-head button:hover {
  background: hsl(var(--secondary));
  color: hsl(var(--foreground));
}

.rail-project-item {
  min-height: 30px !important;
  padding: 0 9px !important;
  border: 1px solid transparent !important;
  border-radius: 8px !important;
  color: hsl(var(--muted-foreground)) !important;
  font-size: 0.78rem !important;
  font-weight: 700 !important;
}

.rail-project-item:hover,
.rail-project-item.active {
  border-color: hsl(var(--border)) !important;
  background: hsl(var(--secondary) / 0.72) !important;
  color: hsl(var(--foreground)) !important;
}

.rail-project-item.active::before {
  background: hsl(var(--primary)) !important;
}

.sidebar-account-strip {
  display: grid;
  gap: 2px;
  margin-top: auto;
  padding: 12px 10px;
  border: 1px solid hsl(var(--border));
  border-radius: 10px;
  background: hsl(var(--card));
}

.sidebar-account-strip span {
  color: hsl(var(--muted-foreground));
  font-size: 0.66rem;
  font-weight: 800;
  text-transform: uppercase;
}

.sidebar-account-strip strong {
  overflow: hidden;
  color: hsl(var(--foreground));
  font-size: 0.82rem;
  font-weight: 800;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.workspace-rail:not(.open) {
  overflow: hidden !important;
}

.workspace-rail:not(.open) .sidebar-productivity-card,
.workspace-rail:not(.open) .sidebar-account-strip,
.workspace-rail:not(.open) .rail-item-main,
.workspace-rail:not(.open) .rail-item-badge,
.workspace-rail:not(.open) .rail-caret,
.workspace-rail:not(.open) .rail-projects {
  display: none !important;
}

.workspace-rail:not(.open) .rail-section + .rail-section {
  margin-top: 8px !important;
  padding-top: 8px !important;
}

.workspace-rail:not(.open) .rail-item {
  grid-template-columns: 32px !important;
  justify-content: center;
  padding: 6px !important;
}

.workspace-rail:not(.open) .rail-item::before {
  left: -6px;
}

@media (max-width: 1120px) {
  .workspace-rail.open {
    width: min(86vw, var(--sidebar-width));
  }

  .sidebar-mobile-head button {
    display: inline-flex !important;
    align-items: center;
  }
}

/* Demandesk logo */

.brand-mark {
  overflow: hidden;
  padding: 0 !important;
  border-left: 0 !important;
  background: transparent !important;
  box-shadow: 0 0 0 1px hsl(var(--border));
}

.brand-mark img {
  display: block;
  width: 100%;
  height: 100%;
  border-radius: inherit;
}

.login-brand {
  gap: 14px !important;
  align-items: center;
}

.login-brand > div {
  display: grid;
  gap: 2px;
  min-width: 0;
}

.login-brand .brand-wordmark {
  color: hsl(var(--foreground)) !important;
  font-size: 1.48rem !important;
  font-weight: 800 !important;
  line-height: 0.95;
  letter-spacing: 0;
}

.login-brand > div span {
  padding-left: 0 !important;
  border-left: 0 !important;
  color: hsl(var(--muted-foreground)) !important;
  font-size: 0.72rem !important;
  font-weight: 700 !important;
  line-height: 1;
  letter-spacing: 0;
  text-transform: none !important;
}

.login-brand .brand-mark {
  width: 42px !important;
  height: 42px !important;
  border-radius: 13px !important;
}

.workspace-rail .brand-mark {
  box-shadow: 0 0 0 1px hsl(var(--border)) !important;
}

.sidebar-brand-lockup .brand-mark {
  width: 36px !important;
  height: 36px !important;
  border-radius: 11px !important;
}

/* Login form stability */

.login-card .v-form {
  display: grid;
  gap: 12px;
  margin-top: 18px;
}

.login-card .v-input,
.login-card .theme--light.v-input {
  margin: 0 !important;
  padding: 0 !important;
  border: 0 !important;
  border-radius: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
}

.login-card .v-input__control {
  min-height: 0 !important;
}

.login-card .v-input__slot,
.login-card .theme--light.v-input .v-input__slot {
  min-height: 48px !important;
  margin: 0 !important;
  padding: 0 12px !important;
  border-radius: 10px !important;
  background: hsl(var(--input)) !important;
  box-shadow: none !important;
}

.login-card .v-text-field--outlined fieldset,
.login-card .v-text-field.v-text-field--outlined fieldset {
  top: 0 !important;
  border-color: hsl(var(--border)) !important;
  border-radius: 10px !important;
}

.login-card .v-text-field--outlined legend {
  display: none !important;
}

.login-card .v-label {
  display: block !important;
  top: 14px !important;
  transform: none !important;
  color: hsl(var(--muted-foreground)) !important;
  font-size: 0.92rem !important;
  line-height: 1.25 !important;
  pointer-events: none;
}

.login-card .v-label.primary--text,
.login-card .v-input.error--text .v-label {
  color: hsl(var(--muted-foreground)) !important;
}

.login-card .v-label.v-label--active {
  opacity: 0 !important;
}

.login-card .v-text-field__slot,
.login-card .v-text-field__prefix {
  align-items: center;
  color: hsl(var(--foreground)) !important;
}

.login-card .v-text-field input,
.login-card .theme--light.v-input input {
  max-height: none !important;
  padding: 0 !important;
  color: hsl(var(--foreground)) !important;
  caret-color: hsl(var(--foreground)) !important;
  line-height: 48px !important;
}

.login-card .v-text-field input::placeholder {
  color: hsl(var(--muted-foreground)) !important;
  opacity: 1;
}

.login-card .v-text-field__details {
  min-height: 16px !important;
  margin: 4px 0 0 !important;
  padding: 0 2px !important;
}

.login-card .v-messages {
  min-height: 16px !important;
  color: hsl(var(--destructive)) !important;
  font-size: 0.72rem !important;
  line-height: 1.25 !important;
}

.login-card .v-messages__message {
  line-height: 1.25 !important;
}

.login-card .v-input.error--text .v-input__slot {
  background: hsl(var(--input)) !important;
}

.login-card .v-input.error--text fieldset {
  border-color: hsl(var(--destructive)) !important;
}

.login-card .primary-button {
  min-height: 44px !important;
  margin-top: 2px;
}

.login-card .primary-button,
.login-card .primary-button.v-btn {
  background-color: #f5f5f5 !important;
  background: #f5f5f5 !important;
  border-color: #f5f5f5 !important;
  color: #000000 !important;
  box-shadow: none !important;
}

.login-card .primary-button .v-btn__content,
.login-card .primary-button.white--text,
.login-card .primary-button .white--text {
  background: transparent !important;
  color: #000000 !important;
}

.login-card .primary-button:hover,
.login-card .primary-button.v-btn:hover {
  background-color: #e0e0e0 !important;
  background: #e0e0e0 !important;
}

.login-card .primary-button:before,
.login-card .primary-button .v-ripple__container {
  display: none !important;
}

.login-card .primary-button:hover,
.login-card .primary-button:focus,
.login-card .primary-button.v-btn:hover,
.login-card .primary-button.v-btn:focus {
  border-color: #e0e0e0 !important;
  outline: none !important;
}

.login-card input:-webkit-autofill,
.login-card input:-webkit-autofill:hover,
.login-card input:-webkit-autofill:focus,
.login-card input:-webkit-autofill:active {
  -webkit-text-fill-color: hsl(var(--foreground)) !important;
  -webkit-box-shadow: 0 0 0 1000px hsl(var(--input)) inset !important;
  box-shadow: 0 0 0 1000px hsl(var(--input)) inset !important;
  caret-color: hsl(var(--foreground));
  transition: background-color 9999s ease-in-out 0s;
}

.workspace-rail .rail-item.active > .rail-icon,
.workspace-rail .rail-item.active .rail-icon svg {
  background: #000000 !important;
  color: #ffffff !important;
}

.gantt-panel {
  display: grid;
  align-content: start;
  gap: 12px;
  min-height: 0;
  padding: 0;
  border: 0 !important;
  background: transparent !important;
}

.gantt-panel.schedule-panel {
  min-width: 0;
  overflow: hidden;
  border-top: 1px solid #292929 !important;
}

.gantt-panel.schedule-panel.schedule-fullscreen {
  position: fixed;
  inset: 0;
  z-index: 150;
  border: 0 !important;
  border-radius: 0;
  backdrop-filter: none;
  background: #101011 !important;
}

.card-schedule-path {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin: 5px 0 0;
  color: #8f8f96;
  font-size: 10px;
  line-height: 1.35;
}

.gantt-header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 48px;
}

.gantt-header-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
}

.gantt-date-filters {
  display: grid;
  grid-template-columns: repeat(2, minmax(140px, 1fr));
  gap: 8px;
  width: min(320px, 100%);
}

.gantt-date-filters .ui-field {
  min-height: 36px;
}

.gantt-date-filters .ui-field > span {
  display: none;
}

.gantt-date-filters .ui-input {
  height: 36px;
  min-height: 36px;
  border-radius: 8px;
  font-size: 0.84rem;
}

html,
body,
.workspace-rail {
  scrollbar-width: none;
  -ms-overflow-style: none;
}

html::-webkit-scrollbar,
body::-webkit-scrollbar,
.workspace-rail::-webkit-scrollbar {
  display: none;
  width: 0;
  height: 0;
}

.gantt-export-option {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
  margin-top: 4px;
  padding: 14px;
  border: 1px solid hsl(var(--border));
  border-radius: 10px;
  background: hsl(var(--secondary) / 0.4);
  text-align: left;
  cursor: pointer;
  transition: border-color 140ms ease, background-color 140ms ease;
}

.gantt-export-option:hover {
  border-color: hsl(var(--ring) / 0.5);
}

.gantt-export-option.active {
  border-color: hsl(var(--primary));
  background: hsl(var(--primary) / 0.08);
}

.gantt-export-option-checkbox {
  display: grid;
  place-items: center;
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  margin-top: 1px;
  border: 1.5px solid hsl(var(--border));
  border-radius: 5px;
  color: hsl(var(--primary-foreground));
  transition: background-color 140ms ease, border-color 140ms ease;
}

.gantt-export-option.active .gantt-export-option-checkbox {
  border-color: hsl(var(--primary));
  background: hsl(var(--primary));
}

.gantt-export-option-text {
  display: grid;
  gap: 3px;
}

.gantt-export-option-text strong {
  color: hsl(var(--foreground));
  font-size: 0.88rem;
  font-weight: 700;
}

.gantt-export-option-text small {
  color: hsl(var(--muted-foreground));
  font-size: 0.76rem;
  line-height: 1.4;
}

.account-trigger {
  display: grid !important;
  grid-template-columns: none !important;
  width: 38px !important;
  min-height: 38px !important;
  max-width: 38px !important;
  padding: 0 !important;
  border-radius: 999px !important;
  background: hsl(var(--secondary)) !important;
  place-items: center;
}

.account-trigger::after {
  display: none !important;
}

.account-trigger span {
  overflow: visible !important;
  max-width: none !important;
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.02em;
}

/* Modal form stability */

.modal-card .v-form,
.modal-card .detail-main-column,
.modal-card .detail-side-column,
.modal-card .request-side-panel,
.modal-card .detail-side-panel {
  gap: 12px;
}

.modal-card .two-columns,
.modal-card .user-modal-columns {
  gap: 14px;
}

.modal-card .v-input,
.modal-card .theme--light.v-input {
  margin: 0 !important;
  padding: 0 !important;
  border: 0 !important;
  border-radius: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
}

.modal-card .v-input__control {
  min-height: 0 !important;
}

.modal-card .v-input__slot,
.modal-card .theme--light.v-input .v-input__slot {
  min-height: 48px !important;
  margin: 0 !important;
  padding: 0 12px !important;
  border-radius: 10px !important;
  background: hsl(var(--input)) !important;
  box-shadow: none !important;
}

.modal-card .v-textarea .v-input__slot,
.modal-card .v-textarea.theme--light.v-input .v-input__slot {
  min-height: 132px !important;
  align-items: flex-start !important;
  padding-top: 12px !important;
  padding-bottom: 12px !important;
}

.modal-card .v-text-field--outlined fieldset,
.modal-card .v-text-field.v-text-field--outlined fieldset,
.modal-card .v-select.v-text-field--outlined fieldset,
.modal-card .v-textarea.v-text-field--outlined fieldset {
  top: 0 !important;
  border-color: hsl(var(--border)) !important;
  border-radius: 10px !important;
}

.modal-card .v-text-field--outlined legend,
.modal-card .v-select.v-text-field--outlined legend,
.modal-card .v-textarea.v-text-field--outlined legend {
  display: none !important;
}

.modal-card .v-label,
.modal-card .theme--light.v-label {
  display: block !important;
  top: 14px !important;
  max-width: calc(100% - 28px) !important;
  padding: 0 !important;
  background: transparent !important;
  color: hsl(var(--muted-foreground)) !important;
  font-size: 0.92rem !important;
  line-height: 1.25 !important;
  transform: none !important;
  transform-origin: left top !important;
  pointer-events: none;
}

.modal-card .v-textarea .v-label {
  top: 14px !important;
}

.modal-card .v-label.primary--text,
.modal-card .v-input.error--text .v-label {
  color: hsl(var(--muted-foreground)) !important;
}

.modal-card .v-label.v-label--active,
.modal-card .v-input--is-label-active .v-label,
.modal-card .v-text-field:has(input[type="date"]) .v-label {
  opacity: 0 !important;
}

.modal-card .v-text-field__slot,
.modal-card .v-select__slot {
  align-items: center;
}

.modal-card .v-text-field input,
.modal-card .theme--light.v-input input,
.modal-card .v-select__selection,
.modal-card .theme--light.v-select .v-select__selection {
  max-height: none !important;
  padding: 0 !important;
  color: hsl(var(--foreground)) !important;
  caret-color: hsl(var(--foreground)) !important;
  line-height: 48px !important;
  -webkit-text-fill-color: hsl(var(--foreground)) !important;
}

.modal-card .v-textarea textarea,
.modal-card .theme--light.v-input textarea {
  margin: 0 !important;
  padding: 0 !important;
  color: hsl(var(--foreground)) !important;
  caret-color: hsl(var(--foreground)) !important;
  line-height: 1.45 !important;
  -webkit-text-fill-color: hsl(var(--foreground)) !important;
}

.modal-card .v-input input::placeholder,
.modal-card .v-textarea textarea::placeholder {
  color: hsl(var(--muted-foreground)) !important;
  opacity: 1;
}

.modal-card .v-input__append-inner,
.modal-card .v-input__prepend-inner {
  align-self: center !important;
  margin-top: 0 !important;
}

.modal-card .v-input__append-inner .v-icon,
.modal-card .v-input__prepend-inner .v-icon {
  color: hsl(var(--muted-foreground)) !important;
}

.modal-card .v-text-field__details {
  min-height: 16px !important;
  margin: 4px 0 0 !important;
  padding: 0 2px !important;
}

.modal-card .v-messages {
  min-height: 16px !important;
  color: hsl(var(--destructive)) !important;
  font-size: 0.72rem !important;
  line-height: 1.25 !important;
}

.modal-card .v-messages__message {
  line-height: 1.25 !important;
}

.modal-card .v-input.error--text .v-input__slot {
  background: hsl(var(--input)) !important;
}

.modal-card .v-input.error--text fieldset {
  border-color: hsl(var(--destructive)) !important;
}

/* shadcn form surface */

.app-page .v-input,
.app-page .theme--light.v-input {
  margin: 0 !important;
  padding: 0 !important;
  border: 0 !important;
  border-radius: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
}

.app-page .v-input__control {
  min-height: 0 !important;
}

.app-page .v-input__slot,
.app-page .theme--light.v-input .v-input__slot {
  min-height: 40px !important;
  margin: 0 !important;
  padding: 0 12px !important;
  border-radius: 8px !important;
  background: hsl(var(--input)) !important;
  box-shadow: none !important;
  transition: border-color 140ms ease, box-shadow 140ms ease, background-color 140ms ease;
}

.app-page .v-textarea .v-input__slot,
.app-page .v-textarea.theme--light.v-input .v-input__slot {
  min-height: 112px !important;
  align-items: flex-start !important;
  padding-top: 11px !important;
  padding-bottom: 11px !important;
}

.app-page .v-text-field.v-text-field--outlined fieldset,
.app-page .v-select.v-text-field--outlined fieldset,
.app-page .v-textarea.v-text-field--outlined fieldset {
  top: 0 !important;
  border-color: hsl(var(--border)) !important;
  border-radius: 8px !important;
}

.app-page .v-text-field--outlined legend,
.app-page .v-select.v-text-field--outlined legend,
.app-page .v-textarea.v-text-field--outlined legend {
  display: none !important;
}

.app-page .v-text-field--outlined.v-input--is-focused fieldset,
.app-page .v-select.v-input--is-focused fieldset,
.app-page .v-textarea.v-input--is-focused fieldset {
  border-color: hsl(var(--ring)) !important;
  box-shadow: 0 0 0 1px hsl(var(--ring) / 0.22) !important;
}

.app-page .v-label,
.app-page .theme--light.v-label {
  display: block !important;
  top: 11px !important;
  max-width: calc(100% - 28px) !important;
  padding: 0 !important;
  background: transparent !important;
  color: hsl(var(--muted-foreground)) !important;
  font-size: 0.875rem !important;
  font-weight: 400 !important;
  line-height: 1.25 !important;
  transform: none !important;
  transform-origin: left top !important;
  pointer-events: none;
}

.app-page .v-textarea .v-label {
  top: 11px !important;
}

.app-page .v-label.primary--text,
.app-page .v-input.error--text .v-label {
  color: hsl(var(--muted-foreground)) !important;
}

.app-page .v-label.v-label--active,
.app-page .v-input--is-label-active .v-label,
.app-page .v-text-field:has(input[type="date"]) .v-label,
.topbar-filter-group .v-label {
  opacity: 0 !important;
}

.topbar-filter-group .v-label {
  display: none !important;
}

.app-page .v-text-field__slot,
.app-page .v-select__slot {
  align-items: center;
}

.app-page .v-text-field input,
.app-page .theme--light.v-input input,
.app-page .v-select__selection,
.app-page .theme--light.v-select .v-select__selection {
  max-height: none !important;
  min-height: 0 !important;
  padding: 0 !important;
  color: hsl(var(--foreground)) !important;
  caret-color: hsl(var(--foreground)) !important;
  font-size: 0.875rem !important;
  line-height: 40px !important;
  -webkit-text-fill-color: hsl(var(--foreground)) !important;
}

.app-page .v-textarea textarea,
.app-page .theme--light.v-input textarea {
  margin: 0 !important;
  padding: 0 !important;
  color: hsl(var(--foreground)) !important;
  caret-color: hsl(var(--foreground)) !important;
  font-size: 0.875rem !important;
  line-height: 1.45 !important;
  -webkit-text-fill-color: hsl(var(--foreground)) !important;
}

.app-page .v-input input::placeholder,
.app-page .v-textarea textarea::placeholder {
  color: hsl(var(--muted-foreground)) !important;
  opacity: 1;
}

.app-page .v-input__append-inner,
.app-page .v-input__prepend-inner {
  align-self: center !important;
  margin-top: 0 !important;
}

.app-page .v-input__append-inner .v-icon,
.app-page .v-input__prepend-inner .v-icon {
  color: hsl(var(--muted-foreground)) !important;
}

.app-page .v-text-field__details {
  min-height: 16px !important;
  margin: 4px 0 0 !important;
  padding: 0 2px !important;
}

.app-page .v-messages {
  min-height: 16px !important;
  color: hsl(var(--destructive)) !important;
  font-size: 0.72rem !important;
  line-height: 1.25 !important;
}

.app-page .v-messages__message {
  line-height: 1.25 !important;
}

.app-page .v-input.error--text .v-input__slot {
  background: hsl(var(--input)) !important;
}

.app-page .v-input.error--text fieldset {
  border-color: hsl(var(--destructive)) !important;
}

.topbar-filter-group .v-input__slot,
.topbar-filter-group .theme--light.v-input .v-input__slot,
.topbar-filter-group .ui-input,
.topbar-filter-group .ui-select {
  min-height: 36px !important;
  height: 36px !important;
  border-radius: 8px !important;
}

.topbar-filter-group .v-text-field input,
.topbar-filter-group .v-select__selection,
.topbar-filter-group .theme--light.v-input input {
  font-size: 0.84rem !important;
  line-height: 36px !important;
}

.v-application .modal-card .v-input__slot,
.v-application .modal-card .theme--light.v-input .v-input__slot {
  height: 40px !important;
  max-height: 40px !important;
  min-height: 40px !important;
  margin: 0 !important;
  padding: 0 12px !important;
  border-radius: 8px !important;
  background: hsl(var(--input)) !important;
  box-shadow: none !important;
}

.v-application .modal-card .v-textarea .v-input__slot,
.v-application .modal-card .v-textarea.theme--light.v-input .v-input__slot {
  height: auto !important;
  max-height: none !important;
  min-height: 112px !important;
  align-items: flex-start !important;
  padding-top: 11px !important;
  padding-bottom: 11px !important;
}

.v-application .modal-card .v-text-field.v-text-field--outlined fieldset,
.v-application .modal-card .v-select.v-text-field--outlined fieldset,
.v-application .modal-card .v-textarea.v-text-field--outlined fieldset {
  top: 0 !important;
  border-color: hsl(var(--border)) !important;
  border-radius: 8px !important;
}

.v-application .modal-card .v-text-field--outlined.v-input--is-focused fieldset,
.v-application .modal-card .v-select.v-input--is-focused fieldset,
.v-application .modal-card .v-textarea.v-input--is-focused fieldset {
  border-color: hsl(var(--ring)) !important;
  box-shadow: 0 0 0 1px hsl(var(--ring) / 0.22) !important;
}

.v-application .modal-card .v-label,
.v-application .modal-card .theme--light.v-label {
  top: 11px !important;
  color: hsl(var(--muted-foreground)) !important;
  font-size: 0.875rem !important;
  line-height: 1.25 !important;
  transform: none !important;
}

.v-application .modal-card .v-label.v-label--active,
.v-application .modal-card .v-input--is-label-active .v-label,
.v-application .modal-card .v-text-field:has(input[type="date"]) .v-label {
  opacity: 0 !important;
}

.v-application .modal-card .v-select__selections,
.v-application .modal-card .v-text-field__slot,
.v-application .modal-card .v-select__slot {
  min-height: 0 !important;
  align-items: center !important;
}

.v-application .modal-card .v-text-field input,
.v-application .modal-card .theme--light.v-input input,
.v-application .modal-card .v-select__selection,
.v-application .modal-card .theme--light.v-select .v-select__selection {
  min-height: 0 !important;
  padding: 0 !important;
  color: hsl(var(--foreground)) !important;
  font-size: 0.875rem !important;
  line-height: 40px !important;
  -webkit-text-fill-color: hsl(var(--foreground)) !important;
}

.v-application .modal-card .v-textarea textarea,
.v-application .modal-card .theme--light.v-input textarea {
  padding: 0 !important;
  color: hsl(var(--foreground)) !important;
  font-size: 0.875rem !important;
  line-height: 1.45 !important;
  -webkit-text-fill-color: hsl(var(--foreground)) !important;
}

/* Kanban board / insights panel layout fix */

.kanban-workspace {
  display: grid !important;
  grid-template-columns: minmax(0, 1fr) !important;
  align-items: start !important;
}

.kanban-workspace .kanban-board {
  position: relative;
  min-width: 0 !important;
  max-width: 100%;
  overflow-x: auto !important;
}

.column-drag-handle {
  display: grid;
  flex: 0 0 22px;
  width: 22px;
  height: 26px;
  place-items: center;
  padding: 0;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: hsl(var(--muted-foreground));
  cursor: grab;
}

.column-drag-handle:hover,
.column-drag-handle:focus-visible {
  background: hsl(var(--secondary));
  color: hsl(var(--foreground));
  outline: none;
}

.column-drag-handle:active { cursor: grabbing; }

.kanban-column.column-drop-before {
  box-shadow: inset 4px 0 hsl(var(--primary)) !important;
}

.kanban-column.column-drop-after {
  box-shadow: inset -4px 0 hsl(var(--primary)) !important;
}

/* Keep request actions reachable at every viewport width. */
.requests-workspace,
.requests-workspace .work-grid,
.requests-workspace .requests-screen {
  min-width: 0;
  height: auto;
  max-height: none;
  overflow: visible;
}
.requests-workspace { padding-bottom: 48px; }
.requests-table-card { min-width: 0; max-width: 100%; }
.requests-table-scroll { width: 100%; overflow-x: auto; }
.requests-table-card .actions-cell { white-space: nowrap; }
.requests-table-scroll:focus-visible { outline: 2px solid hsl(var(--primary)); outline-offset: -2px; }

.archive-modal .v-card__text { min-width: 0; }
.archive-toolbar {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 12px;
}
.archive-toolbar .ui-input { flex: 1 1 auto; min-width: 0; }
.archive-toolbar > span { flex: none; color: hsl(var(--muted-foreground)); font-size: 0.8rem; }
.archive-list { display: grid; }
.archive-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-width: 0;
  padding: 13px 0;
  border-bottom: 1px solid hsl(var(--border));
}
.archive-row:last-child { border-bottom: 0; }
.archive-row-content { display: grid; gap: 3px; min-width: 0; }
.archive-row-content small,
.archive-row-content span { color: hsl(var(--muted-foreground)); font-size: 0.76rem; }
.archive-row-content strong { color: hsl(var(--foreground)); font-size: 0.9rem; overflow-wrap: anywhere; }
.archive-row-actions { display: flex; align-items: center; gap: 8px; flex: none; }
.archive-row-actions button { min-height: 32px; }
@media (max-width: 600px) {
  .archive-toolbar, .archive-row { align-items: stretch; flex-direction: column; }
  .archive-row-actions { justify-content: flex-end; }
}
</style>
